// Ported verbatim from iOS: client-native/src/data/spanishWordFrequency.ts
// Spanish word-frequency ranks (top 10,000 words) + tier/scoring helpers used
// for activity word scoring. Keep in sync with the iOS source of truth.
export type FrequencyTier = 'super-common' | 'very-common' | 'common' | 'intermediate' | 'advanced';

export const TIER_ORDER: FrequencyTier[] = ['super-common', 'very-common', 'common', 'intermediate', 'advanced'];

export const TIER_LABELS: Record<FrequencyTier, string> = {
  'super-common': 'Super Common',
  'very-common': 'Very Common',
  'common': 'Common',
  'intermediate': 'Intermediate',
  'advanced': 'Advanced',
};

// Spanish word frequency ranks based on OpenSubtitles corpus data (~27.4M words)
// Source: Wiktionary frequency lists / OpenSubtitles
// Map of lowercase normalized word -> frequency rank (1 = most common)
const FREQUENCY_RANKS: Record<string, number> = {
  'que': 1,
  'de': 2,
  'no': 3,
  'a': 4,
  'la': 5,
  'el': 6,
  'es': 7,
  'y': 8,
  'en': 9,
  'lo': 10,
  'un': 11,
  'por': 12,
  'qué': 13,
  'me': 14,
  'una': 15,
  'te': 16,
  'los': 17,
  'se': 18,
  'con': 19,
  'para': 20,
  'mi': 21,
  'está': 22,
  'si': 23,
  'bien': 24,
  'pero': 25,
  'yo': 26,
  'eso': 27,
  'las': 28,
  'sí': 29,
  'su': 30,
  'tu': 31,
  'aquí': 32,
  'del': 33,
  'al': 34,
  'como': 35,
  'le': 36,
  'más': 37,
  'esto': 38,
  'ya': 39,
  'todo': 40,
  'esta': 41,
  'vamos': 42,
  'muy': 43,
  'hay': 44,
  'ahora': 45,
  'algo': 46,
  'estoy': 47,
  'tengo': 48,
  'nos': 49,
  'tú': 50,
  'nada': 51,
  'cuando': 52,
  'ha': 53,
  'este': 54,
  'sé': 55,
  'estás': 56,
  'así': 57,
  'puedo': 58,
  'cómo': 59,
  'quiero': 60,
  'sólo': 61,
  'soy': 62,
  'tiene': 63,
  'gracias': 64,
  'o': 65,
  'él': 66,
  'bueno': 67,
  'fue': 68,
  'ser': 69,
  'hacer': 70,
  'son': 71,
  'todos': 72,
  'era': 73,
  'eres': 74,
  'vez': 75,
  'tienes': 76,
  'creo': 77,
  'ella': 78,
  'he': 79,
  'ese': 80,
  'voy': 81,
  'puede': 82,
  'sabes': 83,
  'hola': 84,
  'sus': 85,
  'porque': 86,
  'dios': 87,
  'quién': 88,
  'nunca': 89,
  'dónde': 90,
  'quieres': 91,
  'casa': 92,
  'favor': 93,
  'esa': 94,
  'dos': 95,
  'tan': 96,
  'señor': 97,
  'tiempo': 98,
  'verdad': 99,
  'estaba': 100,
  'mejor': 101,
  'están': 102,
  'va': 103,
  'hombre': 104,
  'usted': 105,
  'mucho': 106,
  'hace': 107,
  'entonces': 108,
  'siento': 109,
  'tenemos': 110,
  'puedes': 111,
  'ahí': 112,
  'ti': 113,
  'vida': 114,
  'ver': 115,
  'alguien': 116,
  'sr': 117,
  'hasta': 118,
  'sin': 119,
  'mí': 120,
  'solo': 121,
  'años': 122,
  'sobre': 123,
  'decir': 124,
  'uno': 125,
  'siempre': 126,
  'oh': 127,
  'ir': 128,
  'cosas': 129,
  'también': 130,
  'antes': 131,
  'has': 132,
  'ni': 133,
  'mis': 134,
  'día': 135,
  'estar': 136,
  'estamos': 137,
  'noche': 138,
  'nadie': 139,
  'otra': 140,
  'quiere': 141,
  'parece': 142,
  'nosotros': 143,
  'poco': 144,
  'padre': 145,
  'trabajo': 146,
  'gente': 147,
  'mira': 148,
  'vas': 149,
  'sea': 150,
  'les': 151,
  'donde': 152,
  'mismo': 153,
  'hecho': 154,
  'ellos': 155,
  'dijo': 156,
  'pasa': 157,
  'dinero': 158,
  'hijo': 159,
  'tal': 160,
  'otro': 161,
  'hablar': 162,
  'seguro': 163,
  'claro': 164,
  'estas': 165,
  'lugar': 166,
  'mundo': 167,
  'amigo': 168,
  'espera': 169,
  'mierda': 170,
  'han': 171,
  'tus': 172,
  'sabe': 173,
  'después': 174,
  'momento': 175,
  'desde': 176,
  'fuera': 177,
  'cosa': 178,
  'tipo': 179,
  'mañana': 180,
  'podemos': 181,
  'dije': 182,
  'gran': 183,
  'necesito': 184,
  'estado': 185,
  'podría': 186,
  'acuerdo': 187,
  'papá': 188,
  'tener': 189,
  'dice': 190,
  'mío': 191,
  'crees': 192,
  'buena': 193,
  'gusta': 194,
  'nuestro': 195,
  'nuevo': 196,
  'será': 197,
  'haciendo': 198,
  'días': 199,
  'nombre': 200,
  'buen': 201,
  'había': 202,
  'ven': 203,
  'tres': 204,
  'menos': 205,
  'debe': 206,
  'tenía': 207,
  'mal': 208,
  'conmigo': 209,
  'madre': 210,
  'hoy': 211,
  'quien': 212,
  'sido': 213,
  'mamá': 214,
  'tienen': 215,
  'luego': 216,
  'todas': 217,
  'allí': 218,
  'toda': 219,
  'hora': 220,
  'mujer': 221,
  'visto': 222,
  'haces': 223,
  'importa': 224,
  'contigo': 225,
  've': 226,
  'tarde': 227,
  'oye': 228,
  'parte': 229,
  'haber': 230,
  'hombres': 231,
  'problema': 232,
  'mas': 233,
  'saber': 234,
  'quería': 235,
  'aún': 236,
  'veces': 237,
  'nuestra': 238,
  'hacerlo': 239,
  'cada': 240,
  'hizo': 241,
  'veo': 242,
  'tanto': 243,
  'razón': 244,
  'ustedes': 245,
  'idea': 246,
  'esos': 247,
  'van': 248,
  'quizá': 249,
  'debo': 250,
  'alguna': 251,
  'cierto': 252,
  'ud': 253,
  'muerto': 254,
  'unos': 255,
  'estos': 256,
  'salir': 257,
  'policía': 258,
  'realmente': 259,
  'demasiado': 260,
  'familia': 261,
  'pueden': 262,
  'cabeza': 263,
  'hemos': 264,
  'amigos': 265,
  'chica': 266,
  'cariño': 267,
  'lado': 268,
  'allá': 269,
  'entre': 270,
  'minutos': 271,
  'digo': 272,
  'algún': 273,
  'serio': 274,
  'cuidado': 275,
  'pasó': 276,
  'buenas': 277,
  'somos': 278,
  'amor': 279,
  'puerta': 280,
  'ves': 281,
  'vaya': 282,
  'ah': 283,
  'suerte': 284,
  'eh': 285,
  'rápido': 286,
  'cuenta': 287,
  'quizás': 288,
  'esas': 289,
  'pues': 290,
  'pasado': 291,
  'pensé': 292,
  'todavía': 293,
  'hermano': 294,
  'debes': 295,
  'casi': 296,
  'forma': 297,
  'aqui': 298,
  'chico': 299,
  'ok': 300,
  'dicho': 301,
  'nueva': 302,
  'sabía': 303,
  'muchas': 304,
  'dentro': 305,
  'hice': 306,
  'contra': 307,
  'auto': 308,
  'camino': 309,
  'ayuda': 310,
  'primera': 311,
  'hacia': 312,
  'vi': 313,
  'miedo': 314,
  'adiós': 315,
  'primero': 316,
  'debería': 317,
  'poder': 318,
  'niños': 319,
  'sería': 320,
  'historia': 321,
  'hey': 322,
  'mientras': 323,
  'ciudad': 324,
  'dijiste': 325,
  'espero': 326,
  'cuánto': 327,
  'esposa': 328,
  'pronto': 329,
  'chicos': 330,
  'cualquier': 331,
  'viejo': 332,
  'debemos': 333,
  'deja': 334,
  'año': 335,
  'muerte': 336,
  'hablando': 337,
  'manos': 338,
  'da': 339,
  'loco': 340,
  'problemas': 341,
  'mano': 342,
  'guerra': 343,
  'semana': 344,
  'pasar': 345,
  'vale': 346,
  'cuál': 347,
  'viene': 348,
  'volver': 349,
  'toma': 350,
  'caso': 351,
  'agua': 352,
  'haré': 353,
  'vete': 354,
  'entiendo': 355,
  'horas': 356,
  'personas': 357,
  'capitán': 358,
  'adelante': 359,
  'niño': 360,
  'listo': 361,
  'noches': 362,
  'buenos': 363,
  'iba': 364,
  'juntos': 365,
  'dame': 366,
  'único': 367,
  'déjame': 368,
  'cerca': 369,
  'otros': 370,
  'sigue': 371,
  'grande': 372,
  'arriba': 373,
  'jefe': 374,
  'habla': 375,
  'supongo': 376,
  'manera': 377,
  'quieren': 378,
  'feliz': 379,
  'significa': 380,
  'sangre': 381,
  'fin': 382,
  'bajo': 383,
  'llama': 384,
  'venir': 385,
  'morir': 386,
  'importante': 387,
  'hiciste': 388,
  'ojos': 389,
  'escucha': 390,
  'entrar': 391,
  'ningún': 392,
  'corazón': 393,
  'diablos': 394,
  'necesitamos': 395,
  'atrás': 396,
  'durante': 397,
  'dices': 398,
  'nuestros': 399,
  'persona': 400,
  'abajo': 401,
  'dr': 402,
  'hija': 403,
  'dejar': 404,
  'necesita': 405,
  'llegar': 406,
  'hago': 407,
  'señora': 408,
  'haya': 409,
  'suficiente': 410,
  'doctor': 411,
  'gustaría': 412,
  'tierra': 413,
  'cara': 414,
  'siquiera': 415,
  'genial': 416,
  'cree': 417,
  'supuesto': 418,
  'tomar': 419,
  'equipo': 420,
  'justo': 421,
  'juego': 422,
  'ninguna': 423,
  'matar': 424,
  'cinco': 425,
  'dicen': 426,
  'amo': 427,
  'cuándo': 428,
  'pequeño': 429,
  'algunos': 430,
  'conozco': 431,
  'clase': 432,
  'maldito': 433,
  'unas': 434,
  'muchos': 435,
  'hubiera': 436,
  'segundo': 437,
  'aunque': 438,
  'pueda': 439,
  'dime': 440,
  'igual': 441,
  'comida': 442,
  'ay': 443,
  'cuerpo': 444,
  'encontrar': 445,
  'fuerte': 446,
  'vuelta': 447,
  'venga': 448,
  'creer': 449,
  'realidad': 450,
  'saben': 451,
  'puta': 452,
  'deberías': 453,
  'pregunta': 454,
  'fui': 455,
  'cuatro': 456,
  'sra': 457,
  'primer': 458,
  'trabajar': 459,
  'e': 460,
  'hagas': 461,
  'alto': 462,
  'maldita': 463,
  'comer': 464,
  'número': 465,
  'dar': 466,
  'necesitas': 467,
  'oportunidad': 468,
  'punto': 469,
  'misma': 470,
  'última': 471,
  'afuera': 472,
  'mujeres': 473,
  'pensar': 474,
  'fueron': 475,
  'difícil': 476,
  'vivir': 477,
  'paso': 478,
  'malo': 479,
  'estabas': 480,
  'vivo': 481,
  'haga': 482,
  'queda': 483,
  'hijos': 484,
  'mayor': 485,
  'fiesta': 486,
  'hacen': 487,
  'medio': 488,
  'algunas': 489,
  'basta': 490,
  'arma': 491,
  'vino': 492,
  'meses': 493,
  'cuarto': 494,
  'éste': 495,
  'escuela': 496,
  'esté': 497,
  'dólares': 498,
  'tío': 499,
  'posible': 500,
  'tuve': 501,
  'fácil': 502,
  'preocupes': 503,
  'luz': 504,
  'eran': 505,
  'carajo': 506,
  'final': 507,
  'lista': 508,
  'trata': 509,
  'armas': 510,
  'hermana': 511,
  'exactamente': 512,
  'chicas': 513,
  'podía': 514,
  'bastante': 515,
  'seguridad': 516,
  'pasando': 517,
  'esperando': 518,
  'acá': 519,
  'teléfono': 520,
  'perro': 521,
  'fuego': 522,
  'murió': 523,
  'tampoco': 524,
  'sola': 525,
  'estuvo': 526,
  'verte': 527,
  'iré': 528,
  'tenido': 529,
  'culpa': 530,
  'veras': 531,
  'adónde': 532,
  'buscando': 533,
  'cuanto': 534,
  'padres': 535,
  'paz': 536,
  'demonios': 537,
  'estará': 538,
  'cual': 539,
  'perdón': 540,
  'asi': 541,
  'jugar': 542,
  'pensando': 543,
  'esperar': 544,
  'sabemos': 545,
  'recuerdo': 546,
  'par': 547,
  'joven': 548,
  'seguir': 549,
  'pueblo': 550,
  'tenga': 551,
  'caballeros': 552,
  'idiota': 553,
  'dio': 554,
  'minuto': 555,
  'bebé': 556,
  'única': 557,
  'lejos': 558,
  'nuestras': 559,
  'plan': 560,
  'pienso': 561,
  'sentido': 562,
  'dormir': 563,
  'digas': 564,
  'palabra': 565,
  'correcto': 566,
  'control': 567,
  'vemos': 568,
  'entiendes': 569,
  'país': 570,
  'seis': 571,
  'último': 572,
  'ésta': 573,
  'diga': 574,
  'podrías': 575,
  'pequeña': 576,
  'cállate': 577,
  'trato': 578,
  'rey': 579,
  'sucede': 580,
  'muchachos': 581,
  'jamás': 582,
  'cama': 583,
  'srta': 584,
  'ayudar': 585,
  'acerca': 586,
  'di': 587,
  'cambio': 588,
  'falta': 589,
  'hospital': 590,
  'lleva': 591,
  'presidente': 592,
  'mil': 593,
  'gusto': 594,
  'conoces': 595,
  'diciendo': 596,
  'os': 597,
  'ido': 598,
  'general': 599,
  'extraño': 600,
  'semanas': 601,
  'coche': 602,
  'peor': 603,
  'mucha': 604,
  'disculpe': 605,
  'diré': 606,
  'anoche': 607,
  'perder': 608,
  'vámonos': 609,
  'nave': 610,
  'cielo': 611,
  'habrá': 612,
  'orden': 613,
  'segura': 614,
  'querida': 615,
  'niña': 616,
  'increíble': 617,
  'además': 618,
  'deben': 619,
  'libro': 620,
  'calle': 621,
  'café': 622,
  'piensas': 623,
  'hacemos': 624,
  'especial': 625,
  'queremos': 626,
  'irme': 627,
  'perfecto': 628,
  'buscar': 629,
  'odio': 630,
  'piensa': 631,
  'oficina': 632,
  'hablas': 633,
  'libre': 634,
  'agente': 635,
  'york': 636,
  'llamar': 637,
  'mala': 638,
  'detrás': 639,
  'viste': 640,
  'dile': 641,
  'grandes': 642,
  'recuerdas': 643,
  'real': 644,
  'estaban': 645,
  'mía': 646,
  'frente': 647,
  'perdido': 648,
  'llamo': 649,
  'muertos': 650,
  'millones': 651,
  'asesino': 652,
  'sueño': 653,
  'quisiera': 654,
  'habría': 655,
  'hará': 656,
  'viaje': 657,
  'probablemente': 658,
  'resto': 659,
  'estaré': 660,
  'maldición': 661,
  'lamento': 662,
  'muchacho': 663,
  'avión': 664,
  'ropa': 665,
  'fuerza': 666,
  'llamado': 667,
  'oído': 668,
  'dado': 669,
  'encima': 670,
  'negro': 671,
  'usar': 672,
  'información': 673,
  'uds': 674,
  'preguntas': 675,
  'tuvo': 676,
  'secreto': 677,
  'vuelve': 678,
  'miren': 679,
  'quieras': 680,
  'haría': 681,
  'acaba': 682,
  'otras': 683,
  'incluso': 684,
  'sientes': 685,
  'deberíamos': 686,
  'haz': 687,
  'decirte': 688,
  'boca': 689,
  'dolor': 690,
  'baño': 691,
  'adentro': 692,
  'profesor': 693,
  'habitación': 694,
  'daño': 695,
  'tuyo': 696,
  'seas': 697,
  'noticias': 698,
  'demás': 699,
  'querido': 700,
  'duro': 701,
  'poner': 702,
  'prueba': 703,
  'mire': 704,
  'tonto': 705,
  'campo': 706,
  'siendo': 707,
  'diez': 708,
  'ése': 709,
  'tranquilo': 710,
  'asunto': 711,
  'acabó': 712,
  'quédate': 713,
  'derecho': 714,
  'placer': 715,
  'recuerda': 716,
  'estuve': 717,
  'tratando': 718,
  'ejército': 719,
  'futuro': 720,
  'llevar': 721,
  'compañía': 722,
  'venido': 723,
  'listos': 724,
  'haremos': 725,
  'sitio': 726,
  'verlo': 727,
  'puesto': 728,
  'atención': 729,
  'sino': 730,
  'cambiar': 731,
  'error': 732,
  'blanco': 733,
  'raro': 734,
  'palabras': 735,
  'llegó': 736,
  'sal': 737,
  'pase': 738,
  'mente': 739,
  'sistema': 740,
  'película': 741,
  'anda': 742,
  'ello': 743,
  'negocio': 744,
  'novia': 745,
  'permiso': 746,
  'creí': 747,
  'suena': 748,
  'ocurre': 749,
  'oficial': 750,
  'espere': 751,
  'aire': 752,
  'mató': 753,
  'regresar': 754,
  'vio': 755,
  'hazlo': 756,
  'trasero': 757,
  'grupo': 758,
  'entendido': 759,
  'señorita': 760,
  'música': 761,
  'perra': 762,
  'conoce': 763,
  'empezar': 764,
  'siente': 765,
  'acabo': 766,
  'estúpido': 767,
  'diferente': 768,
  'traje': 769,
  'modo': 770,
  'encontré': 771,
  'mensaje': 772,
  'llamada': 773,
  'navidad': 774,
  'eras': 775,
  'pena': 776,
  'largo': 777,
  'entra': 778,
  'piso': 779,
  'foto': 780,
  'dijeron': 781,
  'médico': 782,
  'accidente': 783,
  'fuiste': 784,
  'imposible': 785,
  'podríamos': 786,
  'línea': 787,
  'propia': 788,
  'barco': 789,
  'ganar': 790,
  'normal': 791,
  'segundos': 792,
  'vive': 793,
  'mitad': 794,
  'quiera': 795,
  'tras': 796,
  'decirle': 797,
  'lindo': 798,
  'funciona': 799,
  'programa': 800,
  'vine': 801,
  'abre': 802,
  'sean': 803,
  'pagar': 804,
  'fotos': 805,
  'centro': 806,
  'supone': 807,
  'basura': 808,
  'situación': 809,
  'mejores': 810,
  'vienen': 811,
  'encanta': 812,
  'marido': 813,
  'personal': 814,
  'maestro': 815,
  'hambre': 816,
  'ataque': 817,
  'culo': 818,
  'dale': 819,
  'pie': 820,
  'conseguir': 821,
  'trabajando': 822,
  'gracioso': 823,
  'dejó': 824,
  'pudo': 825,
  'derecha': 826,
  'izquierda': 827,
  'próxima': 828,
  'pobre': 829,
  'respuesta': 830,
  'tipos': 831,
  'sentir': 832,
  'tenías': 833,
  'pude': 834,
  'darle': 835,
  'voz': 836,
  'amiga': 837,
  'gustan': 838,
  'vista': 839,
  'salvo': 840,
  'loca': 841,
  'hotel': 842,
  'hicieron': 843,
  'ten': 844,
  'temo': 845,
  'señal': 846,
  'pelo': 847,
  'llevo': 848,
  'ayer': 849,
  'das': 850,
  'nena': 851,
  'servicio': 852,
  'tren': 853,
  'bonito': 854,
  'mes': 855,
  'tendrá': 856,
  'tendrás': 857,
  'edad': 858,
  'ellas': 859,
  'hermosa': 860,
  'honor': 861,
  'simplemente': 862,
  'llamas': 863,
  'tengas': 864,
  'corre': 865,
  'baja': 866,
  'sol': 867,
  'siéntate': 868,
  'dan': 869,
  'humano': 870,
  'divertido': 871,
  'sexo': 872,
  'vuelto': 873,
  'peligro': 874,
  'mesa': 875,
  'siguiente': 876,
  'hablo': 877,
  'disculpa': 878,
  'decirme': 879,
  'caja': 880,
  'negocios': 881,
  'misión': 882,
  'silencio': 883,
  'sale': 884,
  'llegado': 885,
  'estaría': 886,
  'regreso': 887,
  'media': 888,
  'estan': 889,
  'propio': 890,
  'oro': 891,
  'enseguida': 892,
  'linda': 893,
  'prometo': 894,
  'esposo': 895,
  'norte': 896,
  'hubo': 897,
  'juro': 898,
  'muerta': 899,
  'interesante': 900,
  'pensaba': 901,
  'busca': 902,
  'terminar': 903,
  'tendré': 904,
  'completamente': 905,
  'cita': 906,
  'siete': 907,
  'cumpleaños': 908,
  'abogado': 909,
  'alrededor': 910,
  'cerebro': 911,
  'porqué': 912,
  'llave': 913,
  'santo': 914,
  'hermoso': 915,
  'necesario': 916,
  'edificio': 917,
  'irnos': 918,
  'aun': 919,
  'tendremos': 920,
  'vayas': 921,
  'doy': 922,
  'trae': 923,
  'salió': 924,
  'ley': 925,
  'ahi': 926,
  'verdadero': 927,
  'pelea': 928,
  'banco': 929,
  'terrible': 930,
  'calma': 931,
  'cena': 932,
  'daré': 933,
  'gobierno': 934,
  'comprar': 935,
  'creen': 936,
  'sargento': 937,
  'destino': 938,
  'existe': 939,
  'hacía': 940,
  'novio': 941,
  'sala': 942,
  'través': 943,
  'regalo': 944,
  'iglesia': 945,
  'decía': 946,
  'cualquiera': 947,
  'excelente': 948,
  'esperen': 949,
  'deseo': 950,
  'alma': 951,
  'diablo': 952,
  'deje': 953,
  'cuántos': 954,
  'espada': 955,
  'estábamos': 956,
  'carne': 957,
  'maravilloso': 958,
  'vidas': 959,
  'sucedió': 960,
  'oí': 961,
  'peligroso': 962,
  'dirección': 963,
  'libertad': 964,
  'jesús': 965,
  'ocurrió': 966,
  'veré': 967,
  'sueños': 968,
  'pudiera': 969,
  'detective': 970,
  'sorpresa': 971,
  'tuya': 972,
  'pies': 973,
  'club': 974,
  'terminado': 975,
  'infierno': 976,
  'creía': 977,
  'luna': 978,
  'salvar': 979,
  'carta': 980,
  'estés': 981,
  'cielos': 982,
  'teniente': 983,
  'encuentra': 984,
  'veamos': 985,
  'quise': 986,
  'escúchame': 987,
  'necesitan': 988,
  'ambos': 989,
  'decisión': 990,
  'enemigo': 991,
  'hicimos': 992,
  'dulce': 993,
  'pruebas': 994,
  'querías': 995,
  'abuelo': 996,
  'totalmente': 997,
  'mirando': 998,
  'vayan': 999,
  'carrera': 1000,
  'vuelo': 1001,
  'ante': 1002,
  'bienvenido': 1003,
  'harás': 1004,
  'encontramos': 1005,
  'encontrado': 1006,
  'contacto': 1007,
  'posición': 1008,
  'saberlo': 1009,
  'planeta': 1010,
  'humanos': 1011,
  'coronel': 1012,
  'junto': 1013,
  'diría': 1014,
  'ésa': 1015,
  'base': 1016,
  'oír': 1017,
  'suelo': 1018,
  'pelear': 1019,
  'ayudarte': 1020,
  'pistola': 1021,
  'frío': 1022,
  'comandante': 1023,
  'partes': 1024,
  'llega': 1025,
  'verás': 1026,
  'sur': 1027,
  'iremos': 1028,
  'rato': 1029,
  'mar': 1030,
  'espacio': 1031,
  'asesinato': 1032,
  'ventana': 1033,
  'prisa': 1034,
  'tienda': 1035,
  'cámara': 1036,
  'puedas': 1037,
  'según': 1038,
  'broma': 1039,
  'reunión': 1040,
  'despierta': 1041,
  'sacar': 1042,
  'tí': 1043,
  'segunda': 1044,
  'papel': 1045,
  'locura': 1046,
  'departamento': 1047,
  'horrible': 1048,
  'enfermo': 1049,
  'pregunto': 1050,
  'cárcel': 1051,
  'órdenes': 1052,
  'intento': 1053,
  'isla': 1054,
  'salida': 1055,
  'llamó': 1056,
  'volveré': 1057,
  'usa': 1058,
  'gato': 1059,
  'hagan': 1060,
  'dejes': 1061,
  'duele': 1062,
  'vengan': 1063,
  'crimen': 1064,
  'esperaba': 1065,
  'causa': 1066,
  'bar': 1067,
  'seré': 1068,
  'ocho': 1069,
  'temprano': 1070,
  'río': 1071,
  'relación': 1072,
  'drogas': 1073,
  'luces': 1074,
  'bromeando': 1075,
  'ojalá': 1076,
  'hablamos': 1077,
  'trabaja': 1078,
  'irse': 1079,
  'libros': 1080,
  'radio': 1081,
  'vienes': 1082,
  'quedan': 1083,
  'excepto': 1084,
  'brazo': 1085,
  'tome': 1086,
  'rojo': 1087,
  'conocido': 1088,
  'universidad': 1089,
  'investigación': 1090,
  'batalla': 1091,
  'reglas': 1092,
  'cargo': 1093,
  'hogar': 1094,
  'ninguno': 1095,
  'dieron': 1096,
  'vuelva': 1097,
  'sabías': 1098,
  'respeto': 1099,
  'estación': 1100,
  'corte': 1101,
  'paciente': 1102,
  'encuentro': 1103,
  'energía': 1104,
  'dejado': 1105,
  'baile': 1106,
  'abuela': 1107,
  'caliente': 1108,
  'vieja': 1109,
  'viendo': 1110,
  'veremos': 1111,
  'rayos': 1112,
  'simple': 1113,
  'bailar': 1114,
  'papa': 1115,
  'triste': 1116,
  'zona': 1117,
  'serás': 1118,
  'guardia': 1119,
  'canción': 1120,
  'salud': 1121,
  'escuchar': 1122,
  'parar': 1123,
  'estarás': 1124,
  'cenar': 1125,
  'soldados': 1126,
  'caballo': 1127,
  'serán': 1128,
  'estaremos': 1129,
  'interesa': 1130,
  'volar': 1131,
  'principio': 1132,
  'nivel': 1133,
  'cálmate': 1134,
  'conocer': 1135,
  'finalmente': 1136,
  'alegro': 1137,
  'debajo': 1138,
  'podrían': 1139,
  'bosque': 1140,
  'bonita': 1141,
  'bolsa': 1142,
  'pone': 1143,
  'taxi': 1144,
  'ocupado': 1145,
  'amable': 1146,
  'acaso': 1147,
  'detente': 1148,
  'imbécil': 1149,
  'san': 1150,
  'equivocado': 1151,
  'viva': 1152,
  'puso': 1153,
  'obra': 1154,
  'consejo': 1155,
  'público': 1156,
  'ayúdame': 1157,
  'animales': 1158,
  'azul': 1159,
  'apuesto': 1160,
  'prisión': 1161,
  'mirar': 1162,
  'inteligente': 1163,
  'metros': 1164,
  'fantástico': 1165,
  'próximo': 1166,
  'jugando': 1167,
  'ojo': 1168,
  'salga': 1169,
  'vea': 1170,
  'llaman': 1171,
  'entrada': 1172,
  'duda': 1173,
  'cerveza': 1174,
  'unidos': 1175,
  'matado': 1176,
  'princesa': 1177,
  'perdí': 1178,
  'entender': 1179,
  'santa': 1180,
  'quedar': 1181,
  'miles': 1182,
  'llamaré': 1183,
  'compañero': 1184,
  'pensado': 1185,
  'espalda': 1186,
  'dejé': 1187,
  'bomba': 1188,
  'cartas': 1189,
  'apenas': 1190,
  'leer': 1191,
  'hermanos': 1192,
  'darme': 1193,
  'papi': 1194,
  'mantener': 1195,
  'suyo': 1196,
  'rico': 1197,
  'verla': 1198,
  'lee': 1199,
  'sigues': 1200,
  'toca': 1201,
  'olvídalo': 1202,
  'acción': 1203,
  'hayas': 1204,
  'dioses': 1205,
  'mando': 1206,
  'dejaré': 1207,
  'llegue': 1208,
  'formas': 1209,
  'uh': 1210,
  'cierra': 1211,
  'damas': 1212,
  'puente': 1213,
  'memoria': 1214,
  'regresa': 1215,
  'muévanse': 1216,
  'parecía': 1217,
  'vestido': 1218,
  'llaves': 1219,
  'tv': 1220,
  'camión': 1221,
  'acabar': 1222,
  'robot': 1223,
  'llevó': 1224,
  'montón': 1225,
  'estuviste': 1226,
  'máquina': 1227,
  'puertas': 1228,
  'podamos': 1229,
  'muere': 1230,
  'trago': 1231,
  'mayoría': 1232,
  'reina': 1233,
  'lleno': 1234,
  'inglés': 1235,
  'don': 1236,
  'soldado': 1237,
  'estrella': 1238,
  'escuche': 1239,
  'valor': 1240,
  'pido': 1241,
  'delante': 1242,
  'código': 1243,
  'héroe': 1244,
  'fe': 1245,
  'capaz': 1246,
  'verme': 1247,
  'beber': 1248,
  'velocidad': 1249,
  'darte': 1250,
  'llevas': 1251,
  'partido': 1252,
  'estuviera': 1253,
  'dia': 1254,
  'opinión': 1255,
  'irte': 1256,
  'cocina': 1257,
  'abrir': 1258,
  'perros': 1259,
  'tambien': 1260,
  'sepa': 1261,
  'pareces': 1262,
  'escribir': 1263,
  'golpe': 1264,
  'tenia': 1265,
  'alta': 1266,
  'estados': 1267,
  'tocar': 1268,
  'vuelvo': 1269,
  'habían': 1270,
  'ganas': 1271,
  'hacerte': 1272,
  'dejo': 1273,
  'volvió': 1274,
  'ejemplo': 1275,
  'contar': 1276,
  'tenían': 1277,
  'propósito': 1278,
  'entiende': 1279,
  'empieza': 1280,
  'anillo': 1281,
  'londres': 1282,
  'tendría': 1283,
  'pedir': 1284,
  'estilo': 1285,
  'ayudarme': 1286,
  'pista': 1287,
  'cambiado': 1288,
  'escapar': 1289,
  'eeuu': 1290,
  'encontró': 1291,
  'espíritu': 1292,
  'viejos': 1293,
  'molesta': 1294,
  'nota': 1295,
  'diferencia': 1296,
  'tratar': 1297,
  'precio': 1298,
  'caballero': 1299,
  'sirve': 1300,
  'alegra': 1301,
  'doble': 1302,
  'agradable': 1303,
  'embargo': 1304,
  'tengan': 1305,
  'tuvimos': 1306,
  'sube': 1307,
  'estén': 1308,
  'mami': 1309,
  'emergencia': 1310,
  'cuello': 1311,
  'boda': 1312,
  'aprender': 1313,
  'té': 1314,
  'informe': 1315,
  'blanca': 1316,
  'experiencia': 1317,
  'debía': 1318,
  'podido': 1319,
  'mentira': 1320,
  'director': 1321,
  'mata': 1322,
  'exacto': 1323,
  'déjalo': 1324,
  'defensa': 1325,
  'tuviste': 1326,
  'confiar': 1327,
  'color': 1328,
  'hacerle': 1329,
  'aquel': 1330,
  'conocí': 1331,
  'probar': 1332,
  'príncipe': 1333,
  'irá': 1334,
  'despacio': 1335,
  'tiro': 1336,
  'vacaciones': 1337,
  'perdió': 1338,
  'fondo': 1339,
  'verde': 1340,
  'parecen': 1341,
  'mama': 1342,
  'bienvenida': 1343,
  'opción': 1344,
  'operación': 1345,
  'hablado': 1346,
  'traer': 1347,
  'principal': 1348,
  'demonio': 1349,
  'zapatos': 1350,
  'leo': 1351,
  'respecto': 1352,
  'nuevos': 1353,
  'especie': 1354,
  'pidió': 1355,
  'éxito': 1356,
  'cabrón': 1357,
  'mírame': 1358,
  'piernas': 1359,
  'unidad': 1360,
  'hielo': 1361,
  'llegamos': 1362,
  'déjeme': 1363,
  'debí': 1364,
  'vendrá': 1365,
  'matrimonio': 1366,
  'habías': 1367,
  'podrá': 1368,
  'piel': 1369,
  'preocupa': 1370,
  'quedarme': 1371,
  'mio': 1372,
  'tuviera': 1373,
  'tiempos': 1374,
  'quienes': 1375,
  'oiga': 1376,
  'últimos': 1377,
  'oigan': 1378,
  'fuimos': 1379,
  'arte': 1380,
  'necesitaba': 1381,
  'quiénes': 1382,
  'común': 1383,
  'intentando': 1384,
  'llevaré': 1385,
  'levántate': 1386,
  'correr': 1387,
  'brillante': 1388,
  'calor': 1389,
  'planes': 1390,
  'dra': 1391,
  'visita': 1392,
  'presión': 1393,
  'pared': 1394,
  'esperanza': 1395,
  'completo': 1396,
  'subir': 1397,
  'cansado': 1398,
  'llame': 1399,
  'recordar': 1400,
  'trampa': 1401,
  'monstruo': 1402,
  'bajar': 1403,
  'pierna': 1404,
  'señores': 1405,
  'tomó': 1406,
  'pasará': 1407,
  'mataste': 1408,
  'grandioso': 1409,
  'socio': 1410,
  'herido': 1411,
  'cayó': 1412,
  'fuerzas': 1413,
  'cine': 1414,
  'escena': 1415,
  'tía': 1416,
  'dando': 1417,
  'puntos': 1418,
  'teníamos': 1419,
  'dejaste': 1420,
  'muévete': 1421,
  'larga': 1422,
  'solía': 1423,
  'tema': 1424,
  'preparado': 1425,
  'depende': 1426,
  'policías': 1427,
  'solos': 1428,
  'matarme': 1429,
  'pon': 1430,
  'hubiese': 1431,
  'bebe': 1432,
  'tanta': 1433,
  'oeste': 1434,
  'parís': 1435,
  'viento': 1436,
  'jóvenes': 1437,
  'hacerme': 1438,
  'asiento': 1439,
  'estupendo': 1440,
  'caminar': 1441,
  'juicio': 1442,
  'creerlo': 1443,
  'carga': 1444,
  'tranquila': 1445,
  'banda': 1446,
  'estarán': 1447,
  'animal': 1448,
  'escuchen': 1449,
  'encontraron': 1450,
  'dejas': 1451,
  'tarjeta': 1452,
  'ibas': 1453,
  'podrás': 1454,
  'nueve': 1455,
  'seguramente': 1456,
  'volverá': 1457,
  'majestad': 1458,
  'llena': 1459,
  'caer': 1460,
  'aeropuerto': 1461,
  'vayamos': 1462,
  'acabado': 1463,
  'sigo': 1464,
  'enorme': 1465,
  'área': 1466,
  'muevas': 1467,
  'haberlo': 1468,
  'dejen': 1469,
  'ridículo': 1470,
  'reloj': 1471,
  'hagamos': 1472,
  'flores': 1473,
  'mapa': 1474,
  'culpable': 1475,
  'cuentas': 1476,
  'pareja': 1477,
  'tomando': 1478,
  'laboratorio': 1479,
  'beso': 1480,
  'empezó': 1481,
  'pedí': 1482,
  'nariz': 1483,
  'cuestión': 1484,
  'terminó': 1485,
  'vengo': 1486,
  'perdona': 1487,
  'acuerdas': 1488,
  'verano': 1489,
  'prefiero': 1490,
  'peso': 1491,
  'oscuridad': 1492,
  'diferentes': 1493,
  'cliente': 1494,
  'cuántas': 1495,
  'televisión': 1496,
  'suya': 1497,
  'prensa': 1498,
  'vistazo': 1499,
  'inmediatamente': 1500,
  'solamente': 1501,
  'gordo': 1502,
  'quedarse': 1503,
  'posibilidad': 1504,
  'medicina': 1505,
  'malditos': 1506,
  'pantalones': 1507,
  'mataron': 1508,
  'ayude': 1509,
  'podremos': 1510,
  'locos': 1511,
  'nombres': 1512,
  'parecer': 1513,
  'américa': 1514,
  'definitivamente': 1515,
  'cinta': 1516,
  'desea': 1517,
  'brazos': 1518,
  'asuntos': 1519,
  'riesgo': 1520,
  'escuché': 1521,
  'lugares': 1522,
  'cuento': 1523,
  'noticia': 1524,
  'dígame': 1525,
  'decirlo': 1526,
  'tonterías': 1527,
  'sentimientos': 1528,
  'objetivo': 1529,
  'pasada': 1530,
  'arreglar': 1531,
  'casado': 1532,
  'bote': 1533,
  'roja': 1534,
  'leyes': 1535,
  'millón': 1536,
  'árbol': 1537,
  'hable': 1538,
  'proyecto': 1539,
  'nervioso': 1540,
  'lana': 1541,
  'salido': 1542,
  'pedazo': 1543,
  'malas': 1544,
  'joder': 1545,
  'humana': 1546,
  'absolutamente': 1547,
  'evitar': 1548,
  'corriendo': 1549,
  'bala': 1550,
  'vosotros': 1551,
  'despues': 1552,
  'debido': 1553,
  'confianza': 1554,
  'especialmente': 1555,
  'intenta': 1556,
  'pequeños': 1557,
  'paga': 1558,
  'ganado': 1559,
  'ama': 1560,
  'limpio': 1561,
  'comenzar': 1562,
  'ángeles': 1563,
  'robots': 1564,
  'lleve': 1565,
  'ruido': 1566,
  'preocupe': 1567,
  'belleza': 1568,
  'salgan': 1569,
  'come': 1570,
  'verá': 1571,
  'deberían': 1572,
  'líder': 1573,
  'preguntar': 1574,
  'inocente': 1575,
  'lástima': 1576,
  'red': 1577,
  'enfermedad': 1578,
  'desgraciado': 1579,
  'harán': 1580,
  'dientes': 1581,
  'maté': 1582,
  'central': 1583,
  'ponte': 1584,
  'felices': 1585,
  'supe': 1586,
  'haberte': 1587,
  'marcha': 1588,
  'poderes': 1589,
  'hubieras': 1590,
  'justicia': 1591,
  'bella': 1592,
  'dé': 1593,
  'proteger': 1594,
  'cabello': 1595,
  'pedido': 1596,
  'oscuro': 1597,
  'embarazada': 1598,
  'costa': 1599,
  'dama': 1600,
  'alguno': 1601,
  'orgulloso': 1602,
  'vos': 1603,
  'tomado': 1604,
  'digamos': 1605,
  'debió': 1606,
  'gana': 1607,
  'cura': 1608,
  'lengua': 1609,
  'araña': 1610,
  'podré': 1611,
  'voluntad': 1612,
  'total': 1613,
  'dedo': 1614,
  'pago': 1615,
  'agradezco': 1616,
  'partir': 1617,
  'lucha': 1618,
  'leche': 1619,
  'diste': 1620,
  'copa': 1621,
  'quedarte': 1622,
  'seguros': 1623,
  'destruir': 1624,
  'bienvenidos': 1625,
  'llevan': 1626,
  'ponga': 1627,
  'lados': 1628,
  'acceso': 1629,
  'intentar': 1630,
  'trajo': 1631,
  'desastre': 1632,
  'deber': 1633,
  'herida': 1634,
  'débil': 1635,
  'naturaleza': 1636,
  'luchar': 1637,
  'extraña': 1638,
  'encontraste': 1639,
  'vieron': 1640,
  'llámame': 1641,
  'imagen': 1642,
  'pan': 1643,
  'refieres': 1644,
  'clientes': 1645,
  'varios': 1646,
  'show': 1647,
  'auxilio': 1648,
  'oyes': 1649,
  'siguen': 1650,
  'sentado': 1651,
  'saliendo': 1652,
  'responsable': 1653,
  'olvidado': 1654,
  'quedó': 1655,
  'permite': 1656,
  'aquella': 1657,
  'puse': 1658,
  'sonido': 1659,
  'quiso': 1660,
  'escrito': 1661,
  'carro': 1662,
  'preciosa': 1663,
  'muestra': 1664,
  'bastardo': 1665,
  'restaurante': 1666,
  'huevos': 1667,
  'fantasma': 1668,
  'mismos': 1669,
  'profesional': 1670,
  'amas': 1671,
  'entero': 1672,
  'películas': 1673,
  'cerrado': 1674,
  'dedos': 1675,
  'sigan': 1676,
  'dueño': 1677,
  'cuchillo': 1678,
  'turno': 1679,
  'tormenta': 1680,
  'caray': 1681,
  'abierto': 1682,
  'quede': 1683,
  'verdadera': 1684,
  'mato': 1685,
  'doc': 1686,
  'playa': 1687,
  'km': 1688,
  'victoria': 1689,
  'primo': 1690,
  'oíste': 1691,
  'lisa': 1692,
  'interior': 1693,
  'hechos': 1694,
  'piedra': 1695,
  'jodido': 1696,
  'militar': 1697,
  'computadora': 1698,
  'encantaría': 1699,
  'siga': 1700,
  'silla': 1701,
  'movimiento': 1702,
  'momentos': 1703,
  'familiar': 1704,
  'apartamento': 1705,
  'disparar': 1706,
  'tesoro': 1707,
  'perfecta': 1708,
  'refiero': 1709,
  'motivo': 1710,
  'dará': 1711,
  'nuevas': 1712,
  'mr': 1713,
  'querer': 1714,
  'matarlo': 1715,
  'valiente': 1716,
  'víctima': 1717,
  'tendrán': 1718,
  'estúpida': 1719,
  'envió': 1720,
  'encantado': 1721,
  'diario': 1722,
  'abran': 1723,
  'sentí': 1724,
  'autobús': 1725,
  'traído': 1726,
  'casas': 1727,
  'apúrate': 1728,
  'encuentras': 1729,
  'descanso': 1730,
  'sheriff': 1731,
  'mueve': 1732,
  'inglaterra': 1733,
  'quieto': 1734,
  'matarte': 1735,
  'calles': 1736,
  'tamaño': 1737,
  'colegio': 1738,
  'usando': 1739,
  'cabo': 1740,
  'video': 1741,
  'toques': 1742,
  'pelota': 1743,
  'yendo': 1744,
  'directo': 1745,
  'termine': 1746,
  'vivos': 1747,
  'sombrero': 1748,
  'tomé': 1749,
  'estudio': 1750,
  'grave': 1751,
  'protección': 1752,
  'secretos': 1753,
  'perdiendo': 1754,
  'pecho': 1755,
  'sepas': 1756,
  'enferma': 1757,
  'cuesta': 1758,
  'negros': 1759,
  'estrellas': 1760,
  'lago': 1761,
  'clases': 1762,
  'obviamente': 1763,
  'inmediato': 1764,
  'modos': 1765,
  'irás': 1766,
  'robo': 1767,
  'mataré': 1768,
  'seria': 1769,
  'apoyo': 1770,
  'cerrar': 1771,
  'imagino': 1772,
  'amenaza': 1773,
  'toque': 1774,
  'saca': 1775,
  'viernes': 1776,
  'hayan': 1777,
  'roto': 1778,
  'piloto': 1779,
  'marca': 1780,
  'combate': 1781,
  'hablé': 1782,
  'manejar': 1783,
  'sección': 1784,
  'profundo': 1785,
  'tantos': 1786,
  'conocía': 1787,
  'serie': 1788,
  'contrato': 1789,
  'americano': 1790,
  'paseo': 1791,
  'aviones': 1792,
  'balas': 1793,
  'repente': 1794,
  'cambia': 1795,
  'tropas': 1796,
  'llevará': 1797,
  'uso': 1798,
  'pudiste': 1799,
  'detener': 1800,
  'sabia': 1801,
  'llegué': 1802,
  'negra': 1803,
  'decidido': 1804,
  'comienza': 1805,
  'distancia': 1806,
  'éramos': 1807,
  'murieron': 1808,
  'averiguar': 1809,
  'actuar': 1810,
  'quedo': 1811,
  'pulso': 1812,
  'aguanta': 1813,
  'oficiales': 1814,
  'llamaba': 1815,
  'enemigos': 1816,
  'saldrá': 1817,
  'preocupado': 1818,
  'elección': 1819,
  'sobrevivir': 1820,
  'bordo': 1821,
  'sujeto': 1822,
  'pacientes': 1823,
  'disculpen': 1824,
  'época': 1825,
  'entró': 1826,
  'pesar': 1827,
  'habló': 1828,
  'vender': 1829,
  'piensan': 1830,
  'ex': 1831,
  'gratis': 1832,
  'desapareció': 1833,
  'empleo': 1834,
  'llamé': 1835,
  'conocen': 1836,
  'cerdo': 1837,
  'puedan': 1838,
  'comprendo': 1839,
  'tomo': 1840,
  'termina': 1841,
  'aléjate': 1842,
  'busco': 1843,
  'contento': 1844,
  'date': 1845,
  'tumba': 1846,
  'huellas': 1847,
  'cuerpos': 1848,
  'absoluto': 1849,
  'pide': 1850,
  'dura': 1851,
  'descansar': 1852,
  'respuestas': 1853,
  'propiedad': 1854,
  'llamando': 1855,
  'dormido': 1856,
  'castillo': 1857,
  'china': 1858,
  'datos': 1859,
  'torre': 1860,
  'robar': 1861,
  'americanos': 1862,
  'malos': 1863,
  'botella': 1864,
  'aquellos': 1865,
  'últimamente': 1866,
  'testigo': 1867,
  'presento': 1868,
  'periódico': 1869,
  'juez': 1870,
  'historias': 1871,
  'gustaba': 1872,
  'créeme': 1873,
  'necesitar': 1874,
  'esquina': 1875,
  'millas': 1876,
  'llamadas': 1877,
  'fuente': 1878,
  'conocemos': 1879,
  'techo': 1880,
  'genio': 1881,
  'alerta': 1882,
  'ideas': 1883,
  'tomas': 1884,
  'olvidar': 1885,
  'menor': 1886,
  'droga': 1887,
  'explicar': 1888,
  'abierta': 1889,
  'privado': 1890,
  'miembros': 1891,
  'nacional': 1892,
  'gustó': 1893,
  'discutir': 1894,
  'evidencia': 1895,
  'detalles': 1896,
  'tardes': 1897,
  'tomaré': 1898,
  'presente': 1899,
  'criminal': 1900,
  'premio': 1901,
  'vuelvas': 1902,
  'siéntese': 1903,
  'viniste': 1904,
  'recibir': 1905,
  'mires': 1906,
  'teniendo': 1907,
  'huele': 1908,
  'hables': 1909,
  'guardias': 1910,
  'enamorado': 1911,
  'cuidar': 1912,
  'pocos': 1913,
  'perdone': 1914,
  'natural': 1915,
  'caballos': 1916,
  'escucho': 1917,
  'enfermera': 1918,
  'ángel': 1919,
  'sospechoso': 1920,
  'local': 1921,
  'mentiras': 1922,
  'camisa': 1923,
  'confía': 1924,
  'aceptar': 1925,
  'venganza': 1926,
  'perdimos': 1927,
  'cuantos': 1928,
  'parque': 1929,
  'juegos': 1930,
  'vives': 1931,
  'varias': 1932,
  'harías': 1933,
  'menudo': 1934,
  'déjenme': 1935,
  'barrio': 1936,
  'virus': 1937,
  'talento': 1938,
  'ministro': 1939,
  'hiciera': 1940,
  'robó': 1941,
  'conocerte': 1942,
  'viviendo': 1943,
  'responsabilidad': 1944,
  'respira': 1945,
  'números': 1946,
  'pertenece': 1947,
  'desayuno': 1948,
  'campeón': 1949,
  'controlar': 1950,
  'maría': 1951,
  'nieve': 1952,
  'méxico': 1953,
  'dias': 1954,
  'vergüenza': 1955,
  'doctora': 1956,
  'blancos': 1957,
  'fumar': 1958,
  'llevamos': 1959,
  'europa': 1960,
  'dudo': 1961,
  'pastel': 1962,
  'quedas': 1963,
  'cae': 1964,
  'vimos': 1965,
  'recibido': 1966,
  'andando': 1967,
  'agentes': 1968,
  'fueras': 1969,
  'asustado': 1970,
  'perdóname': 1971,
  'reino': 1972,
  'clave': 1973,
  'papeles': 1974,
  'matando': 1975,
  'efecto': 1976,
  'plata': 1977,
  'olvidé': 1978,
  'iban': 1979,
  'universo': 1980,
  'política': 1981,
  'almuerzo': 1982,
  'magia': 1983,
  'lluvia': 1984,
  'humor': 1985,
  'autos': 1986,
  'veía': 1987,
  'tantas': 1988,
  'respirar': 1989,
  'cantar': 1990,
  'trabajas': 1991,
  'disparo': 1992,
  'conversación': 1993,
  'olor': 1994,
  'asesinos': 1995,
  'muchacha': 1996,
  'modelo': 1997,
  'curso': 1998,
  'canal': 1999,
  'rock': 2000,
  'llorar': 2001,
  'borracho': 2002,
  'regresen': 2003,
  'váyanse': 2004,
  'sentía': 2005,
  'resultado': 2006,
  'parado': 2007,
  'olvides': 2008,
  'mirada': 2009,
  'inútil': 2010,
  'acabas': 2011,
  'ciencia': 2012,
  'perdiste': 2013,
  'sociedad': 2014,
  'regrese': 2015,
  'camioneta': 2016,
  'coño': 2017,
  'revista': 2018,
  'pareció': 2019,
  'montaña': 2020,
  'maravillosa': 2021,
  'dejarlo': 2022,
  'máximo': 2023,
  'echar': 2024,
  'corriente': 2025,
  'escuchando': 2026,
  'disculpas': 2027,
  'cadáver': 2028,
  'seguimos': 2029,
  'ron': 2030,
  'espectáculo': 2031,
  'mundial': 2032,
  'francia': 2033,
  'decirles': 2034,
  'aspecto': 2035,
  'presencia': 2036,
  'famoso': 2037,
  'corto': 2038,
  'relájate': 2039,
  'poderoso': 2040,
  'recuerdos': 2041,
  'llego': 2042,
  'carretera': 2043,
  'pasé': 2044,
  'oigo': 2045,
  'esperamos': 2046,
  'escuchado': 2047,
  'cable': 2048,
  'rosa': 2049,
  'bola': 2050,
  'oferta': 2051,
  'intentarlo': 2052,
  'habido': 2053,
  'continuar': 2054,
  'ladrón': 2055,
  'humanidad': 2056,
  'estómago': 2057,
  'entera': 2058,
  'contó': 2059,
  'poniendo': 2060,
  'mercado': 2061,
  'granja': 2062,
  'idiotas': 2063,
  'hablemos': 2064,
  'estudiante': 2065,
  'siglo': 2066,
  'relaciones': 2067,
  'hablan': 2068,
  'nosotras': 2069,
  'miembro': 2070,
  'fortuna': 2071,
  'libres': 2072,
  'acciones': 2073,
  'esperas': 2074,
  'suficientemente': 2075,
  'llevaron': 2076,
  'ocurrido': 2077,
  'andar': 2078,
  'perfectamente': 2079,
  'pasan': 2080,
  'jardín': 2081,
  'cola': 2082,
  'resultados': 2083,
  'francés': 2084,
  'troya': 2085,
  'tercera': 2086,
  'entrenamiento': 2087,
  'azúcar': 2088,
  'metido': 2089,
  'ayudarlo': 2090,
  'diles': 2091,
  'coger': 2092,
  'lunes': 2093,
  'ey': 2094,
  'intención': 2095,
  'volviendo': 2096,
  'efectivo': 2097,
  'salgamos': 2098,
  'preguntaba': 2099,
  'olvida': 2100,
  'helicóptero': 2101,
  'fuertes': 2102,
  'encantador': 2103,
  'original': 2104,
  'cadena': 2105,
  'tarea': 2106,
  'dispara': 2107,
  'corta': 2108,
  'compañeros': 2109,
  'cirugía': 2110,
  'quítate': 2111,
  'querían': 2112,
  'precioso': 2113,
  'lord': 2114,
  'comenzó': 2115,
  'truco': 2116,
  'inspector': 2117,
  'enojado': 2118,
  'felicidades': 2119,
  'desaparecido': 2120,
  'acto': 2121,
  'pollo': 2122,
  'chocolate': 2123,
  'adonde': 2124,
  'resulta': 2125,
  'merece': 2126,
  'gas': 2127,
  'examen': 2128,
  'vean': 2129,
  'tonta': 2130,
  'importantes': 2131,
  'apuesta': 2132,
  'directamente': 2133,
  'recuperar': 2134,
  'pongo': 2135,
  'llegaron': 2136,
  'cobarde': 2137,
  'cantidad': 2138,
  'sucio': 2139,
  'habia': 2140,
  'emperador': 2141,
  'mono': 2142,
  'encontraremos': 2143,
  'disparen': 2144,
  'gloria': 2145,
  'contrario': 2146,
  'socorro': 2147,
  'sir': 2148,
  'escaleras': 2149,
  'pasos': 2150,
  'nuevamente': 2151,
  'derechos': 2152,
  'obtener': 2153,
  'guste': 2154,
  'búsqueda': 2155,
  'helado': 2156,
  'fútbol': 2157,
  'sexual': 2158,
  'polvo': 2159,
  'aseguro': 2160,
  'rostro': 2161,
  'robado': 2162,
  'policia': 2163,
  'empresa': 2164,
  'escuchas': 2165,
  'pones': 2166,
  'lárgate': 2167,
  'interés': 2168,
  'discúlpeme': 2169,
  'dejaron': 2170,
  'tengamos': 2171,
  'firma': 2172,
  'llevaba': 2173,
  'llegas': 2174,
  'cristo': 2175,
  'tercer': 2176,
  'senador': 2177,
  'continúa': 2178,
  'gracia': 2179,
  'cien': 2180,
  'des': 2181,
  'tratado': 2182,
  'razones': 2183,
  'caminando': 2184,
  'urgencias': 2185,
  'u': 2186,
  'esperan': 2187,
  'muñeca': 2188,
  'firme': 2189,
  'duerme': 2190,
  'ambulancia': 2191,
  'limpia': 2192,
  'habéis': 2193,
  'flota': 2194,
  'leyenda': 2195,
  'encuentre': 2196,
  'fué': 2197,
  'romper': 2198,
  'muriendo': 2199,
  'encontrarlo': 2200,
  'rescate': 2201,
  'quedado': 2202,
  'enfrente': 2203,
  'comienzo': 2204,
  'árboles': 2205,
  'queso': 2206,
  'favorito': 2207,
  'haberme': 2208,
  'frontera': 2209,
  'creemos': 2210,
  'colega': 2211,
  'jueves': 2212,
  'alemanes': 2213,
  'sábado': 2214,
  'limpiar': 2215,
  'puto': 2216,
  'poquito': 2217,
  'cientos': 2218,
  'muero': 2219,
  'irak': 2220,
  'inteligencia': 2221,
  'funcionar': 2222,
  'felicitaciones': 2223,
  'escapó': 2224,
  'criatura': 2225,
  'milagro': 2226,
  'deprisa': 2227,
  'especiales': 2228,
  'campamento': 2229,
  'responde': 2230,
  'escuchaste': 2231,
  'debiste': 2232,
  'adios': 2233,
  'viven': 2234,
  'recoger': 2235,
  'preguntarte': 2236,
  'compras': 2237,
  'cansada': 2238,
  'elegir': 2239,
  'cáncer': 2240,
  'servir': 2241,
  'pata': 2242,
  'huir': 2243,
  'útil': 2244,
  'grados': 2245,
  'atacar': 2246,
  'recién': 2247,
  'querría': 2248,
  'guapo': 2249,
  'alcance': 2250,
  'dejame': 2251,
  'quedamos': 2252,
  'llegará': 2253,
  'seremos': 2254,
  'desierto': 2255,
  'licencia': 2256,
  'trató': 2257,
  'proceso': 2258,
  'llevado': 2259,
  'durmiendo': 2260,
  'tensión': 2261,
  'martes': 2262,
  'kilómetros': 2263,
  'cartera': 2264,
  'tráfico': 2265,
  'guarda': 2266,
  'impresionante': 2267,
  'víctimas': 2268,
  'ricos': 2269,
  'primeros': 2270,
  'dejamos': 2271,
  'tomen': 2272,
  'iguales': 2273,
  'domingo': 2274,
  'crear': 2275,
  'quedará': 2276,
  'pérdida': 2277,
  'extra': 2278,
  'fuese': 2279,
  'confío': 2280,
  'sabido': 2281,
  'análisis': 2282,
  'llegaste': 2283,
  'fría': 2284,
  'violencia': 2285,
  'mate': 2286,
  'lobo': 2287,
  'discurso': 2288,
  'actor': 2289,
  'orgullo': 2290,
  'odia': 2291,
  'libras': 2292,
  'alli': 2293,
  'agencia': 2294,
  'vuelvan': 2295,
  'necesidad': 2296,
  'naves': 2297,
  'descubrir': 2298,
  'tira': 2299,
  'resolver': 2300,
  'pondré': 2301,
  'volando': 2302,
  'pensamos': 2303,
  'míos': 2304,
  'errores': 2305,
  'celular': 2306,
  'cuantas': 2307,
  'verlos': 2308,
  'ayudarle': 2309,
  'meter': 2310,
  'hablaré': 2311,
  'ésto': 2312,
  'buscas': 2313,
  'equivocada': 2314,
  'dejan': 2315,
  'vacío': 2316,
  'trabajaba': 2317,
  'salón': 2318,
  'motor': 2319,
  'conducir': 2320,
  'compré': 2321,
  'quizas': 2322,
  'den': 2323,
  'cerrada': 2324,
  'cortar': 2325,
  'crea': 2326,
  'sera': 2327,
  'mueva': 2328,
  'hazme': 2329,
  'bravo': 2330,
  'pregunté': 2331,
  'promesa': 2332,
  'espejo': 2333,
  'dígale': 2334,
  'sienta': 2335,
  'médicos': 2336,
  'cero': 2337,
  'escribió': 2338,
  'volveremos': 2339,
  'unidades': 2340,
  'cheque': 2341,
  'consigue': 2342,
  'antiguo': 2343,
  'regla': 2344,
  'pesadilla': 2345,
  'enviar': 2346,
  'podías': 2347,
  'llevarte': 2348,
  'entrenador': 2349,
  'cuida': 2350,
  'paciencia': 2351,
  'dilo': 2352,
  'levanta': 2353,
  'complicado': 2354,
  'superior': 2355,
  'sugiero': 2356,
  'familias': 2357,
  'dirá': 2358,
  'pienses': 2359,
  'temporada': 2360,
  'suelta': 2361,
  'corran': 2362,
  'propios': 2363,
  'feo': 2364,
  'dispuesto': 2365,
  'ayúdenme': 2366,
  'teoría': 2367,
  'salvaje': 2368,
  'negativo': 2369,
  'íbamos': 2370,
  'hubieran': 2371,
  'tripulación': 2372,
  'taza': 2373,
  'perdida': 2374,
  'vinieron': 2375,
  'ponen': 2376,
  'pasamos': 2377,
  'parecido': 2378,
  'acepto': 2379,
  'gay': 2380,
  'señoras': 2381,
  'quedaré': 2382,
  'parada': 2383,
  'niñas': 2384,
  'conseguí': 2385,
  'acabamos': 2386,
  'normalmente': 2387,
  'muchísimo': 2388,
  'bruja': 2389,
  'vegas': 2390,
  'tuvieron': 2391,
  'traigo': 2392,
  'matará': 2393,
  'funeral': 2394,
  'conseguiste': 2395,
  'teatro': 2396,
  'medianoche': 2397,
  'biblioteca': 2398,
  'estemos': 2399,
  'túnel': 2400,
  'encargo': 2401,
  'aparece': 2402,
  'testigos': 2403,
  'pensó': 2404,
  'fueran': 2405,
  'fantasmas': 2406,
  'correo': 2407,
  'vuestro': 2408,
  'piscina': 2409,
  'paquete': 2410,
  'copia': 2411,
  'suicidio': 2412,
  'crédito': 2413,
  'consigo': 2414,
  'vuestra': 2415,
  'rara': 2416,
  'legal': 2417,
  'hablaremos': 2418,
  'cambió': 2419,
  'solas': 2420,
  'salimos': 2421,
  'ganador': 2422,
  'estudiantes': 2423,
  'despierto': 2424,
  'pare': 2425,
  'invitado': 2426,
  'guía': 2427,
  'oso': 2428,
  'leí': 2429,
  'explosión': 2430,
  'dejarme': 2431,
  'chino': 2432,
  'sorprende': 2433,
  'sistemas': 2434,
  'nación': 2435,
  'entienden': 2436,
  'arena': 2437,
  'mentiroso': 2438,
  'hablaba': 2439,
  'veas': 2440,
  'pura': 2441,
  'asesinado': 2442,
  'mintiendo': 2443,
  'casos': 2444,
  'vehículo': 2445,
  'rata': 2446,
  'importancia': 2447,
  'distinto': 2448,
  'pintura': 2449,
  'intenté': 2450,
  'conciencia': 2451,
  'miras': 2452,
  'máquinas': 2453,
  'traeré': 2454,
  'revisar': 2455,
  'veinte': 2456,
  'seres': 2457,
  'hablarle': 2458,
  'disco': 2459,
  'transporte': 2460,
  'mina': 2461,
  'labios': 2462,
  'señoría': 2463,
  'llamamos': 2464,
  'doce': 2465,
  'cuídate': 2466,
  'pasillo': 2467,
  'tele': 2468,
  'podria': 2469,
  'completa': 2470,
  'rápida': 2471,
  'precisamente': 2472,
  'llamaste': 2473,
  'cumplir': 2474,
  'servicios': 2475,
  'señales': 2476,
  'acaban': 2477,
  'suéltame': 2478,
  'hablarte': 2479,
  'traté': 2480,
  'pescado': 2481,
  'huesos': 2482,
  'daría': 2483,
  'compra': 2484,
  'trajiste': 2485,
  'templo': 2486,
  'placa': 2487,
  'posibilidades': 2488,
  'ira': 2489,
  'vengas': 2490,
  'siguiendo': 2491,
  'podrán': 2492,
  'llamarme': 2493,
  'cierta': 2494,
  'permitir': 2495,
  'condiciones': 2496,
  'atrapar': 2497,
  'temperatura': 2498,
  'explicación': 2499,
  'enviado': 2500,
  'contado': 2501,
  'asistente': 2502,
  'imperio': 2503,
  'pidiendo': 2504,
  'notas': 2505,
  'curioso': 2506,
  'operaciones': 2507,
  'mantén': 2508,
  'tierras': 2509,
  'creas': 2510,
  'pez': 2511,
  'morirá': 2512,
  'visión': 2513,
  'montañas': 2514,
  'comido': 2515,
  'construir': 2516,
  'comunidad': 2517,
  'verán': 2518,
  'pierde': 2519,
  'piense': 2520,
  'felicidad': 2521,
  'diversión': 2522,
  'salvó': 2523,
  'quita': 2524,
  'museo': 2525,
  'disparó': 2526,
  'desearía': 2527,
  'misterio': 2528,
  'buscamos': 2529,
  'aviso': 2530,
  'almirante': 2531,
  'solución': 2532,
  'pequeñas': 2533,
  'faltan': 2534,
  'existen': 2535,
  'combustible': 2536,
  'leído': 2537,
  'tirar': 2538,
  'responder': 2539,
  'esfuerzo': 2540,
  'vuelves': 2541,
  'pájaro': 2542,
  'hermanas': 2543,
  'guerrero': 2544,
  'esposas': 2545,
  'coma': 2546,
  'abra': 2547,
  'traes': 2548,
  'sientas': 2549,
  'olvide': 2550,
  'bolsillo': 2551,
  'heridas': 2552,
  'saco': 2553,
  'gasolina': 2554,
  'conexión': 2555,
  'correcta': 2556,
  'pedazos': 2557,
  'líneas': 2558,
  'estarías': 2559,
  'convierte': 2560,
  'ayudará': 2561,
  'rumbo': 2562,
  'rompió': 2563,
  'muera': 2564,
  'mia': 2565,
  'material': 2566,
  'diamantes': 2567,
  'rápidamente': 2568,
  'mírate': 2569,
  'fecha': 2570,
  'escritorio': 2571,
  'digan': 2572,
  'registro': 2573,
  'maten': 2574,
  'incendio': 2575,
  'deme': 2576,
  'sois': 2577,
  'pensamientos': 2578,
  'dímelo': 2579,
  'comiendo': 2580,
  'aparte': 2581,
  'asesinatos': 2582,
  'lección': 2583,
  'algun': 2584,
  'abrigo': 2585,
  'tratamiento': 2586,
  'julio': 2587,
  'pene': 2588,
  'conejo': 2589,
  'suceder': 2590,
  'ruta': 2591,
  'ciego': 2592,
  'asqueroso': 2593,
  'súper': 2594,
  'prisionero': 2595,
  'odie': 2596,
  'ocupada': 2597,
  'llevarlo': 2598,
  'empezamos': 2599,
  'estuvimos': 2600,
  'decírselo': 2601,
  'honesto': 2602,
  'entrevista': 2603,
  'antigua': 2604,
  'amante': 2605,
  'humo': 2606,
  'gustas': 2607,
  'condado': 2608,
  'conseguido': 2609,
  'viajar': 2610,
  'sombra': 2611,
  'sabrá': 2612,
  'refiere': 2613,
  'lío': 2614,
  'estáis': 2615,
  'entrega': 2616,
  'drácula': 2617,
  'compartir': 2618,
  'volvamos': 2619,
  'sonrisa': 2620,
  'sencillo': 2621,
  'atrapado': 2622,
  'aburrido': 2623,
  'pocas': 2624,
  'pensaste': 2625,
  'dragón': 2626,
  'volví': 2627,
  'planta': 2628,
  'clima': 2629,
  'adoro': 2630,
  'suave': 2631,
  'ruso': 2632,
  'rodillas': 2633,
  'marina': 2634,
  'bolso': 2635,
  'alarma': 2636,
  'vivía': 2637,
  'quédese': 2638,
  'recompensa': 2639,
  'juega': 2640,
  'cigarrillo': 2641,
  'autoridad': 2642,
  'amigas': 2643,
  'agujero': 2644,
  'acabe': 2645,
  'sacaste': 2646,
  'orgullosa': 2647,
  'entendí': 2648,
  'dijera': 2649,
  'sótano': 2650,
  'paredes': 2651,
  'guapa': 2652,
  'iría': 2653,
  'imaginar': 2654,
  'fiebre': 2655,
  'déjala': 2656,
  'botón': 2657,
  'preguntarle': 2658,
  'leyendo': 2659,
  'darles': 2660,
  'uniforme': 2661,
  'dulces': 2662,
  'vaso': 2663,
  'documentos': 2664,
  'ruego': 2665,
  'maneras': 2666,
  'invitados': 2667,
  'cómodo': 2668,
  'ayudarnos': 2669,
  'visitar': 2670,
  'vigilancia': 2671,
  'nadar': 2672,
  'altura': 2673,
  'todavia': 2674,
  'preparados': 2675,
  'cuáles': 2676,
  'mover': 2677,
  'jugador': 2678,
  'izquierdo': 2679,
  'tecnología': 2680,
  'raza': 2681,
  'conocerlo': 2682,
  'conde': 2683,
  'social': 2684,
  'gobernador': 2685,
  'coge': 2686,
  'cubierta': 2687,
  'chiste': 2688,
  'celda': 2689,
  'agarra': 2690,
  'secreta': 2691,
  'personalmente': 2692,
  'amistad': 2693,
  'amaba': 2694,
  'nació': 2695,
  'interesado': 2696,
  'intentado': 2697,
  'educación': 2698,
  'curiosidad': 2699,
  'buscan': 2700,
  'repito': 2701,
  'pecado': 2702,
  'mortal': 2703,
  'manda': 2704,
  'claramente': 2705,
  'vía': 2706,
  'juntas': 2707,
  'identificación': 2708,
  'dirige': 2709,
  'declaración': 2710,
  'alemán': 2711,
  'tontos': 2712,
  'mantiene': 2713,
  'bestia': 2714,
  'veneno': 2715,
  'vecinos': 2716,
  'supiera': 2717,
  'quieran': 2718,
  'salgo': 2719,
  'propias': 2720,
  'privada': 2721,
  'jurado': 2722,
  'golpes': 2723,
  'usan': 2724,
  'metas': 2725,
  'garganta': 2726,
  'invierno': 2727,
  'fila': 2728,
  'miel': 2729,
  'traigan': 2730,
  'sucedido': 2731,
  'tranquilos': 2732,
  'madera': 2733,
  'burro': 2734,
  'artista': 2735,
  'pobres': 2736,
  'impresión': 2737,
  'ganó': 2738,
  'cruzar': 2739,
  'cigarrillos': 2740,
  'ó': 2741,
  'llevarme': 2742,
  'grial': 2743,
  'chaqueta': 2744,
  'alcalde': 2745,
  'vueltas': 2746,
  'usado': 2747,
  'reputación': 2748,
  'pongas': 2749,
  'oxígeno': 2750,
  'cajas': 2751,
  'preocupada': 2752,
  'matan': 2753,
  'empezando': 2754,
  'cabezas': 2755,
  'ponerme': 2756,
  'experto': 2757,
  'océano': 2758,
  'maestra': 2759,
  'pieza': 2760,
  'cargos': 2761,
  'bombas': 2762,
  'alcohol': 2763,
  'trataba': 2764,
  'salen': 2765,
  'pizza': 2766,
  'recibí': 2767,
  'junta': 2768,
  'criaturas': 2769,
  'apareció': 2770,
  'actitud': 2771,
  'gira': 2772,
  'cristal': 2773,
  'encuentran': 2774,
  'dejarte': 2775,
  'caído': 2776,
  'ultima': 2777,
  'sensación': 2778,
  'seguido': 2779,
  'puro': 2780,
  'whisky': 2781,
  'vernos': 2782,
  'pantalla': 2783,
  'bebida': 2784,
  'estudiar': 2785,
  'ducha': 2786,
  'despedido': 2787,
  'consiguió': 2788,
  'venía': 2789,
  'pagan': 2790,
  'discúlpame': 2791,
  'artículo': 2792,
  'aparentemente': 2793,
  'ambas': 2794,
  'trauma': 2795,
  'pusieron': 2796,
  'fiestas': 2797,
  'contesta': 2798,
  'contenta': 2799,
  'ambiente': 2800,
  'prometí': 2801,
  'llegando': 2802,
  'mostraré': 2803,
  'deba': 2804,
  'berlín': 2805,
  'nerviosa': 2806,
  'hallar': 2807,
  'dudas': 2808,
  'llegan': 2809,
  'instrucciones': 2810,
  'baje': 2811,
  'vinimos': 2812,
  'ritmo': 2813,
  'quédense': 2814,
  'suponía': 2815,
  'pistas': 2816,
  'palacio': 2817,
  'encontraré': 2818,
  'venta': 2819,
  'urgente': 2820,
  'tomamos': 2821,
  'salí': 2822,
  'cuerda': 2823,
  'vendría': 2824,
  'pasaba': 2825,
  'instante': 2826,
  'almas': 2827,
  'suficientes': 2828,
  'secundaria': 2829,
  'saltar': 2830,
  'queria': 2831,
  'quedarnos': 2832,
  'molesto': 2833,
  'colina': 2834,
  'porquería': 2835,
  'sienten': 2836,
  'pondrá': 2837,
  'terroristas': 2838,
  'empecé': 2839,
  'pongan': 2840,
  'moto': 2841,
  'opinas': 2842,
  'habilidad': 2843,
  'entren': 2844,
  'descubierto': 2845,
  'convertido': 2846,
  'asegúrate': 2847,
  'quedé': 2848,
  'kilos': 2849,
  'florida': 2850,
  'tomará': 2851,
  'sufrir': 2852,
  'mental': 2853,
  'lograr': 2854,
  'instituto': 2855,
  'serlo': 2856,
  'rapido': 2857,
  'plato': 2858,
  'llorando': 2859,
  'vd': 2860,
  'váyase': 2861,
  'sucediendo': 2862,
  'robaron': 2863,
  'alemania': 2864,
  'bolas': 2865,
  'pasaría': 2866,
  'ocurra': 2867,
  'ilegal': 2868,
  'fresco': 2869,
  'falso': 2870,
  'abogados': 2871,
  'tómalo': 2872,
  'mandó': 2873,
  'cuán': 2874,
  'cuales': 2875,
  'botas': 2876,
  'tiburones': 2877,
  'grado': 2878,
  'deseas': 2879,
  'archivos': 2880,
  'adivina': 2881,
  'cruz': 2882,
  'campaña': 2883,
  'asustada': 2884,
  'centavos': 2885,
  'vivimos': 2886,
  'galletas': 2887,
  'frecuencia': 2888,
  'daños': 2889,
  'minas': 2890,
  'emociones': 2891,
  'dejará': 2892,
  'regresó': 2893,
  'ratas': 2894,
  'pasión': 2895,
  'hacerse': 2896,
  'comportamiento': 2897,
  'pense': 2898,
  'conocimiento': 2899,
  'sales': 2900,
  'fiscal': 2901,
  'decisiones': 2902,
  'criminales': 2903,
  'cámaras': 2904,
  'amanecer': 2905,
  'convirtió': 2906,
  'aldea': 2907,
  'abandonar': 2908,
  'obvio': 2909,
  'habíamos': 2910,
  'apesta': 2911,
  'abandonado': 2912,
  'volverás': 2913,
  'preocuparse': 2914,
  'favorita': 2915,
  'lanzamiento': 2916,
  'difíciles': 2917,
  'vivido': 2918,
  'roca': 2919,
  'peces': 2920,
  'maravilla': 2921,
  'espacial': 2922,
  'conductor': 2923,
  'camarada': 2924,
  'trabajado': 2925,
  'caramba': 2926,
  'sopa': 2927,
  'numero': 2928,
  'gritar': 2929,
  'envía': 2930,
  'casino': 2931,
  'alteza': 2932,
  'identidad': 2933,
  'cambios': 2934,
  'guardar': 2935,
  'gatos': 2936,
  'gané': 2937,
  'arresto': 2938,
  'éstas': 2939,
  'preguntó': 2940,
  'éstos': 2941,
  'probable': 2942,
  'seamos': 2943,
  'importaría': 2944,
  'firmar': 2945,
  'federal': 2946,
  'caras': 2947,
  'trabajos': 2948,
  'hagámoslo': 2949,
  'expediente': 2950,
  'tigre': 2951,
  'intentó': 2952,
  'harto': 2953,
  'decidir': 2954,
  'periódicos': 2955,
  'casarse': 2956,
  'lento': 2957,
  'hecha': 2958,
  'daba': 2959,
  'ponerte': 2960,
  'pelotas': 2961,
  'monstruos': 2962,
  'cruel': 2963,
  'señoritas': 2964,
  'gigante': 2965,
  'siglos': 2966,
  'escalera': 2967,
  'incidente': 2968,
  'escenario': 2969,
  'dirás': 2970,
  'ciertas': 2971,
  'casar': 2972,
  'llamen': 2973,
  'biblia': 2974,
  'reporte': 2975,
  'moda': 2976,
  'casada': 2977,
  'reacción': 2978,
  'falla': 2979,
  'castigo': 2980,
  'fácilmente': 2981,
  'caza': 2982,
  'magnífico': 2983,
  'decidí': 2984,
  'camina': 2985,
  'superficie': 2986,
  'procedimiento': 2987,
  'condición': 2988,
  'asalto': 2989,
  'virgen': 2990,
  'verle': 2991,
  'conocimos': 2992,
  'quitar': 2993,
  'tratas': 2994,
  'noble': 2995,
  'hierba': 2996,
  'excelencia': 2997,
  'asco': 2998,
  'título': 2999,
  'regresaré': 3000,
  'ejercicio': 3001,
  'pasaporte': 3002,
  'extraños': 3003,
  'ciento': 3004,
  'afortunado': 3005,
  'sacó': 3006,
  'irán': 3007,
  'sacerdote': 3008,
  'pedirle': 3009,
  'impuestos': 3010,
  'crisis': 3011,
  'aventura': 3012,
  'puestos': 3013,
  'pánico': 3014,
  'llames': 3015,
  'ladrones': 3016,
  'enseñó': 3017,
  'logramos': 3018,
  'estuvieron': 3019,
  'últimas': 3020,
  'página': 3021,
  'metro': 3022,
  'alla': 3023,
  'pudieron': 3024,
  'mostrar': 3025,
  'escribe': 3026,
  'ventaja': 3027,
  'enamorada': 3028,
  'gorda': 3029,
  'vendrán': 3030,
  'paren': 3031,
  'piedras': 3032,
  'pesado': 3033,
  'despertar': 3034,
  'pierdes': 3035,
  'jesucristo': 3036,
  'damos': 3037,
  'treinta': 3038,
  'recuerde': 3039,
  'capacidad': 3040,
  'soportar': 3041,
  'senado': 3042,
  'decidió': 3043,
  'prepárense': 3044,
  'asusta': 3045,
  'aquello': 3046,
  'aguantar': 3047,
  'aprendí': 3048,
  'tenéis': 3049,
  'parezca': 3050,
  'alegría': 3051,
  'territorio': 3052,
  'física': 3053,
  'escape': 3054,
  'tomes': 3055,
  'excusa': 3056,
  'estudios': 3057,
  'septiembre': 3058,
  'personaje': 3059,
  'fábrica': 3060,
  'elegido': 3061,
  'cole': 3062,
  'tanque': 3063,
  'salta': 3064,
  'hablaste': 3065,
  'conferencia': 3066,
  'compromiso': 3067,
  'ayudaré': 3068,
  'salieron': 3069,
  'rusos': 3070,
  'parezco': 3071,
  'llevando': 3072,
  'exterior': 3073,
  'punta': 3074,
  'ponerse': 3075,
  'pasen': 3076,
  'puerto': 3077,
  'decírtelo': 3078,
  'caro': 3079,
  'asombroso': 3080,
  'pasara': 3081,
  'matarán': 3082,
  'maleta': 3083,
  'competencia': 3084,
  'canciones': 3085,
  'cinturón': 3086,
  'papas': 3087,
  'marco': 3088,
  'destrucción': 3089,
  'paraíso': 3090,
  'meta': 3091,
  'alternativa': 3092,
  'socios': 3093,
  'alas': 3094,
  'ocasión': 3095,
  'casarme': 3096,
  'salsa': 3097,
  'romano': 3098,
  'prisioneros': 3099,
  'encantan': 3100,
  'encantadora': 3101,
  'comercial': 3102,
  'primavera': 3103,
  'metió': 3104,
  'volvemos': 3105,
  'mandar': 3106,
  'luce': 3107,
  'sagrado': 3108,
  'nacido': 3109,
  'fabuloso': 3110,
  'echo': 3111,
  'desaparecer': 3112,
  'potencia': 3113,
  'adn': 3114,
  'traiga': 3115,
  'permítame': 3116,
  'mayores': 3117,
  'regalos': 3118,
  'pusiste': 3119,
  'peligrosa': 3120,
  'militares': 3121,
  'jodida': 3122,
  'coincidencia': 3123,
  'bandera': 3124,
  'adivinar': 3125,
  'tubo': 3126,
  'partida': 3127,
  'opciones': 3128,
  'llamaron': 3129,
  'hombro': 3130,
  'vampiros': 3131,
  'sabían': 3132,
  'recibió': 3133,
  'jugadores': 3134,
  'celebrar': 3135,
  'armario': 3136,
  'tetas': 3137,
  'paliza': 3138,
  'sabor': 3139,
  'míralo': 3140,
  'lata': 3141,
  'héroes': 3142,
  'serpiente': 3143,
  'escribí': 3144,
  'comité': 3145,
  'bendiga': 3146,
  'terminé': 3147,
  'oyó': 3148,
  'imaginación': 3149,
  'funcionó': 3150,
  'bello': 3151,
  'hígado': 3152,
  'cierre': 3153,
  'cabina': 3154,
  'queríamos': 3155,
  'pasas': 3156,
  'mantente': 3157,
  'japón': 3158,
  'deseos': 3159,
  'cuéntame': 3160,
  'rastro': 3161,
  'compró': 3162,
  'anterior': 3163,
  'acero': 3164,
  'desnudo': 3165,
  'comisario': 3166,
  'refuerzos': 3167,
  'preparar': 3168,
  'perdidos': 3169,
  'pagaré': 3170,
  'huevo': 3171,
  'entrando': 3172,
  'armada': 3173,
  'trate': 3174,
  'supo': 3175,
  'bromeas': 3176,
  'verdaderamente': 3177,
  'miro': 3178,
  'límite': 3179,
  'esperemos': 3180,
  'sacado': 3181,
  'diamante': 3182,
  'secuestro': 3183,
  'casco': 3184,
  'ayudó': 3185,
  'nene': 3186,
  'marcas': 3187,
  'pudieras': 3188,
  'poca': 3189,
  'jugo': 3190,
  'heridos': 3191,
  'congreso': 3192,
  'cementerio': 3193,
  'prostituta': 3194,
  'desaparece': 3195,
  'consecuencias': 3196,
  'terreno': 3197,
  'salvado': 3198,
  'mueren': 3199,
  'igualmente': 3200,
  'ganamos': 3201,
  'circunstancias': 3202,
  'trabajamos': 3203,
  'amén': 3204,
  'funcionando': 3205,
  'personalidad': 3206,
  'muro': 3207,
  'contestar': 3208,
  'razon': 3209,
  'peores': 3210,
  'particular': 3211,
  'llévame': 3212,
  'gimnasio': 3213,
  'emocionante': 3214,
  'desgracia': 3215,
  'científico': 3216,
  'buscado': 3217,
  'terapia': 3218,
  'prepara': 3219,
  'patrulla': 3220,
  'globo': 3221,
  'despejado': 3222,
  'creado': 3223,
  'aprendido': 3224,
  'nací': 3225,
  'embajador': 3226,
  'barcos': 3227,
  'posiblemente': 3228,
  'malvado': 3229,
  'dormitorio': 3230,
  'despacho': 3231,
  'desagradable': 3232,
  'viniendo': 3233,
  'recibo': 3234,
  'nuclear': 3235,
  'mensajes': 3236,
  'mantengan': 3237,
  'ultimo': 3238,
  'autoridades': 3239,
  'quedes': 3240,
  'pasaron': 3241,
  'gano': 3242,
  'ayudo': 3243,
  'aprecio': 3244,
  'sentimiento': 3245,
  'popular': 3246,
  'extrañas': 3247,
  'detenerlo': 3248,
  'civiles': 3249,
  'tontería': 3250,
  'reyes': 3251,
  'americana': 3252,
  'vuelven': 3253,
  'ventanas': 3254,
  'tiburón': 3255,
  'peleas': 3256,
  'organización': 3257,
  'ayudarla': 3258,
  'tuvieras': 3259,
  'querrás': 3260,
  'querrá': 3261,
  'infección': 3262,
  'decente': 3263,
  'logrado': 3264,
  'jersey': 3265,
  'amar': 3266,
  'equipos': 3267,
  'canta': 3268,
  'bebés': 3269,
  'pozo': 3270,
  'india': 3271,
  'esperado': 3272,
  'enseñar': 3273,
  'darnos': 3274,
  'tomaste': 3275,
  'internet': 3276,
  'habilidades': 3277,
  'encanto': 3278,
  'dispararon': 3279,
  'comprendes': 3280,
  'áfrica': 3281,
  'adorable': 3282,
  'rica': 3283,
  'llegaremos': 3284,
  'reales': 3285,
  'mismas': 3286,
  'medios': 3287,
  'marihuana': 3288,
  'traidor': 3289,
  'funcione': 3290,
  'clínica': 3291,
  'preferiría': 3292,
  'podían': 3293,
  'olvidó': 3294,
  'entramos': 3295,
  'ejercito': 3296,
  'división': 3297,
  'sector': 3298,
  'perdedor': 3299,
  'patrón': 3300,
  'encontrará': 3301,
  'crema': 3302,
  'planeado': 3303,
  'mantenga': 3304,
  'juguete': 3305,
  'técnica': 3306,
  'nacimiento': 3307,
  'esperaré': 3308,
  'termino': 3309,
  'matas': 3310,
  'entrado': 3311,
  'destruido': 3312,
  'daremos': 3313,
  'circo': 3314,
  'ascensor': 3315,
  'aca': 3316,
  'investigar': 3317,
  'encuentren': 3318,
  'archivo': 3319,
  'salve': 3320,
  'presentar': 3321,
  'logro': 3322,
  'intentas': 3323,
  'encargaré': 3324,
  'armado': 3325,
  'apuestas': 3326,
  'arrestado': 3327,
  'unión': 3328,
  'permanecer': 3329,
  'muéstrame': 3330,
  'móvil': 3331,
  'dificil': 3332,
  'casados': 3333,
  'cañón': 3334,
  'tribunal': 3335,
  'sepan': 3336,
  'satélite': 3337,
  'recuerden': 3338,
  'intentaré': 3339,
  'falsa': 3340,
  'coca': 3341,
  'símbolo': 3342,
  'rocas': 3343,
  'putas': 3344,
  'pendejo': 3345,
  'mátalo': 3346,
  'judío': 3347,
  'estando': 3348,
  'subtítulos': 3349,
  'gritando': 3350,
  'dejemos': 3351,
  'científicos': 3352,
  'bebiendo': 3353,
  'saque': 3354,
  'metal': 3355,
  'despedida': 3356,
  'golf': 3357,
  'empiece': 3358,
  'defender': 3359,
  'crecer': 3360,
  'rota': 3361,
  'sentimos': 3362,
  'guerreros': 3363,
  'echa': 3364,
  'comunicación': 3365,
  'probado': 3366,
  'pensarlo': 3367,
  'háblame': 3368,
  'detenido': 3369,
  'sabría': 3370,
  'muertes': 3371,
  'movimientos': 3372,
  'prohibido': 3373,
  'pájaros': 3374,
  'cargar': 3375,
  'podíamos': 3376,
  'junior': 3377,
  'imágenes': 3378,
  'haberse': 3379,
  'acusado': 3380,
  'peleando': 3381,
  'lealtad': 3382,
  'considera': 3383,
  'cazador': 3384,
  'buscarlo': 3385,
  'apaga': 3386,
  'objeto': 3387,
  'sofá': 3388,
  'personales': 3389,
  'homicidio': 3390,
  'electricidad': 3391,
  'advertencia': 3392,
  'rumores': 3393,
  'mentir': 3394,
  'fíjate': 3395,
  'deuda': 3396,
  'celoso': 3397,
  'bicicleta': 3398,
  'azules': 3399,
  'visitas': 3400,
  'mejorar': 3401,
  'escudo': 3402,
  'cueva': 3403,
  'aérea': 3404,
  'levantar': 3405,
  'juguetes': 3406,
  'ciertamente': 3407,
  'bajen': 3408,
  'temas': 3409,
  'pedirte': 3410,
  'elegante': 3411,
  'divorcio': 3412,
  'depósito': 3413,
  'concierto': 3414,
  'calla': 3415,
  'transmisión': 3416,
  'mataría': 3417,
  'flor': 3418,
  'viejas': 3419,
  'trabajan': 3420,
  'onda': 3421,
  'saldremos': 3422,
  'preguntado': 3423,
  'aliento': 3424,
  'academia': 3425,
  'sonar': 3426,
  'recibe': 3427,
  'patio': 3428,
  'función': 3429,
  'conduce': 3430,
  'pregúntale': 3431,
  'palo': 3432,
  'internacional': 3433,
  'hueso': 3434,
  'esclavos': 3435,
  'versión': 3436,
  'sacrificio': 3437,
  'judíos': 3438,
  'increible': 3439,
  'fotografía': 3440,
  'delicioso': 3441,
  'crímenes': 3442,
  'zorra': 3443,
  'valle': 3444,
  'llevaremos': 3445,
  'lentamente': 3446,
  'encantada': 3447,
  'arreglo': 3448,
  'volvería': 3449,
  'logró': 3450,
  'romántico': 3451,
  'preocuparte': 3452,
  'olvido': 3453,
  'fascinante': 3454,
  'cuadro': 3455,
  'creyó': 3456,
  'adultos': 3457,
  'wow': 3458,
  'sobrino': 3459,
  'secuencia': 3460,
  'lleven': 3461,
  'huh': 3462,
  'entradas': 3463,
  'decirnos': 3464,
  'resistencia': 3465,
  'llamarlo': 3466,
  'ayudas': 3467,
  'esperanzas': 3468,
  'disponible': 3469,
  'civil': 3470,
  'caos': 3471,
  'velas': 3472,
  'tenerlo': 3473,
  'ponerle': 3474,
  'muestras': 3475,
  'lógica': 3476,
  'casualidad': 3477,
  'sigamos': 3478,
  'rayo': 3479,
  'pato': 3480,
  'pagado': 3481,
  'máscara': 3482,
  'gustado': 3483,
  'buscarte': 3484,
  'selva': 3485,
  'pm': 3486,
  'equivoqué': 3487,
  'condenado': 3488,
  'cometido': 3489,
  'tratan': 3490,
  'pudimos': 3491,
  'preguntando': 3492,
  'tragedia': 3493,
  'poli': 3494,
  'compañías': 3495,
  'charla': 3496,
  'mete': 3497,
  'malditas': 3498,
  'campos': 3499,
  'cadáveres': 3500,
  'básicamente': 3501,
  'vamonos': 3502,
  'trucos': 3503,
  'preparada': 3504,
  'funcionará': 3505,
  'conté': 3506,
  'contando': 3507,
  'construcción': 3508,
  'ciudadanos': 3509,
  'agarrar': 3510,
  'infeliz': 3511,
  'francesa': 3512,
  'explica': 3513,
  'decide': 3514,
  'vende': 3515,
  'rutina': 3516,
  'gustar': 3517,
  'empieces': 3518,
  'caída': 3519,
  'almorzar': 3520,
  'actividad': 3521,
  'inocentes': 3522,
  'fondos': 3523,
  'coraje': 3524,
  'ayudando': 3525,
  'aterrizar': 3526,
  'actuación': 3527,
  'suba': 3528,
  'ayudante': 3529,
  'vuela': 3530,
  'vaca': 3531,
  'cirujano': 3532,
  'tio': 3533,
  'olvídate': 3534,
  'okay': 3535,
  'entregar': 3536,
  'encerrado': 3537,
  'rubia': 3538,
  'joyas': 3539,
  'empecemos': 3540,
  'amado': 3541,
  'terminamos': 3542,
  'romanos': 3543,
  'piedad': 3544,
  'llamarte': 3545,
  'hacían': 3546,
  'furia': 3547,
  'detalle': 3548,
  'admitir': 3549,
  'tendrías': 3550,
  'respiración': 3551,
  'prepárate': 3552,
  'escribiendo': 3553,
  'confundido': 3554,
  'vigilando': 3555,
  'sufrimiento': 3556,
  'saludos': 3557,
  'robaste': 3558,
  'golpear': 3559,
  'garaje': 3560,
  'buscaré': 3561,
  'revolución': 3562,
  'motel': 3563,
  'buscaba': 3564,
  'alianza': 3565,
  'estupidez': 3566,
  'disparado': 3567,
  'sangrando': 3568,
  'quietos': 3569,
  'miserable': 3570,
  'ideal': 3571,
  'empiezo': 3572,
  'controla': 3573,
  'prometiste': 3574,
  'heroína': 3575,
  'empezado': 3576,
  'dámelo': 3577,
  'vecindario': 3578,
  'empleado': 3579,
  'conociste': 3580,
  'colores': 3581,
  'cerebral': 3582,
  'plástico': 3583,
  'piano': 3584,
  'pensamiento': 3585,
  'letra': 3586,
  'empleados': 3587,
  'dosis': 3588,
  'dólar': 3589,
  'agrada': 3590,
  'tocado': 3591,
  'tíos': 3592,
  'super': 3593,
  'rifle': 3594,
  'renunciar': 3595,
  'incluyendo': 3596,
  'hoyo': 3597,
  'español': 3598,
  'acepta': 3599,
  'rueda': 3600,
  'municiones': 3601,
  'ciudades': 3602,
  'béisbol': 3603,
  'regresado': 3604,
  'pediste': 3605,
  'ja': 3606,
  'sesión': 3607,
  'comprender': 3608,
  'tocando': 3609,
  'montar': 3610,
  'familiares': 3611,
  'espía': 3612,
  'efectos': 3613,
  'salto': 3614,
  'pagó': 3615,
  'impacto': 3616,
  'emoción': 3617,
  'disparos': 3618,
  'comando': 3619,
  'autorización': 3620,
  'informes': 3621,
  'honestamente': 3622,
  'existencia': 3623,
  'demasiada': 3624,
  'coches': 3625,
  'clara': 3626,
  'alcanzar': 3627,
  'volviste': 3628,
  'octubre': 3629,
  'conseguimos': 3630,
  'barato': 3631,
  'apostar': 3632,
  'requiere': 3633,
  'importan': 3634,
  'grano': 3635,
  'demasiados': 3636,
  'casarte': 3637,
  'tráeme': 3638,
  'progreso': 3639,
  'odias': 3640,
  'ocurrir': 3641,
  'hacías': 3642,
  'serías': 3643,
  'secretario': 3644,
  'rusia': 3645,
  'piénsalo': 3646,
  'gatito': 3647,
  'déme': 3648,
  'colección': 3649,
  'vais': 3650,
  'sufrido': 3651,
  'ningun': 3652,
  'helena': 3653,
  'terror': 3654,
  'salva': 3655,
  'salgas': 3656,
  'sacarte': 3657,
  'lanza': 3658,
  'usó': 3659,
  'quince': 3660,
  'prometido': 3661,
  'preciso': 3662,
  'pirata': 3663,
  'moral': 3664,
  'moneda': 3665,
  'haberle': 3666,
  'estuvieras': 3667,
  'dijimos': 3668,
  'cayendo': 3669,
  'bajas': 3670,
  'viniera': 3671,
  'soñando': 3672,
  'servirá': 3673,
  'moscú': 3674,
  'mínimo': 3675,
  'hermosas': 3676,
  'escondido': 3677,
  'demostrar': 3678,
  'perrito': 3679,
  'intente': 3680,
  'habrán': 3681,
  'experimento': 3682,
  'espaldas': 3683,
  'diseño': 3684,
  'barra': 3685,
  'anciano': 3686,
  'alfombra': 3687,
  'actriz': 3688,
  'tono': 3689,
  'sentarse': 3690,
  'remedio': 3691,
  'listas': 3692,
  'gris': 3693,
  'enviaron': 3694,
  'entendiste': 3695,
  'disfrutar': 3696,
  'contaré': 3697,
  'comen': 3698,
  'sentada': 3699,
  'pregunte': 3700,
  'japonés': 3701,
  'gritos': 3702,
  'dolares': 3703,
  'asegurarme': 3704,
  'restos': 3705,
  'pierdo': 3706,
  'motivos': 3707,
  'dimos': 3708,
  'campeonato': 3709,
  'apúrense': 3710,
  'unico': 3711,
  'reír': 3712,
  'prima': 3713,
  'maricón': 3714,
  'actúa': 3715,
  'periodista': 3716,
  'luchando': 3717,
  'estelar': 3718,
  'escritor': 3719,
  'encontrarás': 3720,
  'dejando': 3721,
  'adecuado': 3722,
  'terrorista': 3723,
  'hierro': 3724,
  'enterrado': 3725,
  'sincero': 3726,
  'representa': 3727,
  'positivo': 3728,
  'mereces': 3729,
  'italia': 3730,
  'fea': 3731,
  'acerques': 3732,
  'petróleo': 3733,
  'encargado': 3734,
  'capítulo': 3735,
  'enseñaré': 3736,
  'cuentos': 3737,
  'breve': 3738,
  'atreves': 3739,
  'súbete': 3740,
  'sed': 3741,
  'patético': 3742,
  'mostrarte': 3743,
  'doctores': 3744,
  'carácter': 3745,
  'tuyos': 3746,
  'plaza': 3747,
  'obispo': 3748,
  'necesite': 3749,
  'muevan': 3750,
  'involucrado': 3751,
  'darán': 3752,
  'ingleses': 3753,
  'usas': 3754,
  'ruedas': 3755,
  'prácticamente': 3756,
  'orejas': 3757,
  'golpeó': 3758,
  'costado': 3759,
  'conducta': 3760,
  'toman': 3761,
  'suban': 3762,
  'médica': 3763,
  'manzana': 3764,
  'escuadrón': 3765,
  'reconozco': 3766,
  'ordenes': 3767,
  'gallina': 3768,
  'cambiando': 3769,
  'borde': 3770,
  'ataques': 3771,
  'actual': 3772,
  'pecados': 3773,
  'invito': 3774,
  'inconsciente': 3775,
  'bastardos': 3776,
  'abrió': 3777,
  'oyeron': 3778,
  'intentaba': 3779,
  'cuartel': 3780,
  'consciente': 3781,
  'tranquilízate': 3782,
  'suyos': 3783,
  'residencia': 3784,
  'máxima': 3785,
  'llevarla': 3786,
  'grasa': 3787,
  'células': 3788,
  'retrocedan': 3789,
  'recepción': 3790,
  'medicinas': 3791,
  'explotar': 3792,
  'corona': 3793,
  'callate': 3794,
  'calidad': 3795,
  'ayúdeme': 3796,
  'asesina': 3797,
  'absurdo': 3798,
  'revés': 3799,
  'producto': 3800,
  'encontrarla': 3801,
  'dj': 3802,
  'descansa': 3803,
  'copias': 3804,
  'apagar': 3805,
  'profesionales': 3806,
  'práctica': 3807,
  'investigando': 3808,
  'francamente': 3809,
  'afganistán': 3810,
  'vecino': 3811,
  'trozo': 3812,
  'tómate': 3813,
  'tercero': 3814,
  'sirven': 3815,
  'saquen': 3816,
  'manual': 3817,
  'enciende': 3818,
  'cercano': 3819,
  'bailando': 3820,
  'atras': 3821,
  'usarlo': 3822,
  'telefono': 3823,
  'registros': 3824,
  'ponlo': 3825,
  'pasta': 3826,
  'lleguen': 3827,
  'saliste': 3828,
  'sabrás': 3829,
  'limpieza': 3830,
  'estadounidense': 3831,
  'enojada': 3832,
  'desafortunadamente': 3833,
  'conoció': 3834,
  'pinta': 3835,
  'matamos': 3836,
  'funcionan': 3837,
  'cocinar': 3838,
  'cima': 3839,
  'atractivo': 3840,
  'preguntes': 3841,
  'necesitaré': 3842,
  'lleguemos': 3843,
  'interrumpir': 3844,
  'haberla': 3845,
  'apropiado': 3846,
  'presentimiento': 3847,
  'intentamos': 3848,
  'finales': 3849,
  'evitarlo': 3850,
  'enviaré': 3851,
  'deporte': 3852,
  'bajando': 3853,
  'sucia': 3854,
  'recibimos': 3855,
  'pastillas': 3856,
  'ceremonia': 3857,
  'tina': 3858,
  'muelle': 3859,
  'maletas': 3860,
  'hijas': 3861,
  'desconocido': 3862,
  'conseguiré': 3863,
  'refugio': 3864,
  'imagina': 3865,
  'franceses': 3866,
  'convertirse': 3867,
  'arruinado': 3868,
  'vomitar': 3869,
  'pasajeros': 3870,
  'normales': 3871,
  'disfraz': 3872,
  'continúe': 3873,
  'arreglado': 3874,
  'aprende': 3875,
  'venimos': 3876,
  'tenerte': 3877,
  'seguirá': 3878,
  'razonable': 3879,
  'puesta': 3880,
  'masaje': 3881,
  'lágrimas': 3882,
  'gustaria': 3883,
  'extranjero': 3884,
  'dejaremos': 3885,
  'apellido': 3886,
  'alguacil': 3887,
  'temporal': 3888,
  'rompe': 3889,
  'mayo': 3890,
  'egoísta': 3891,
  'aceite': 3892,
  'platos': 3893,
  'miércoles': 3894,
  'indios': 3895,
  'decimos': 3896,
  'bolsas': 3897,
  'anuncio': 3898,
  'tirado': 3899,
  'notado': 3900,
  'mutantes': 3901,
  'imaginas': 3902,
  'habitaciones': 3903,
  'grecia': 3904,
  'entré': 3905,
  'díselo': 3906,
  'césar': 3907,
  'comprado': 3908,
  'cocaína': 3909,
  'amarillo': 3910,
  'acabará': 3911,
  'suceda': 3912,
  'mentes': 3913,
  'maldad': 3914,
  'gustará': 3915,
  'eliminar': 3916,
  'corbata': 3917,
  'atrapados': 3918,
  'sabíamos': 3919,
  'merezco': 3920,
  'medalla': 3921,
  'lograste': 3922,
  'jaula': 3923,
  'dió': 3924,
  'chofer': 3925,
  'informado': 3926,
  'fatal': 3927,
  'enfadado': 3928,
  'cubierto': 3929,
  'compañera': 3930,
  'ronda': 3931,
  'cobra': 3932,
  'cantante': 3933,
  'abrazo': 3934,
  'tarjetas': 3935,
  'sentarme': 3936,
  'refería': 3937,
  'indica': 3938,
  'gravedad': 3939,
  'ganando': 3940,
  'estúpidos': 3941,
  'elecciones': 3942,
  'collar': 3943,
  'cincuenta': 3944,
  'programas': 3945,
  'naturalmente': 3946,
  'embajada': 3947,
  'desayunar': 3948,
  'dejaría': 3949,
  'vigila': 3950,
  'quisieras': 3951,
  'intereses': 3952,
  'agárrate': 3953,
  'aéreo': 3954,
  'traición': 3955,
  'seguiré': 3956,
  'salvajes': 3957,
  'prefiere': 3958,
  'podéis': 3959,
  'mueres': 3960,
  'molestar': 3961,
  'italiano': 3962,
  'dejarla': 3963,
  'corazones': 3964,
  'averiguarlo': 3965,
  'ave': 3966,
  'arreglarlo': 3967,
  'advierto': 3968,
  'vendiendo': 3969,
  'pública': 3970,
  'monje': 3971,
  'jugamos': 3972,
  'hablarme': 3973,
  'escoger': 3974,
  'andas': 3975,
  'ví': 3976,
  'pico': 3977,
  'llevé': 3978,
  'entro': 3979,
  'tenias': 3980,
  'priorato': 3981,
  'perdemos': 3982,
  'industria': 3983,
  'cretino': 3984,
  'cafetería': 3985,
  'cabaña': 3986,
  'aparecer': 3987,
  'tradición': 3988,
  'suelto': 3989,
  'publicidad': 3990,
  'pares': 3991,
  'oídos': 3992,
  'medida': 3993,
  'japoneses': 3994,
  'dieta': 3995,
  'capa': 3996,
  'vendrás': 3997,
  'sincronizado': 3998,
  'historial': 3999,
  'guión': 4000,
  'enano': 4001,
  'devolver': 4002,
  'solitario': 4003,
  'misiles': 4004,
  'desnuda': 4005,
  'vaquero': 4006,
  'únicos': 4007,
  'trasera': 4008,
  'sano': 4009,
  'piezas': 4010,
  'liga': 4011,
  'gerente': 4012,
  'caridad': 4013,
  'armados': 4014,
  'trajeron': 4015,
  'religión': 4016,
  'fotografías': 4017,
  'sentirse': 4018,
  'rezar': 4019,
  'protege': 4020,
  'preparen': 4021,
  'niñera': 4022,
  'naranja': 4023,
  'matarnos': 4024,
  'jugada': 4025,
  'espérame': 4026,
  'enseñarte': 4027,
  'cohete': 4028,
  'tanques': 4029,
  'sombras': 4030,
  'moverse': 4031,
  'llegara': 4032,
  'cortó': 4033,
  'cierren': 4034,
  'volverán': 4035,
  'usamos': 4036,
  'sabremos': 4037,
  'maletín': 4038,
  'lenguaje': 4039,
  'invitación': 4040,
  'cambian': 4041,
  'rehenes': 4042,
  'encontremos': 4043,
  'descubrí': 4044,
  'audiencia': 4045,
  'agenda': 4046,
  'sígueme': 4047,
  'sensible': 4048,
  'reverendo': 4049,
  'queréis': 4050,
  'cazar': 4051,
  'bodas': 4052,
  'seguiremos': 4053,
  'rosas': 4054,
  'retiro': 4055,
  'primeras': 4056,
  'poderosa': 4057,
  'deberia': 4058,
  'cantando': 4059,
  'antiguos': 4060,
  'teme': 4061,
  'síganme': 4062,
  'moviendo': 4063,
  'juventud': 4064,
  'equivocas': 4065,
  'trabajadores': 4066,
  'seguí': 4067,
  'salvaste': 4068,
  'pulmones': 4069,
  'payaso': 4070,
  'oírlo': 4071,
  'fama': 4072,
  'engaño': 4073,
  'distrito': 4074,
  'disculparme': 4075,
  'ciudadano': 4076,
  'robando': 4077,
  'equipaje': 4078,
  'ciencias': 4079,
  'ubicación': 4080,
  'federales': 4081,
  'tratamos': 4082,
  'ofrece': 4083,
  'junio': 4084,
  'extremadamente': 4085,
  'actuando': 4086,
  'traducción': 4087,
  'supiste': 4088,
  'sacarlo': 4089,
  'preparando': 4090,
  'letras': 4091,
  'típico': 4092,
  'reserva': 4093,
  'propuesta': 4094,
  'plataforma': 4095,
  'mago': 4096,
  'linea': 4097,
  'dude': 4098,
  'busque': 4099,
  'tabaco': 4100,
  'principios': 4101,
  'dormida': 4102,
  'costó': 4103,
  'causar': 4104,
  'temer': 4105,
  'subiendo': 4106,
  'rollo': 4107,
  'hechizo': 4108,
  'gusano': 4109,
  'guau': 4110,
  'grupos': 4111,
  'discos': 4112,
  'despierte': 4113,
  'ocurriendo': 4114,
  'horario': 4115,
  'expedientes': 4116,
  'envié': 4117,
  'dibujos': 4118,
  'concurso': 4119,
  'autopista': 4120,
  'atacó': 4121,
  'vendedor': 4122,
  'sigas': 4123,
  'rebeldes': 4124,
  'radar': 4125,
  'expresión': 4126,
  'escuchan': 4127,
  'use': 4128,
  'resultó': 4129,
  'principe': 4130,
  'hágalo': 4131,
  'exposición': 4132,
  'demasiadas': 4133,
  'cuidando': 4134,
  'aterrizaje': 4135,
  'apetece': 4136,
  'tomaron': 4137,
  'sorprendente': 4138,
  'responda': 4139,
  'olvidaste': 4140,
  'lobos': 4141,
  'echado': 4142,
  'deberias': 4143,
  'cortado': 4144,
  'citas': 4145,
  'abiertos': 4146,
  'zapato': 4147,
  'preso': 4148,
  'lastimar': 4149,
  'explosivos': 4150,
  'colegas': 4151,
  'supieras': 4152,
  'secretaria': 4153,
  'queridos': 4154,
  'morirás': 4155,
  'cuero': 4156,
  'confesión': 4157,
  'círculo': 4158,
  'sabéis': 4159,
  'química': 4160,
  'obras': 4161,
  'llores': 4162,
  'juegas': 4163,
  'invisible': 4164,
  'fase': 4165,
  'esconde': 4166,
  'capitan': 4167,
  'adulto': 4168,
  'vacía': 4169,
  'siéntense': 4170,
  'pastor': 4171,
  'llegada': 4172,
  'gatillo': 4173,
  'debamos': 4174,
  'teléfonos': 4175,
  'saqué': 4176,
  'regresará': 4177,
  'olvidaré': 4178,
  'límites': 4179,
  'jovencita': 4180,
  'dibujo': 4181,
  'británico': 4182,
  'balboa': 4183,
  'psiquiatra': 4184,
  'objetos': 4185,
  'negociar': 4186,
  'formar': 4187,
  'fiel': 4188,
  'diosa': 4189,
  'descubrió': 4190,
  'ala': 4191,
  'abril': 4192,
  'saldré': 4193,
  'mías': 4194,
  'disfruta': 4195,
  'atender': 4196,
  'tomaremos': 4197,
  'oreja': 4198,
  'necesitaremos': 4199,
  'motores': 4200,
  'intenciones': 4201,
  'cumplido': 4202,
  'brindis': 4203,
  'villa': 4204,
  'trono': 4205,
  'traen': 4206,
  'sentados': 4207,
  'samurai': 4208,
  'salía': 4209,
  'raras': 4210,
  'producción': 4211,
  'medias': 4212,
  'lavar': 4213,
  'jazz': 4214,
  'extraordinario': 4215,
  'causado': 4216,
  'serpientes': 4217,
  'revisa': 4218,
  'quedate': 4219,
  'nervios': 4220,
  'estable': 4221,
  'cerdos': 4222,
  'riesgos': 4223,
  'pasaste': 4224,
  'madres': 4225,
  'instinto': 4226,
  'episodio': 4227,
  'empezaron': 4228,
  'aniversario': 4229,
  'viera': 4230,
  'viajes': 4231,
  'trabajé': 4232,
  'rango': 4233,
  'preocupación': 4234,
  'métete': 4235,
  'imaginé': 4236,
  'hayamos': 4237,
  'fenómeno': 4238,
  'dolores': 4239,
  'demanda': 4240,
  'creó': 4241,
  'asegurar': 4242,
  'pesadillas': 4243,
  'niega': 4244,
  'molestia': 4245,
  'extremo': 4246,
  'debilidad': 4247,
  'compasión': 4248,
  'camiones': 4249,
  'aproximadamente': 4250,
  'afecta': 4251,
  'administración': 4252,
  'reconocimiento': 4253,
  'recientemente': 4254,
  'practicar': 4255,
  'pierdas': 4256,
  'pandilla': 4257,
  'miss': 4258,
  'mates': 4259,
  'disparando': 4260,
  'británicos': 4261,
  'ayudarlos': 4262,
  'sueldo': 4263,
  'siguió': 4264,
  'significado': 4265,
  'miente': 4266,
  'medidas': 4267,
  'guitarra': 4268,
  'custodia': 4269,
  'boletos': 4270,
  'ayudado': 4271,
  'sentarte': 4272,
  'risa': 4273,
  'quemar': 4274,
  'prometió': 4275,
  'observando': 4276,
  'lectura': 4277,
  'jódete': 4278,
  'considerado': 4279,
  'cometer': 4280,
  'cambié': 4281,
  'asientos': 4282,
  'satisfecho': 4283,
  'saludable': 4284,
  'lanzar': 4285,
  'hoja': 4286,
  'conozca': 4287,
  'concepto': 4288,
  'colgar': 4289,
  'apunta': 4290,
  'renta': 4291,
  'prefieres': 4292,
  'pediré': 4293,
  'mediodía': 4294,
  'influencia': 4295,
  'hacernos': 4296,
  'figura': 4297,
  'ensayo': 4298,
  'coordenadas': 4299,
  'casó': 4300,
  'político': 4301,
  'países': 4302,
  'once': 4303,
  'matarlos': 4304,
  'graduación': 4305,
  'frase': 4306,
  'eterna': 4307,
  'estarlo': 4308,
  'descuida': 4309,
  'toalla': 4310,
  'próximos': 4311,
  'monedas': 4312,
  'mirad': 4313,
  'miraba': 4314,
  'griegos': 4315,
  'famosa': 4316,
  'corredor': 4317,
  'atravesar': 4318,
  'aguas': 4319,
  'modales': 4320,
  'guerras': 4321,
  'eternidad': 4322,
  'conocerla': 4323,
  'busquen': 4324,
  'acuerda': 4325,
  'sentirme': 4326,
  'quema': 4327,
  'poema': 4328,
  'ordenó': 4329,
  'espiritual': 4330,
  'costumbre': 4331,
  'comencé': 4332,
  'cabra': 4333,
  'alquiler': 4334,
  'vencido': 4335,
  'vela': 4336,
  'trataré': 4337,
  'sospechosos': 4338,
  'quinto': 4339,
  'pilotos': 4340,
  'perdieron': 4341,
  'generación': 4342,
  'formación': 4343,
  'estructura': 4344,
  'concéntrate': 4345,
  'complejo': 4346,
  'voces': 4347,
  'tragos': 4348,
  'temor': 4349,
  'república': 4350,
  'recursos': 4351,
  'presentación': 4352,
  'pirámide': 4353,
  'oficialmente': 4354,
  'maquillaje': 4355,
  'guardaespaldas': 4356,
  'escucharme': 4357,
  'dejara': 4358,
  'contiene': 4359,
  'bendición': 4360,
  'vuestros': 4361,
  'semejante': 4362,
  'pague': 4363,
  'molestes': 4364,
  'cultura': 4365,
  'comió': 4366,
  'antecedentes': 4367,
  'tendríamos': 4368,
  'representante': 4369,
  'prestado': 4370,
  'posesión': 4371,
  'oir': 4372,
  'nubes': 4373,
  'meterte': 4374,
  'guantes': 4375,
  'esperabas': 4376,
  'enseña': 4377,
  'encontrarme': 4378,
  'consejos': 4379,
  'regreses': 4380,
  'preocupen': 4381,
  'pistolas': 4382,
  'gane': 4383,
  'sos': 4384,
  'niveles': 4385,
  'ganaste': 4386,
  'considerando': 4387,
  'capital': 4388,
  'cambie': 4389,
  'ánimo': 4390,
  'utilizar': 4391,
  'lograrlo': 4392,
  'llévate': 4393,
  'gafas': 4394,
  'espíritus': 4395,
  'preguntaste': 4396,
  'pagará': 4397,
  'maíz': 4398,
  'estacionamiento': 4399,
  'contactos': 4400,
  'aguja': 4401,
  'trenes': 4402,
  'soporto': 4403,
  'sacaré': 4404,
  'oido': 4405,
  'fortaleza': 4406,
  'corea': 4407,
  'conversar': 4408,
  'computadoras': 4409,
  'cálmese': 4410,
  'volante': 4411,
  'sucederá': 4412,
  'decías': 4413,
  'data': 4414,
  'cuente': 4415,
  'civilización': 4416,
  'cabrones': 4417,
  'arruinar': 4418,
  'vampiro': 4419,
  'tocó': 4420,
  'regresé': 4421,
  'pases': 4422,
  'fantástica': 4423,
  'facil': 4424,
  'estabamos': 4425,
  'diera': 4426,
  'detenga': 4427,
  'violación': 4428,
  'hemorragia': 4429,
  'fianza': 4430,
  'docena': 4431,
  'centavo': 4432,
  'célula': 4433,
  'carreras': 4434,
  'callado': 4435,
  'rodilla': 4436,
  'prometes': 4437,
  'profundamente': 4438,
  'posiciones': 4439,
  'hicieras': 4440,
  'furioso': 4441,
  'desperté': 4442,
  'comes': 4443,
  'ciega': 4444,
  'cicatriz': 4445,
  'bebidas': 4446,
  'acércate': 4447,
  'tiró': 4448,
  'policial': 4449,
  'pasear': 4450,
  'masa': 4451,
  'llegues': 4452,
  'global': 4453,
  'escapado': 4454,
  'dinos': 4455,
  'cuarta': 4456,
  'bienes': 4457,
  'aparato': 4458,
  'sostén': 4459,
  'realizar': 4460,
  'permitiré': 4461,
  'orgullosos': 4462,
  'corten': 4463,
  'comisión': 4464,
  'vidrio': 4465,
  'presa': 4466,
  'ocupados': 4467,
  'morfina': 4468,
  'habrían': 4469,
  'columna': 4470,
  'sacaron': 4471,
  'patas': 4472,
  'oírme': 4473,
  'ocultar': 4474,
  'objeción': 4475,
  'maneja': 4476,
  'largas': 4477,
  'generalmente': 4478,
  'fuentes': 4479,
  'cubrir': 4480,
  'ataca': 4481,
  'tenis': 4482,
  'santos': 4483,
  'potencial': 4484,
  'plano': 4485,
  'levante': 4486,
  'escala': 4487,
  'enfrentar': 4488,
  'cuánta': 4489,
  'sobra': 4490,
  'rumor': 4491,
  'páginas': 4492,
  'órganos': 4493,
  'león': 4494,
  'entras': 4495,
  'decían': 4496,
  'chinos': 4497,
  'brujas': 4498,
  'barbara': 4499,
  'admiro': 4500,
  'voto': 4501,
  'volvieron': 4502,
  'suelte': 4503,
  'reto': 4504,
  'metes': 4505,
  'infarto': 4506,
  'cuyo': 4507,
  'construyó': 4508,
  'boleto': 4509,
  'besar': 4510,
  'portal': 4511,
  'ponemos': 4512,
  'monte': 4513,
  'discusión': 4514,
  'digno': 4515,
  'desean': 4516,
  'darse': 4517,
  'considerar': 4518,
  'bancos': 4519,
  'poción': 4520,
  'perdóneme': 4521,
  'pensabas': 4522,
  'generoso': 4523,
  'estatua': 4524,
  'convencer': 4525,
  'conocida': 4526,
  'comenzamos': 4527,
  'aceptado': 4528,
  'rob': 4529,
  'importar': 4530,
  'fracaso': 4531,
  'ciertos': 4532,
  'bomberos': 4533,
  'sorprendido': 4534,
  'protegido': 4535,
  'presenta': 4536,
  'metí': 4537,
  'entenderlo': 4538,
  'detengan': 4539,
  'conflicto': 4540,
  'códigos': 4541,
  'seco': 4542,
  'sabiendo': 4543,
  'hacerla': 4544,
  'graves': 4545,
  'dejarás': 4546,
  'convencido': 4547,
  'comprende': 4548,
  'bromas': 4549,
  'oyen': 4550,
  'moleste': 4551,
  'mencionar': 4552,
  'gastos': 4553,
  'estaríamos': 4554,
  'esperaremos': 4555,
  'encontrarán': 4556,
  'billetes': 4557,
  'arco': 4558,
  'altos': 4559,
  'actores': 4560,
  'talvez': 4561,
  'sitios': 4562,
  'saluda': 4563,
  'quejas': 4564,
  'planeando': 4565,
  'moriremos': 4566,
  'marica': 4567,
  'habrías': 4568,
  'estarían': 4569,
  'dejarán': 4570,
  'amantes': 4571,
  'acostumbrado': 4572,
  'torneo': 4573,
  'planos': 4574,
  'ordeno': 4575,
  'miré': 4576,
  'maldicion': 4577,
  'kg': 4578,
  'dirías': 4579,
  'confirmado': 4580,
  'vacas': 4581,
  'quirófano': 4582,
  'ponerlo': 4583,
  'origen': 4584,
  'fusión': 4585,
  'deberá': 4586,
  'verdaderos': 4587,
  'serían': 4588,
  'seguía': 4589,
  'sabrán': 4590,
  'pasaje': 4591,
  'necesidades': 4592,
  'lavado': 4593,
  'galaxia': 4594,
  'fresca': 4595,
  'doloroso': 4596,
  'comenzado': 4597,
  'uñas': 4598,
  'trajes': 4599,
  'perfil': 4600,
  'necesitará': 4601,
  'manejando': 4602,
  'lola': 4603,
  'lentes': 4604,
  'engañar': 4605,
  'cállense': 4606,
  'bate': 4607,
  'automático': 4608,
  'tuyas': 4609,
  'quedarás': 4610,
  'profunda': 4611,
  'piensen': 4612,
  'permiten': 4613,
  'heredero': 4614,
  'entraron': 4615,
  'canadá': 4616,
  'caminos': 4617,
  'sintió': 4618,
  'robé': 4619,
  'porno': 4620,
  'lejano': 4621,
  'intercambio': 4622,
  'hojas': 4623,
  'dispare': 4624,
  'cuan': 4625,
  'champaña': 4626,
  'atractiva': 4627,
  'terminal': 4628,
  'rita': 4629,
  'poesía': 4630,
  'pierda': 4631,
  'expertos': 4632,
  'ensalada': 4633,
  'convertir': 4634,
  'calientes': 4635,
  'autopsia': 4636,
  'tristeza': 4637,
  'tiendas': 4638,
  'mafia': 4639,
  'jodas': 4640,
  'escuchó': 4641,
  'enormes': 4642,
  'denme': 4643,
  'atacaron': 4644,
  'almacén': 4645,
  'sufrió': 4646,
  'productos': 4647,
  'productor': 4648,
  'permitido': 4649,
  'pegó': 4650,
  'matarla': 4651,
  'interno': 4652,
  'interna': 4653,
  'infantil': 4654,
  'españa': 4655,
  'diarios': 4656,
  'combinación': 4657,
  'acompañe': 4658,
  'ropas': 4659,
  'pondremos': 4660,
  'metiste': 4661,
  'esencia': 4662,
  'brillantes': 4663,
  'bingo': 4664,
  'sordo': 4665,
  'sacas': 4666,
  'rancho': 4667,
  'objetivos': 4668,
  'muebles': 4669,
  'mencionó': 4670,
  'estupido': 4671,
  'enterado': 4672,
  'documento': 4673,
  'vencer': 4674,
  'sintiendo': 4675,
  'quieta': 4676,
  'período': 4677,
  'oportunidades': 4678,
  'monos': 4679,
  'mercancía': 4680,
  'lastimado': 4681,
  'internos': 4682,
  'hueco': 4683,
  'golpea': 4684,
  'evento': 4685,
  'estudiando': 4686,
  'elige': 4687,
  'crecido': 4688,
  'conspiración': 4689,
  'avenida': 4690,
  'tremendo': 4691,
  'tocas': 4692,
  'sabias': 4693,
  'raya': 4694,
  'líos': 4695,
  'invitar': 4696,
  'homicidios': 4697,
  'herramientas': 4698,
  'flecha': 4699,
  'derrota': 4700,
  'cervezas': 4701,
  'terminará': 4702,
  'llenar': 4703,
  'líderes': 4704,
  'ganan': 4705,
  'fingir': 4706,
  'estrategia': 4707,
  'dirán': 4708,
  'confías': 4709,
  'cómoda': 4710,
  'comience': 4711,
  'cenizas': 4712,
  'capaces': 4713,
  'temía': 4714,
  'sonríe': 4715,
  'quite': 4716,
  'necesites': 4717,
  'mostrarle': 4718,
  'mordió': 4719,
  'jefa': 4720,
  'fallo': 4721,
  'empiezan': 4722,
  'dispositivo': 4723,
  'detesto': 4724,
  'aprendiste': 4725,
  'aman': 4726,
  'agradecido': 4727,
  'agáchate': 4728,
  'terminaste': 4729,
  'sida': 4730,
  'mueras': 4731,
  'mentí': 4732,
  'lujo': 4733,
  'láser': 4734,
  'importe': 4735,
  'físico': 4736,
  'caes': 4737,
  'tiros': 4738,
  'miran': 4739,
  'hadas': 4740,
  'ganancias': 4741,
  'fritas': 4742,
  'fantasía': 4743,
  'deténgase': 4744,
  'déjenlo': 4745,
  'curar': 4746,
  'comidas': 4747,
  'quisiste': 4748,
  'pacífico': 4749,
  'oler': 4750,
  'ofreció': 4751,
  'importaba': 4752,
  'certeza': 4753,
  'actividades': 4754,
  'vestidos': 4755,
  'suele': 4756,
  'submarino': 4757,
  'podrias': 4758,
  'pesa': 4759,
  'judas': 4760,
  'idioma': 4761,
  'identificar': 4762,
  'hubiéramos': 4763,
  'cañones': 4764,
  'beneficios': 4765,
  'sospecha': 4766,
  'observación': 4767,
  'muchachas': 4768,
  'manejo': 4769,
  'jodiendo': 4770,
  'explicarlo': 4771,
  'divino': 4772,
  'divertida': 4773,
  'despegue': 4774,
  'contrató': 4775,
  'volumen': 4776,
  'trabajó': 4777,
  'términos': 4778,
  'morirán': 4779,
  'hombros': 4780,
  'hada': 4781,
  'alegre': 4782,
  'terribles': 4783,
  'temes': 4784,
  'sabiduría': 4785,
  'prioridad': 4786,
  'pluma': 4787,
  'misil': 4788,
  'mansión': 4789,
  'detenerme': 4790,
  'despertó': 4791,
  'decírmelo': 4792,
  'cambiará': 4793,
  'arranca': 4794,
  'alejado': 4795,
  'soltero': 4796,
  'pierden': 4797,
  'mascota': 4798,
  'empuja': 4799,
  'dentista': 4800,
  'cogido': 4801,
  'batería': 4802,
  'visual': 4803,
  'reconoce': 4804,
  'pedimos': 4805,
  'niebla': 4806,
  'métodos': 4807,
  'durmió': 4808,
  'desfile': 4809,
  'creciendo': 4810,
  'continua': 4811,
  'borracha': 4812,
  'beca': 4813,
  'adolescente': 4814,
  'actos': 4815,
  'mágica': 4816,
  'imbéciles': 4817,
  'huyendo': 4818,
  'fotógrafo': 4819,
  'experimentos': 4820,
  'edificios': 4821,
  'deténganse': 4822,
  'cálmense': 4823,
  'atmósfera': 4824,
  'acabaron': 4825,
  'votos': 4826,
  'tareas': 4827,
  'presupuesto': 4828,
  'persecución': 4829,
  'enseñarle': 4830,
  'enfermos': 4831,
  'cortes': 4832,
  'contaste': 4833,
  'recuerdan': 4834,
  'probabilidades': 4835,
  'núcleo': 4836,
  'multitud': 4837,
  'moriré': 4838,
  'empresas': 4839,
  'delta': 4840,
  'asociación': 4841,
  'verdes': 4842,
  'profesión': 4843,
  'pelotón': 4844,
  'paro': 4845,
  'largos': 4846,
  'danos': 4847,
  'creído': 4848,
  'cosecha': 4849,
  'cintas': 4850,
  'agallas': 4851,
  'vendido': 4852,
  'tortura': 4853,
  'suéltala': 4854,
  'sexuales': 4855,
  'retirado': 4856,
  'reconoces': 4857,
  'pudieran': 4858,
  'desorden': 4859,
  'desarrollo': 4860,
  'abandonó': 4861,
  'vodka': 4862,
  'violento': 4863,
  'vestida': 4864,
  'probarlo': 4865,
  'balón': 4866,
  'población': 4867,
  'pechos': 4868,
  'ocurrirá': 4869,
  'mientes': 4870,
  'leal': 4871,
  'jo': 4872,
  'federación': 4873,
  'dí': 4874,
  'asustaste': 4875,
  'apártate': 4876,
  'vinieras': 4877,
  'valioso': 4878,
  'profesora': 4879,
  'misericordia': 4880,
  'mancha': 4881,
  'ilusión': 4882,
  'habrás': 4883,
  'golpeado': 4884,
  'generador': 4885,
  'empiezas': 4886,
  'detuvo': 4887,
  'despedir': 4888,
  'cuerdas': 4889,
  'cráneo': 4890,
  'champán': 4891,
  'cartel': 4892,
  'campana': 4893,
  'vendió': 4894,
  'rompí': 4895,
  'revistas': 4896,
  'ordena': 4897,
  'cállese': 4898,
  'avanzar': 4899,
  'técnicamente': 4900,
  'remoto': 4901,
  'pudiéramos': 4902,
  'pega': 4903,
  'parto': 4904,
  'nido': 4905,
  'molestando': 4906,
  'gota': 4907,
  'fracasado': 4908,
  'esperé': 4909,
  'escúcheme': 4910,
  'eco': 4911,
  'duros': 4912,
  'dispararle': 4913,
  'desaparecieron': 4914,
  'contarle': 4915,
  'cargando': 4916,
  'caen': 4917,
  'asesinó': 4918,
  'prométeme': 4919,
  'políticos': 4920,
  'plantas': 4921,
  'oculta': 4922,
  'mundos': 4923,
  'fines': 4924,
  'dueños': 4925,
  'detén': 4926,
  'cm': 4927,
  'asesinar': 4928,
  'agosto': 4929,
  'rojas': 4930,
  'pantano': 4931,
  'opina': 4932,
  'odian': 4933,
  'llegaré': 4934,
  'impresionado': 4935,
  'dirigir': 4936,
  'diciembre': 4937,
  'decidimos': 4938,
  'conectado': 4939,
  'toco': 4940,
  'sinceramente': 4941,
  'relacionado': 4942,
  'quedaron': 4943,
  'levanten': 4944,
  'espantoso': 4945,
  'enseño': 4946,
  'enfermedades': 4947,
  'divirtiendo': 4948,
  'cubre': 4949,
  'confidencial': 4950,
  'compre': 4951,
  'baúl': 4952,
  'baila': 4953,
  'aumento': 4954,
  'ascenso': 4955,
  'testimonio': 4956,
  'saliera': 4957,
  'resiste': 4958,
  'párate': 4959,
  'ofrecer': 4960,
  'ocupo': 4961,
  'merecía': 4962,
  'jabón': 4963,
  'hamburguesa': 4964,
  'escuelas': 4965,
  'equivoco': 4966,
  'caiga': 4967,
  'beneficio': 4968,
  'suéltalo': 4969,
  'rusa': 4970,
  'receta': 4971,
  'modelos': 4972,
  'misterioso': 4973,
  'esconder': 4974,
  'eche': 4975,
  'contarte': 4976,
  'callejón': 4977,
  'buscarla': 4978,
  'bonitas': 4979,
  'toquen': 4980,
  'rudo': 4981,
  'pasaré': 4982,
  'extrañé': 4983,
  'detras': 4984,
  'detenerlos': 4985,
  'delito': 4986,
  'atacado': 4987,
  'traerlo': 4988,
  'sentencia': 4989,
  'sácame': 4990,
  'posibles': 4991,
  'participar': 4992,
  'pagas': 4993,
  'mintió': 4994,
  'jefes': 4995,
  'excepción': 4996,
  'espadas': 4997,
  'engañado': 4998,
  'débiles': 4999,
  'corporación': 5000,
  'cerrados': 5001,
  'carajos': 5002,
  'vería': 5003,
  'seca': 5004,
  'recibiendo': 5005,
  'pescar': 5006,
  'olvidas': 5007,
  'naciones': 5008,
  'llevara': 5009,
  'graciosa': 5010,
  'emergencias': 5011,
  'conviene': 5012,
  'comunicaciones': 5013,
  'caí': 5014,
  'ocuparé': 5015,
  'niñita': 5016,
  'hazte': 5017,
  'estupenda': 5018,
  'disciplina': 5019,
  'case': 5020,
  'arriesgado': 5021,
  'arquitecto': 5022,
  'vaticano': 5023,
  'traerme': 5024,
  'tiroteo': 5025,
  'sirvo': 5026,
  'sentirás': 5027,
  'ratón': 5028,
  'quitarme': 5029,
  'quedaba': 5030,
  'preguntan': 5031,
  'permíteme': 5032,
  'masacre': 5033,
  'infancia': 5034,
  'indio': 5035,
  'homosexual': 5036,
  'chance': 5037,
  'barba': 5038,
  'artes': 5039,
  'acerque': 5040,
  'susto': 5041,
  'síntomas': 5042,
  'salvarte': 5043,
  'sabio': 5044,
  'romance': 5045,
  'petición': 5046,
  'peligrosos': 5047,
  'orinar': 5048,
  'macho': 5049,
  'llevarse': 5050,
  'lindos': 5051,
  'protegerte': 5052,
  'profundidad': 5053,
  'pides': 5054,
  'permanente': 5055,
  'investigaciones': 5056,
  'cruzando': 5057,
  'comenzando': 5058,
  'atrapada': 5059,
  'trampas': 5060,
  'tomemos': 5061,
  'tímido': 5062,
  'ola': 5063,
  'narices': 5064,
  'diana': 5065,
  'presentarles': 5066,
  'pasaremos': 5067,
  'observa': 5068,
  'nacer': 5069,
  'mona': 5070,
  'mm': 5071,
  'increíblemente': 5072,
  'despegar': 5073,
  'comentario': 5074,
  'sufre': 5075,
  'ramera': 5076,
  'perfume': 5077,
  'oscura': 5078,
  'olas': 5079,
  'obligado': 5080,
  'noviembre': 5081,
  'logré': 5082,
  'llevaría': 5083,
  'fallado': 5084,
  'escocia': 5085,
  'destruyó': 5086,
  'conduciendo': 5087,
  'camiseta': 5088,
  'venas': 5089,
  'riendo': 5090,
  'resistir': 5091,
  'pavo': 5092,
  'pagué': 5093,
  'nieto': 5094,
  'liberar': 5095,
  'hogares': 5096,
  'denle': 5097,
  'barón': 5098,
  'aguanto': 5099,
  'vicepresidente': 5100,
  'simón': 5101,
  'querrías': 5102,
  'quedara': 5103,
  'ordenador': 5104,
  'jodidos': 5105,
  'enfermeras': 5106,
  'despertado': 5107,
  'comentarios': 5108,
  'alumnos': 5109,
  'aburrida': 5110,
  'superar': 5111,
  'norma': 5112,
  'musical': 5113,
  'lápiz': 5114,
  'jodan': 5115,
  'incluye': 5116,
  'fruta': 5117,
  'entran': 5118,
  'eléctrica': 5119,
  'crítica': 5120,
  'comí': 5121,
  'chip': 5122,
  'cargado': 5123,
  'altas': 5124,
  'venden': 5125,
  'superiores': 5126,
  'sufriendo': 5127,
  'sacamos': 5128,
  'invitó': 5129,
  'huelo': 5130,
  'harían': 5131,
  'gastar': 5132,
  'escapa': 5133,
  'confirmar': 5134,
  'quitado': 5135,
  'probando': 5136,
  'perímetro': 5137,
  'pegado': 5138,
  'olviden': 5139,
  'mentirosa': 5140,
  'incapaz': 5141,
  'forense': 5142,
  'ésas': 5143,
  'dignidad': 5144,
  'detectives': 5145,
  'deportes': 5146,
  'delicado': 5147,
  'cuarenta': 5148,
  'constante': 5149,
  'bebes': 5150,
  'sesos': 5151,
  'seriamente': 5152,
  'regresemos': 5153,
  'protegiendo': 5154,
  'muralla': 5155,
  'meterse': 5156,
  'lleves': 5157,
  'inténtalo': 5158,
  'huye': 5159,
  'embarazo': 5160,
  'debimos': 5161,
  'cuartos': 5162,
  'copas': 5163,
  'rehén': 5164,
  'pisos': 5165,
  'persiguiendo': 5166,
  'milagros': 5167,
  'horror': 5168,
  'emocional': 5169,
  'comedor': 5170,
  'busqué': 5171,
  'buscarme': 5172,
  'tumbas': 5173,
  'subtitulado': 5174,
  'plazo': 5175,
  'pesca': 5176,
  'muertas': 5177,
  'mantenerlo': 5178,
  'llamaremos': 5179,
  'litros': 5180,
  'horribles': 5181,
  'deudas': 5182,
  'detiene': 5183,
  'compraste': 5184,
  'comisaría': 5185,
  'verlas': 5186,
  'vé': 5187,
  'vago': 5188,
  'unica': 5189,
  'radiación': 5190,
  'múltiples': 5191,
  'muchísimas': 5192,
  'equivoca': 5193,
  'cuya': 5194,
  'cortinas': 5195,
  'tenerla': 5196,
  'tapa': 5197,
  'sorpresas': 5198,
  'respiro': 5199,
  'queja': 5200,
  'preguntarme': 5201,
  'oyendo': 5202,
  'mordida': 5203,
  'mmm': 5204,
  'miró': 5205,
  'marchar': 5206,
  'mantequilla': 5207,
  'guardián': 5208,
  'escándalo': 5209,
  'causó': 5210,
  'aléjense': 5211,
  'acercando': 5212,
  'vió': 5213,
  'tinta': 5214,
  'sindicato': 5215,
  'reuniones': 5216,
  'pida': 5217,
  'personajes': 5218,
  'lees': 5219,
  'dispuesta': 5220,
  'descripción': 5221,
  'calzones': 5222,
  'brigada': 5223,
  'ayudaría': 5224,
  'repugnante': 5225,
  'odiaba': 5226,
  'misa': 5227,
  'metrópolis': 5228,
  'meterme': 5229,
  'mamita': 5230,
  'jamón': 5231,
  'ésos': 5232,
  'cazadores': 5233,
  'usarla': 5234,
  'retraso': 5235,
  'responsabilidades': 5236,
  'preocupas': 5237,
  'pagando': 5238,
  'linterna': 5239,
  'inevitable': 5240,
  'encender': 5241,
  'distinta': 5242,
  'compro': 5243,
  'cacería': 5244,
  'atacan': 5245,
  'usaba': 5246,
  'parientes': 5247,
  'menores': 5248,
  'jungla': 5249,
  'dedicado': 5250,
  'contentos': 5251,
  'compraré': 5252,
  'astuto': 5253,
  'antídoto': 5254,
  'vivas': 5255,
  'vano': 5256,
  'sajones': 5257,
  'raros': 5258,
  'quemado': 5259,
  'promesas': 5260,
  'parejas': 5261,
  'olvidarlo': 5262,
  'locales': 5263,
  'fuga': 5264,
  'estaciones': 5265,
  'dispares': 5266,
  'despidieron': 5267,
  'asesinada': 5268,
  'artillería': 5269,
  'apariencia': 5270,
  'advertí': 5271,
  'sacarme': 5272,
  'pueblos': 5273,
  'mágico': 5274,
  'garantizo': 5275,
  'excusas': 5276,
  'contratar': 5277,
  'candidato': 5278,
  'anteriormente': 5279,
  'adicto': 5280,
  'sello': 5281,
  'salvarlo': 5282,
  'retrasado': 5283,
  'placas': 5284,
  'hermosos': 5285,
  'galleta': 5286,
  'escucharon': 5287,
  'chistoso': 5288,
  'anillos': 5289,
  'subió': 5290,
  'solar': 5291,
  'píldoras': 5292,
  'pásame': 5293,
  'juegues': 5294,
  'grito': 5295,
  'enseñado': 5296,
  'encontrarte': 5297,
  'descubrimiento': 5298,
  'consigues': 5299,
  'clérigo': 5300,
  'clásico': 5301,
  'batallas': 5302,
  'uses': 5303,
  'trates': 5304,
  'marzo': 5305,
  'hechas': 5306,
  'fraude': 5307,
  'economía': 5308,
  'disfrute': 5309,
  'creerme': 5310,
  'costo': 5311,
  'consiga': 5312,
  'condena': 5313,
  'bájate': 5314,
  'alivio': 5315,
  'traerá': 5316,
  'quedaremos': 5317,
  'micrófono': 5318,
  'matemáticas': 5319,
  'interesan': 5320,
  'ida': 5321,
  'hueles': 5322,
  'giro': 5323,
  'desgraciados': 5324,
  'arroz': 5325,
  'andan': 5326,
  'unido': 5327,
  'traseros': 5328,
  'torres': 5329,
  'seda': 5330,
  'engañando': 5331,
  'encuentres': 5332,
  'controles': 5333,
  'choque': 5334,
  'celos': 5335,
  'aclarar': 5336,
  'sentiría': 5337,
  'santuario': 5338,
  'queden': 5339,
  'piratas': 5340,
  'impulso': 5341,
  'impedir': 5342,
  'grita': 5343,
  'envíe': 5344,
  'entraste': 5345,
  'debieron': 5346,
  'creías': 5347,
  'cobrar': 5348,
  'audición': 5349,
  'asesinados': 5350,
  'afueras': 5351,
  'tribu': 5352,
  'testamento': 5353,
  'postre': 5354,
  'paró': 5355,
  'ópera': 5356,
  'falló': 5357,
  'estatal': 5358,
  'esclavo': 5359,
  'déjate': 5360,
  'costillas': 5361,
  'costará': 5362,
  'chistes': 5363,
  'arreglaré': 5364,
  'alcanza': 5365,
  'valientes': 5366,
  'privilegio': 5367,
  'orina': 5368,
  'jovencito': 5369,
  'invasión': 5370,
  'estuvieran': 5371,
  'estupideces': 5372,
  'encaja': 5373,
  'durará': 5374,
  'descanse': 5375,
  'déjanos': 5376,
  'camas': 5377,
  'cables': 5378,
  'bárbara': 5379,
  'avanzando': 5380,
  'usualmente': 5381,
  'sagrada': 5382,
  'sable': 5383,
  'retírense': 5384,
  'retirada': 5385,
  'psicópata': 5386,
  'preocupo': 5387,
  'plaga': 5388,
  'ohh': 5389,
  'malvada': 5390,
  'existir': 5391,
  'entendemos': 5392,
  'bendito': 5393,
  'arregla': 5394,
  'supuestamente': 5395,
  'residente': 5396,
  'rescatar': 5397,
  'pesada': 5398,
  'oímos': 5399,
  'locas': 5400,
  'llegarán': 5401,
  'hermanito': 5402,
  'hamburguesas': 5403,
  'firmado': 5404,
  'entrenar': 5405,
  'encendido': 5406,
  'disfruten': 5407,
  'deseaba': 5408,
  'conocías': 5409,
  'cambiaron': 5410,
  'boxeo': 5411,
  'autorizado': 5412,
  'armadura': 5413,
  'sobrevivió': 5414,
  'preocupados': 5415,
  'ocasiones': 5416,
  'necesitarás': 5417,
  'mutante': 5418,
  'músculos': 5419,
  'mantienen': 5420,
  'justa': 5421,
  'increíbles': 5422,
  'incómodo': 5423,
  'harta': 5424,
  'expedición': 5425,
  'esfuerzos': 5426,
  'directa': 5427,
  'deberes': 5428,
  'contratado': 5429,
  'usé': 5430,
  'sentar': 5431,
  'presentes': 5432,
  'mierdas': 5433,
  'merecen': 5434,
  'llamaban': 5435,
  'ejecución': 5436,
  'darás': 5437,
  'créame': 5438,
  'busquemos': 5439,
  'afirmativo': 5440,
  'admito': 5441,
  'talla': 5442,
  'rodeado': 5443,
  'reparar': 5444,
  'quitó': 5445,
  'patada': 5446,
  'lea': 5447,
  'jugado': 5448,
  'humilde': 5449,
  'encantó': 5450,
  'encantará': 5451,
  'elefante': 5452,
  'echaré': 5453,
  'demostración': 5454,
  'creación': 5455,
  'compañia': 5456,
  'camaradas': 5457,
  'bebido': 5458,
  'bata': 5459,
  'soga': 5460,
  'signos': 5461,
  'sentirte': 5462,
  'póngase': 5463,
  'perderé': 5464,
  'pegar': 5465,
  'ondas': 5466,
  'mapas': 5467,
  'mantenerte': 5468,
  'llenos': 5469,
  'intentes': 5470,
  'informar': 5471,
  'independiente': 5472,
  'fuéramos': 5473,
  'encontrarnos': 5474,
  'distintas': 5475,
  'conque': 5476,
  'conozcas': 5477,
  'cometa': 5478,
  'acostado': 5479,
  'pentágono': 5480,
  'pensaría': 5481,
  'galería': 5482,
  'duermo': 5483,
  'decepcionado': 5484,
  'blancas': 5485,
  'bajó': 5486,
  'ausencia': 5487,
  'asistencia': 5488,
  'usaste': 5489,
  'suma': 5490,
  'ogro': 5491,
  'griego': 5492,
  'caballería': 5493,
  'bondad': 5494,
  'bolsillos': 5495,
  'avanzada': 5496,
  'ancestros': 5497,
  'alturas': 5498,
  'puño': 5499,
  'otoño': 5500,
  'observar': 5501,
  'mande': 5502,
  'ligera': 5503,
  'iris': 5504,
  'horno': 5505,
  'grosero': 5506,
  'elementos': 5507,
  'debías': 5508,
  'culpo': 5509,
  'comencemos': 5510,
  'bebo': 5511,
  'veloz': 5512,
  'telefónica': 5513,
  'tatuaje': 5514,
  'sácalo': 5515,
  'resuelto': 5516,
  'quedaría': 5517,
  'pais': 5518,
  'nocturno': 5519,
  'líquido': 5520,
  'filas': 5521,
  'estes': 5522,
  'espías': 5523,
  'especialidad': 5524,
  'aves': 5525,
  'amables': 5526,
  'uniformes': 5527,
  'sentirlo': 5528,
  'retrocede': 5529,
  'reconocer': 5530,
  'rebelde': 5531,
  'plena': 5532,
  'pervertido': 5533,
  'obedecer': 5534,
  'mentido': 5535,
  'matara': 5536,
  'llamarla': 5537,
  'jade': 5538,
  'entienda': 5539,
  'ego': 5540,
  'echó': 5541,
  'considero': 5542,
  'cobardes': 5543,
  'césped': 5544,
  'capullo': 5545,
  'baños': 5546,
  'supervivencia': 5547,
  'soñado': 5548,
  'separados': 5549,
  'respirando': 5550,
  'negras': 5551,
  'infantería': 5552,
  'identificado': 5553,
  'honesta': 5554,
  'hambriento': 5555,
  'escribo': 5556,
  'despedirme': 5557,
  'demos': 5558,
  'cortesía': 5559,
  'colonia': 5560,
  'ayudes': 5561,
  'amiguito': 5562,
  'alfa': 5563,
  'temen': 5564,
  'suéltenme': 5565,
  'sensacional': 5566,
  'sacarle': 5567,
  'prostitutas': 5568,
  'pondrás': 5569,
  'literalmente': 5570,
  'licor': 5571,
  'juzgar': 5572,
  'equilibrio': 5573,
  'envíen': 5574,
  'entregado': 5575,
  'corazon': 5576,
  'consulta': 5577,
  'boxeador': 5578,
  'bahía': 5579,
  'usen': 5580,
  'taller': 5581,
  'rompiste': 5582,
  'quitaron': 5583,
  'patatas': 5584,
  'operar': 5585,
  'mochila': 5586,
  'interesada': 5587,
  'inspiración': 5588,
  'explicaré': 5589,
  'estuviese': 5590,
  'enero': 5591,
  'cumple': 5592,
  'claudia': 5593,
  'lógico': 5594,
  'islas': 5595,
  'insignificante': 5596,
  'hermandad': 5597,
  'goma': 5598,
  'franco': 5599,
  'duermes': 5600,
  'chimenea': 5601,
  'trataron': 5602,
  'tardará': 5603,
  'sábanas': 5604,
  'polo': 5605,
  'podia': 5606,
  'perspectiva': 5607,
  'papás': 5608,
  'ministerio': 5609,
  'gesto': 5610,
  'fumando': 5611,
  'disfrutando': 5612,
  'debas': 5613,
  'culpar': 5614,
  'atreve': 5615,
  'arriesgar': 5616,
  'arabia': 5617,
  'vital': 5618,
  'túneles': 5619,
  'transferencia': 5620,
  'suicidó': 5621,
  'sucios': 5622,
  'seguida': 5623,
  'regresamos': 5624,
  'recibirá': 5625,
  'oculto': 5626,
  'honorable': 5627,
  'hmm': 5628,
  'generaciones': 5629,
  'escolar': 5630,
  'dámela': 5631,
  'controlado': 5632,
  'constantemente': 5633,
  'cheques': 5634,
  'bromeo': 5635,
  'australia': 5636,
  'asegurarnos': 5637,
  'apetito': 5638,
  'vivió': 5639,
  'vías': 5640,
  'piden': 5641,
  'órbita': 5642,
  'manzanas': 5643,
  'interesantes': 5644,
  'herir': 5645,
  'exámenes': 5646,
  'enfadada': 5647,
  'cuadras': 5648,
  'cometí': 5649,
  'colorado': 5650,
  'asia': 5651,
  'término': 5652,
  'síndrome': 5653,
  'refrigerador': 5654,
  'ofrezco': 5655,
  'mentiste': 5656,
  'mecanismo': 5657,
  'manejarlo': 5658,
  'especialista': 5659,
  'dejarlos': 5660,
  'crucero': 5661,
  'avísame': 5662,
  'ardiendo': 5663,
  'saludar': 5664,
  'papito': 5665,
  'oficio': 5666,
  'metieron': 5667,
  'estadounidenses': 5668,
  'divertirnos': 5669,
  'cuentan': 5670,
  'toneladas': 5671,
  'tobillo': 5672,
  'técnico': 5673,
  'saudita': 5674,
  'salvador': 5675,
  'sabré': 5676,
  'presta': 5677,
  'ordenar': 5678,
  'naciste': 5679,
  'mostró': 5680,
  'moderna': 5681,
  'mito': 5682,
  'juramento': 5683,
  'garras': 5684,
  'consejero': 5685,
  'confusión': 5686,
  'aguarda': 5687,
  'vuestras': 5688,
  'ventas': 5689,
  'sirviente': 5690,
  'sierra': 5691,
  'serios': 5692,
  'pónganse': 5693,
  'perderte': 5694,
  'juegan': 5695,
  'indicado': 5696,
  'entrenado': 5697,
  'enteré': 5698,
  'enhorabuena': 5699,
  'cuba': 5700,
  'cruce': 5701,
  'crean': 5702,
  'contarme': 5703,
  'cancelar': 5704,
  'callar': 5705,
  'aceptarlo': 5706,
  'suite': 5707,
  'soñar': 5708,
  'provisiones': 5709,
  'preferido': 5710,
  'normas': 5711,
  'mientas': 5712,
  'mandé': 5713,
  'mandado': 5714,
  'llámeme': 5715,
  'huella': 5716,
  'gilipollas': 5717,
  'fingiendo': 5718,
  'eterno': 5719,
  'escopeta': 5720,
  'divertirse': 5721,
  'distintos': 5722,
  'desesperado': 5723,
  'bailarina': 5724,
  'alimentar': 5725,
  'adora': 5726,
  'simpático': 5727,
  'ridícula': 5728,
  'respetar': 5729,
  'rabia': 5730,
  'presentó': 5731,
  'poderosos': 5732,
  'pito': 5733,
  'perla': 5734,
  'pendiente': 5735,
  'maestros': 5736,
  'humanas': 5737,
  'estare': 5738,
  'escuchame': 5739,
  'entere': 5740,
  'bésame': 5741,
  'suicida': 5742,
  'suelten': 5743,
  'sobrina': 5744,
  'presentarte': 5745,
  'padrino': 5746,
  'fractura': 5747,
  'explicarte': 5748,
  'escuches': 5749,
  'envío': 5750,
  'enterrar': 5751,
  'destrozado': 5752,
  'dejarle': 5753,
  'condicional': 5754,
  'cometió': 5755,
  'avergonzado': 5756,
  'artículos': 5757,
  'amenazas': 5758,
  'victima': 5759,
  'val': 5760,
  'símbolos': 5761,
  'saltó': 5762,
  'repetir': 5763,
  'pelos': 5764,
  'organizado': 5765,
  'novato': 5766,
  'localizar': 5767,
  'lindas': 5768,
  'intentan': 5769,
  'estara': 5770,
  'enamorados': 5771,
  'discutiendo': 5772,
  'cajón': 5773,
  'aliados': 5774,
  'águila': 5775,
  'sugiere': 5776,
  'sexto': 5777,
  'retirarse': 5778,
  'reciente': 5779,
  'protector': 5780,
  'presos': 5781,
  'pidieron': 5782,
  'mide': 5783,
  'materia': 5784,
  'hueón': 5785,
  'gentil': 5786,
  'evidente': 5787,
  'enlace': 5788,
  'dorado': 5789,
  'discúlpenme': 5790,
  'dejare': 5791,
  'conclusión': 5792,
  'competir': 5793,
  'auténtico': 5794,
  'anuncios': 5795,
  'afortunadamente': 5796,
  'siesta': 5797,
  'sentidos': 5798,
  'principales': 5799,
  'mátame': 5800,
  'hallé': 5801,
  'girar': 5802,
  'extraterrestre': 5803,
  'egipto': 5804,
  'canciller': 5805,
  'valores': 5806,
  'recibiste': 5807,
  'quítense': 5808,
  'protocolo': 5809,
  'preocuparme': 5810,
  'pacto': 5811,
  'módulo': 5812,
  'inteligentes': 5813,
  'imaginaba': 5814,
  'huracán': 5815,
  'dejaba': 5816,
  'canales': 5817,
  'buscaremos': 5818,
  'botellas': 5819,
  'billones': 5820,
  'atraparlo': 5821,
  'arrestar': 5822,
  'acento': 5823,
  'accidentes': 5824,
  'vientre': 5825,
  'tomaría': 5826,
  'timón': 5827,
  'tabla': 5828,
  'sacando': 5829,
  'pintar': 5830,
  'particularmente': 5831,
  'palma': 5832,
  'organizar': 5833,
  'llevarán': 5834,
  'llamaría': 5835,
  'irlandés': 5836,
  'esperes': 5837,
  'crecí': 5838,
  'as': 5839,
  'agarró': 5840,
  'voluntario': 5841,
  'vivirá': 5842,
  'vigilar': 5843,
  'vestíbulo': 5844,
  'soltar': 5845,
  'siguientes': 5846,
  'residentes': 5847,
  'leíste': 5848,
  'hacha': 5849,
  'felicito': 5850,
  'enojar': 5851,
  'ejercicios': 5852,
  'drogado': 5853,
  'cagar': 5854,
  'traducido': 5855,
  'suplico': 5856,
  'salario': 5857,
  'préstamo': 5858,
  'mensajero': 5859,
  'granjero': 5860,
  'filosofía': 5861,
  'faltaba': 5862,
  'explosivo': 5863,
  'diagnóstico': 5864,
  'cardíaco': 5865,
  'bosques': 5866,
  'acabé': 5867,
  'tomate': 5868,
  'quitarle': 5869,
  'magneto': 5870,
  'lance': 5871,
  'huyó': 5872,
  'hilo': 5873,
  'hablará': 5874,
  'gemelos': 5875,
  'eligió': 5876,
  'doler': 5877,
  'dificultades': 5878,
  'concentración': 5879,
  'artistas': 5880,
  'aceptas': 5881,
  'acabando': 5882,
  'torpe': 5883,
  'sobrevivientes': 5884,
  'saldrás': 5885,
  'región': 5886,
  'produce': 5887,
  'post': 5888,
  'obsesionado': 5889,
  'descubre': 5890,
  'cerró': 5891,
  'cayeron': 5892,
  'casé': 5893,
  'automóvil': 5894,
  'atiende': 5895,
  'voló': 5896,
  'ruinas': 5897,
  'orilla': 5898,
  'mañanas': 5899,
  'magnífica': 5900,
  'excitante': 5901,
  'enseñarles': 5902,
  'comenzaron': 5903,
  'cerradas': 5904,
  'celosa': 5905,
  'batallón': 5906,
  'atrae': 5907,
  'apresúrate': 5908,
  'rojos': 5909,
  'ofender': 5910,
  'novela': 5911,
  'nazis': 5912,
  'muros': 5913,
  'limpias': 5914,
  'espectacular': 5915,
  'empiecen': 5916,
  'cortina': 5917,
  'construido': 5918,
  'comerciales': 5919,
  'chaval': 5920,
  'cadenas': 5921,
  'botones': 5922,
  'arrogante': 5923,
  'vendo': 5924,
  'tuviéramos': 5925,
  'tocaba': 5926,
  'situaciones': 5927,
  'semental': 5928,
  'responsables': 5929,
  'reportes': 5930,
  'muestran': 5931,
  'facultad': 5932,
  'envidia': 5933,
  'emocionado': 5934,
  'disposición': 5935,
  'diremos': 5936,
  'dejarnos': 5937,
  'deberiamos': 5938,
  'conquistar': 5939,
  'bonitos': 5940,
  'averigua': 5941,
  'almohada': 5942,
  'activo': 5943,
  'abuelita': 5944,
  'vitales': 5945,
  'verse': 5946,
  'traicionado': 5947,
  'teresa': 5948,
  'soñé': 5949,
  'roba': 5950,
  'moderno': 5951,
  'manta': 5952,
  'lidiar': 5953,
  'joda': 5954,
  'despejen': 5955,
  'comunista': 5956,
  'aprendiendo': 5957,
  'tirando': 5958,
  'suiza': 5959,
  'pita': 5960,
  'pensaron': 5961,
  'nudos': 5962,
  'muestre': 5963,
  'molestado': 5964,
  'llora': 5965,
  'hembra': 5966,
  'eternamente': 5967,
  'duermas': 5968,
  'disparan': 5969,
  'creerá': 5970,
  'colgado': 5971,
  'arrestaron': 5972,
  'acusación': 5973,
  'viajando': 5974,
  'tremenda': 5975,
  'simples': 5976,
  'servirle': 5977,
  'planea': 5978,
  'negar': 5979,
  'necesitabas': 5980,
  'mordido': 5981,
  'lecciones': 5982,
  'insectos': 5983,
  'imaginado': 5984,
  'habríamos': 5985,
  'fallar': 5986,
  'escudos': 5987,
  'diviértete': 5988,
  'crece': 5989,
  'combatir': 5990,
  'canto': 5991,
  'bichos': 5992,
  'atacando': 5993,
  'apestoso': 5994,
  'visa': 5995,
  'times': 5996,
  'retirar': 5997,
  'persigue': 5998,
  'ooh': 5999,
  'leerlo': 6000,
  'investigador': 6001,
  'fan': 6002,
  'falsos': 6003,
  'echarle': 6004,
  'dire': 6005,
  'cuide': 6006,
  'comemos': 6007,
  'transmisor': 6008,
  'servido': 6009,
  'salchichas': 6010,
  'récord': 6011,
  'raíces': 6012,
  'propongo': 6013,
  'oficinas': 6014,
  'neo': 6015,
  'navegar': 6016,
  'llevarnos': 6017,
  'lastima': 6018,
  'granero': 6019,
  'ginebra': 6020,
  'evidencias': 6021,
  'enseñarme': 6022,
  'dejaras': 6023,
  'dañado': 6024,
  'cuentes': 6025,
  'bandas': 6026,
  'avancen': 6027,
  'ángulo': 6028,
  'ancianos': 6029,
  'activado': 6030,
  'acceder': 6031,
  'varón': 6032,
  'sociales': 6033,
  'sake': 6034,
  'recuperado': 6035,
  'quedarán': 6036,
  'porfavor': 6037,
  'navaja': 6038,
  'muñeco': 6039,
  'milord': 6040,
  'manchas': 6041,
  'escuchamos': 6042,
  'entremos': 6043,
  'cosmos': 6044,
  'conservar': 6045,
  'condesa': 6046,
  'ataúd': 6047,
  'antena': 6048,
  'anciana': 6049,
  'ademas': 6050,
  'toro': 6051,
  'subido': 6052,
  'sígame': 6053,
  'recoge': 6054,
  'quemando': 6055,
  'preguntaré': 6056,
  'pondría': 6057,
  'pensión': 6058,
  'panel': 6059,
  'muriera': 6060,
  'llegarás': 6061,
  'inversión': 6062,
  'hablen': 6063,
  'geniales': 6064,
  'genética': 6065,
  'funciones': 6066,
  'enfermería': 6067,
  'destruye': 6068,
  'destruirlo': 6069,
  'cargamento': 6070,
  'calmado': 6071,
  'cabe': 6072,
  'billete': 6073,
  'asustó': 6074,
  'aparecen': 6075,
  'aléjese': 6076,
  'recuperación': 6077,
  'portada': 6078,
  'mojado': 6079,
  'método': 6080,
  'masiva': 6081,
  'martillo': 6082,
  'hospitales': 6083,
  'hallaron': 6084,
  'hallamos': 6085,
  'fax': 6086,
  'exacta': 6087,
  'estrés': 6088,
  'empezaste': 6089,
  'echaron': 6090,
  'cuidados': 6091,
  'creíste': 6092,
  'conteste': 6093,
  'avisar': 6094,
  'atraparon': 6095,
  'viaja': 6096,
  'terrorismo': 6097,
  'tarado': 6098,
  'salchicha': 6099,
  'pum': 6100,
  'peste': 6101,
  'niego': 6102,
  'morgue': 6103,
  'misteriosa': 6104,
  'miseria': 6105,
  'mantenimiento': 6106,
  'malentendido': 6107,
  'justamente': 6108,
  'iniciar': 6109,
  'escondiendo': 6110,
  'cambiaste': 6111,
  'caerá': 6112,
  'bóveda': 6113,
  'billetera': 6114,
  'amamos': 6115,
  'unirse': 6116,
  'soltera': 6117,
  'mesas': 6118,
  'marte': 6119,
  'logra': 6120,
  'indican': 6121,
  'imagínate': 6122,
  'gusanos': 6123,
  'grabación': 6124,
  'fuegos': 6125,
  'firmada': 6126,
  'experiencias': 6127,
  'editor': 6128,
  'asilo': 6129,
  'venid': 6130,
  'vendrías': 6131,
  'utiliza': 6132,
  'tórax': 6133,
  'significan': 6134,
  'saludo': 6135,
  'ordenado': 6136,
  'dales': 6137,
  'cuchillos': 6138,
  'beba': 6139,
  'barril': 6140,
  'algodón': 6141,
  'ajedrez': 6142,
  'acostarte': 6143,
  'reno': 6144,
  'llévenselo': 6145,
  'ligero': 6146,
  'inusual': 6147,
  'gases': 6148,
  'estadio': 6149,
  'desee': 6150,
  'convención': 6151,
  'circuito': 6152,
  'azar': 6153,
  'atrapa': 6154,
  'asumo': 6155,
  'test': 6156,
  'termines': 6157,
  'reunir': 6158,
  'posee': 6159,
  'lenta': 6160,
  'kilo': 6161,
  'inyección': 6162,
  'ingeniero': 6163,
  'entres': 6164,
  'drama': 6165,
  'derrotar': 6166,
  'dedica': 6167,
  'criado': 6168,
  'consuelo': 6169,
  'conocidos': 6170,
  'comeré': 6171,
  'calzoncillos': 6172,
  'calmate': 6173,
  'bobo': 6174,
  'bastantes': 6175,
  'bajado': 6176,
  'ayudarles': 6177,
  'afecto': 6178,
  'recto': 6179,
  'rebelión': 6180,
  'pinturas': 6181,
  'murio': 6182,
  'grabar': 6183,
  'durar': 6184,
  'culpables': 6185,
  'cómico': 6186,
  'bendita': 6187,
  'artificial': 6188,
  'agradecer': 6189,
  'adelantado': 6190,
  'acabemos': 6191,
  'valen': 6192,
  'talentos': 6193,
  'separado': 6194,
  'pusimos': 6195,
  'menú': 6196,
  'manténganse': 6197,
  'juguemos': 6198,
  'gral': 6199,
  'demora': 6200,
  'democracia': 6201,
  'cuelga': 6202,
  'cruzado': 6203,
  'cristiano': 6204,
  'caramelo': 6205,
  'aprieta': 6206,
  'tomarlo': 6207,
  'tarta': 6208,
  'tales': 6209,
  'supremo': 6210,
  'solucionar': 6211,
  'sensores': 6212,
  'retrato': 6213,
  'prometida': 6214,
  'pagarle': 6215,
  'musica': 6216,
  'mejorando': 6217,
  'granada': 6218,
  'espléndido': 6219,
  'creando': 6220,
  'billar': 6221,
  'acercan': 6222,
  'yardas': 6223,
  'volvía': 6224,
  'vehículos': 6225,
  'tejido': 6226,
  'signo': 6227,
  'saturación': 6228,
  'picnic': 6229,
  'multa': 6230,
  'lloviendo': 6231,
  'literatura': 6232,
  'ire': 6233,
  'enviamos': 6234,
  'cortas': 6235,
  'corren': 6236,
  'congelado': 6237,
  'comprobar': 6238,
  'camarero': 6239,
  'bajos': 6240,
  'actualmente': 6241,
  'turistas': 6242,
  'sumamente': 6243,
  'perderá': 6244,
  'pagamos': 6245,
  'ovejas': 6246,
  'novias': 6247,
  'muñecas': 6248,
  'maní': 6249,
  'madrina': 6250,
  'llevarás': 6251,
  'instintos': 6252,
  'hacíamos': 6253,
  'enseñé': 6254,
  'conseguirlo': 6255,
  'carbón': 6256,
  'avanzado': 6257,
  'avance': 6258,
  'autógrafo': 6259,
  'aumentar': 6260,
  'aplauso': 6261,
  'viuda': 6262,
  'vacías': 6263,
  'universal': 6264,
  'trabaje': 6265,
  'tómala': 6266,
  'sorprendió': 6267,
  'ríes': 6268,
  'pulmón': 6269,
  'próximas': 6270,
  'orcos': 6271,
  'mezcla': 6272,
  'meto': 6273,
  'inglesa': 6274,
  'independencia': 6275,
  'huésped': 6276,
  'extrañar': 6277,
  'encantar': 6278,
  'destruyendo': 6279,
  'corresponde': 6280,
  'correctamente': 6281,
  'coco': 6282,
  'asustando': 6283,
  'administrador': 6284,
  'abogada': 6285,
  'tierno': 6286,
  'soledad': 6287,
  'recuerdes': 6288,
  'pope': 6289,
  'perderemos': 6290,
  'osos': 6291,
  'oírte': 6292,
  'escribiste': 6293,
  'depresión': 6294,
  'caramelos': 6295,
  'basado': 6296,
  'aventuras': 6297,
  'aumenta': 6298,
  'amada': 6299,
  'agotado': 6300,
  'agarré': 6301,
  'trabajador': 6302,
  'rodar': 6303,
  'repite': 6304,
  'recordaré': 6305,
  'llover': 6306,
  'entendió': 6307,
  'contactar': 6308,
  'agradecería': 6309,
  'usaré': 6310,
  'tomaba': 6311,
  'sujétate': 6312,
  'sospechas': 6313,
  'soplón': 6314,
  'sándwich': 6315,
  'reunido': 6316,
  'prepare': 6317,
  'norteamericanos': 6318,
  'hallado': 6319,
  'gustaban': 6320,
  'festival': 6321,
  'eventos': 6322,
  'escriba': 6323,
  'escogió': 6324,
  'demuestra': 6325,
  'déjelo': 6326,
  'barata': 6327,
  'banquete': 6328,
  'arreglos': 6329,
  'alcanzado': 6330,
  'acostarme': 6331,
  'sentiste': 6332,
  'plomo': 6333,
  'perdería': 6334,
  'noté': 6335,
  'fuí': 6336,
  'destruida': 6337,
  'desperdicio': 6338,
  'conveniente': 6339,
  'apártense': 6340,
  'amarilla': 6341,
  'agarren': 6342,
  'yate': 6343,
  'vendré': 6344,
  'soportarlo': 6345,
  'sencilla': 6346,
  'regresaremos': 6347,
  'regimiento': 6348,
  'quitarte': 6349,
  'ponme': 6350,
  'peleamos': 6351,
  'muevanse': 6352,
  'manicomio': 6353,
  'lesbiana': 6354,
  'juzgado': 6355,
  'jugaba': 6356,
  'inventó': 6357,
  'injusto': 6358,
  'farsa': 6359,
  'estribor': 6360,
  'esencial': 6361,
  'encontraba': 6362,
  'conjunto': 6363,
  'comprendido': 6364,
  'comercio': 6365,
  'chau': 6366,
  'cazas': 6367,
  'atado': 6368,
  'alimentos': 6369,
  'vagón': 6370,
  'tranquilidad': 6371,
  'torta': 6372,
  'similar': 6373,
  'quedaste': 6374,
  'mandaron': 6375,
  'hondo': 6376,
  'haciéndolo': 6377,
  'gripe': 6378,
  'dirigen': 6379,
  'diferencias': 6380,
  'calibre': 6381,
  'botes': 6382,
  'borrar': 6383,
  'asquerosa': 6384,
  'apague': 6385,
  'ahh': 6386,
  'abismo': 6387,
  'zoológico': 6388,
  'salvación': 6389,
  'querrán': 6390,
  'propiedades': 6391,
  'permita': 6392,
  'llevaste': 6393,
  'llenas': 6394,
  'invento': 6395,
  'inferior': 6396,
  'huéspedes': 6397,
  'hoteles': 6398,
  'gigantes': 6399,
  'flujo': 6400,
  'esperábamos': 6401,
  'encantaba': 6402,
  'ebrio': 6403,
  'dormí': 6404,
  'decides': 6405,
  'cobertura': 6406,
  'cigarro': 6407,
  'acepte': 6408,
  'traía': 6409,
  'terminemos': 6410,
  'seguirás': 6411,
  'reconocí': 6412,
  'proxima': 6413,
  'ponle': 6414,
  'plasma': 6415,
  'monitor': 6416,
  'lunar': 6417,
  'escucharlo': 6418,
  'descarga': 6419,
  'cuidaré': 6420,
  'cometiendo': 6421,
  'centímetros': 6422,
  'capturado': 6423,
  'ardiente': 6424,
  'usaremos': 6425,
  'traten': 6426,
  'terminaron': 6427,
  'sacaremos': 6428,
  'rodando': 6429,
  'renuncio': 6430,
  'recomiendo': 6431,
  'ponía': 6432,
  'policias': 6433,
  'papeleo': 6434,
  'novios': 6435,
  'medicación': 6436,
  'fu': 6437,
  'escenas': 6438,
  'equivocados': 6439,
  'cortaron': 6440,
  'contratos': 6441,
  'contaba': 6442,
  'camilla': 6443,
  'bla': 6444,
  'bellas': 6445,
  'adecuada': 6446,
  'viviente': 6447,
  'secuestrado': 6448,
  'religioso': 6449,
  'regresaste': 6450,
  'permites': 6451,
  'perfectos': 6452,
  'pelicula': 6453,
  'mínima': 6454,
  'luchador': 6455,
  'lámpara': 6456,
  'famosos': 6457,
  'extraterrestres': 6458,
  'escotilla': 6459,
  'dolió': 6460,
  'condenada': 6461,
  'andaba': 6462,
  'traficante': 6463,
  'tiras': 6464,
  'tardaré': 6465,
  'salvé': 6466,
  'regresas': 6467,
  'recordarlo': 6468,
  'molestaría': 6469,
  'llamará': 6470,
  'feria': 6471,
  'febrero': 6472,
  'eventualmente': 6473,
  'escondite': 6474,
  'duerma': 6475,
  'dijeras': 6476,
  'creció': 6477,
  'colinas': 6478,
  'atrevas': 6479,
  'ataquen': 6480,
  'apunten': 6481,
  'antiguas': 6482,
  'adolescentes': 6483,
  'acusaciones': 6484,
  'supuse': 6485,
  'sgto': 6486,
  'recado': 6487,
  'programado': 6488,
  'patria': 6489,
  'pariente': 6490,
  'necesaria': 6491,
  'mecánico': 6492,
  'joya': 6493,
  'inútiles': 6494,
  'hara': 6495,
  'ejecutivo': 6496,
  'dulzura': 6497,
  'disparé': 6498,
  'disparaste': 6499,
  'detendrá': 6500,
  'desconocidos': 6501,
  'comprarme': 6502,
  'bién': 6503,
  'videos': 6504,
  'veredicto': 6505,
  'trajimos': 6506,
  'tocan': 6507,
  'tiré': 6508,
  'tardar': 6509,
  'suenas': 6510,
  'reservas': 6511,
  'reciben': 6512,
  'invita': 6513,
  'granadas': 6514,
  'grabado': 6515,
  'falda': 6516,
  'extraordinaria': 6517,
  'escondidas': 6518,
  'direcciones': 6519,
  'comparado': 6520,
  'clan': 6521,
  'besos': 6522,
  'angel': 6523,
  'afortunada': 6524,
  'ácido': 6525,
  'tiren': 6526,
  'pongamos': 6527,
  'planetas': 6528,
  'perdon': 6529,
  'pañales': 6530,
  'moriría': 6531,
  'mexicano': 6532,
  'marchó': 6533,
  'maletero': 6534,
  'individuo': 6535,
  'huelga': 6536,
  'habitual': 6537,
  'extranjeros': 6538,
  'deseado': 6539,
  'dada': 6540,
  'cohetes': 6541,
  'cocinero': 6542,
  'científica': 6543,
  'casaste': 6544,
  'bigote': 6545,
  'avanza': 6546,
  'atrapó': 6547,
  'ansioso': 6548,
  'taco': 6549,
  'sana': 6550,
  'peleen': 6551,
  'pasteles': 6552,
  'mirado': 6553,
  'marinero': 6554,
  'echando': 6555,
  'descubrieron': 6556,
  'confesar': 6557,
  'compartimos': 6558,
  'bicho': 6559,
  'avisa': 6560,
  'atajo': 6561,
  'acostarse': 6562,
  'voluntarios': 6563,
  'marcador': 6564,
  'maquina': 6565,
  'gallinas': 6566,
  'estallar': 6567,
  'desafío': 6568,
  'decida': 6569,
  'cuadros': 6570,
  'contador': 6571,
  'británica': 6572,
  'autor': 6573,
  'arde': 6574,
  'absoluta': 6575,
  'vera': 6576,
  'trece': 6577,
  'supervisor': 6578,
  'solitaria': 6579,
  'rindo': 6580,
  'paquetes': 6581,
  'oriente': 6582,
  'lastimarte': 6583,
  'inestable': 6584,
  'habernos': 6585,
  'fans': 6586,
  'enseñaron': 6587,
  'duermen': 6588,
  'documental': 6589,
  'divina': 6590,
  'criar': 6591,
  'casarnos': 6592,
  'bandidos': 6593,
  'agradecerle': 6594,
  'abdomen': 6595,
  'tortuga': 6596,
  'tocarme': 6597,
  'tire': 6598,
  'revisen': 6599,
  'pañuelo': 6600,
  'levantarse': 6601,
  'imperial': 6602,
  'deprimido': 6603,
  'cadera': 6604,
  'bandeja': 6605,
  'aparezca': 6606,
  'visitantes': 6607,
  'tumor': 6608,
  'todopoderoso': 6609,
  'tentación': 6610,
  'sugerencia': 6611,
  'seguirán': 6612,
  'seguidores': 6613,
  'saldrán': 6614,
  'record': 6615,
  'pruebe': 6616,
  'ponla': 6617,
  'permitirá': 6618,
  'permanece': 6619,
  'olvidemos': 6620,
  'mueran': 6621,
  'mariposa': 6622,
  'hockey': 6623,
  'fumo': 6624,
  'firmes': 6625,
  'echas': 6626,
  'crítico': 6627,
  'carros': 6628,
  'caravana': 6629,
  'bestias': 6630,
  'barrera': 6631,
  'acerquen': 6632,
  'tráelo': 6633,
  'satisfacción': 6634,
  'revelar': 6635,
  'mueven': 6636,
  'mosca': 6637,
  'metida': 6638,
  'mascotas': 6639,
  'hablábamos': 6640,
  'ejércitos': 6641,
  'discutimos': 6642,
  'declarar': 6643,
  'conozcan': 6644,
  'colocar': 6645,
  'cinturones': 6646,
  'ayudara': 6647,
  'asumir': 6648,
  'asesinaron': 6649,
  'alquilar': 6650,
  'agujeros': 6651,
  'vuelos': 6652,
  'sirvió': 6653,
  'sirva': 6654,
  'ruidos': 6655,
  'rincón': 6656,
  'quito': 6657,
  'putos': 6658,
  'patear': 6659,
  'paracaídas': 6660,
  'mear': 6661,
  'irlanda': 6662,
  'honrado': 6663,
  'escrita': 6664,
  'complace': 6665,
  'cojo': 6666,
  'cazando': 6667,
  'capilla': 6668,
  'america': 6669,
  'volviera': 6670,
  'verificar': 6671,
  'suélteme': 6672,
  'sirvientes': 6673,
  'senadora': 6674,
  'semilla': 6675,
  'salio': 6676,
  'quinta': 6677,
  'pertenecen': 6678,
  'pendientes': 6679,
  'partidos': 6680,
  'nazi': 6681,
  'misterios': 6682,
  'instrumento': 6683,
  'háganlo': 6684,
  'hacienda': 6685,
  'fuma': 6686,
  'explique': 6687,
  'esparta': 6688,
  'escribes': 6689,
  'diseñado': 6690,
  'canalla': 6691,
  'cambias': 6692,
  'vaqueros': 6693,
  'tronco': 6694,
  'toallas': 6695,
  'tendrían': 6696,
  'significaba': 6697,
  'sientan': 6698,
  'salidas': 6699,
  'políticas': 6700,
  'oriental': 6701,
  'mutuamente': 6702,
  'lila': 6703,
  'intimidad': 6704,
  'grabando': 6705,
  'filmar': 6706,
  'fenomenal': 6707,
  'falsas': 6708,
  'evacuar': 6709,
  'duelo': 6710,
  'dormiste': 6711,
  'dinosaurios': 6712,
  'dados': 6713,
  'cruza': 6714,
  'conseguiremos': 6715,
  'confíe': 6716,
  'conexiones': 6717,
  'cómplice': 6718,
  'cerradura': 6719,
  'asesor': 6720,
  'admitirlo': 6721,
  'vagabundo': 6722,
  'tomarme': 6723,
  'tarda': 6724,
  'sonriendo': 6725,
  'so': 6726,
  'revisando': 6727,
  'prudente': 6728,
  'postal': 6729,
  'po': 6730,
  'pizzas': 6731,
  'mariscal': 6732,
  'marcos': 6733,
  'marcharse': 6734,
  'lava': 6735,
  'flanco': 6736,
  'explico': 6737,
  'escúchenme': 6738,
  'encárgate': 6739,
  'decidiste': 6740,
  'confuso': 6741,
  'condenados': 6742,
  'comiencen': 6743,
  'coman': 6744,
  'cerrando': 6745,
  'cd': 6746,
  'vientos': 6747,
  'traicionó': 6748,
  'suyas': 6749,
  'suelen': 6750,
  'romántica': 6751,
  'perderás': 6752,
  'oración': 6753,
  'occidental': 6754,
  'liberación': 6755,
  'levantado': 6756,
  'gancho': 6757,
  'evolución': 6758,
  'escoge': 6759,
  'cueste': 6760,
  'chef': 6761,
  'árabe': 6762,
  'venían': 6763,
  'venda': 6764,
  'sustancia': 6765,
  'secundario': 6766,
  'sacarla': 6767,
  'rubio': 6768,
  'presentado': 6769,
  'precisa': 6770,
  'perteneces': 6771,
  'palos': 6772,
  'oscuras': 6773,
  'lunático': 6774,
  'lograremos': 6775,
  'llamados': 6776,
  'leyó': 6777,
  'inicio': 6778,
  'docenas': 6779,
  'directora': 6780,
  'complicada': 6781,
  'cogió': 6782,
  'camara': 6783,
  'arruinó': 6784,
  'apuro': 6785,
  'abro': 6786,
  'vestuario': 6787,
  'vergonzoso': 6788,
  'sorprendería': 6789,
  'sientate': 6790,
  'rastrear': 6791,
  'proviene': 6792,
  'profesores': 6793,
  'primos': 6794,
  'premios': 6795,
  'permitió': 6796,
  'medico': 6797,
  'marcar': 6798,
  'mantenlo': 6799,
  'jueces': 6800,
  'instrumentos': 6801,
  'incendios': 6802,
  'imbecil': 6803,
  'garantía': 6804,
  'francos': 6805,
  'especies': 6806,
  'escribiré': 6807,
  'emboscada': 6808,
  'educado': 6809,
  'devolveré': 6810,
  'danza': 6811,
  'creería': 6812,
  'confundida': 6813,
  'asegurado': 6814,
  'apuntando': 6815,
  'anormal': 6816,
  'agradecemos': 6817,
  'tomara': 6818,
  'televisor': 6819,
  'suponer': 6820,
  'salvarme': 6821,
  'preocupan': 6822,
  'perdonar': 6823,
  'perdedores': 6824,
  'pa': 6825,
  'margen': 6826,
  'mandaré': 6827,
  'ingeniería': 6828,
  'id': 6829,
  'herencia': 6830,
  'gobernar': 6831,
  'doncella': 6832,
  'comprando': 6833,
  'colonias': 6834,
  'bases': 6835,
  'barras': 6836,
  'bájame': 6837,
  'solicitud': 6838,
  'sendero': 6839,
  'sacerdotes': 6840,
  'revisión': 6841,
  'restaurantes': 6842,
  'rastros': 6843,
  'pegarle': 6844,
  'patrones': 6845,
  'obtuvo': 6846,
  'mortales': 6847,
  'matrícula': 6848,
  'instalaciones': 6849,
  'enojo': 6850,
  'duras': 6851,
  'dejarías': 6852,
  'corrupción': 6853,
  'controlarlo': 6854,
  'apágalo': 6855,
  'alimento': 6856,
  'terriblemente': 6857,
  'solíamos': 6858,
  'ríe': 6859,
  'reza': 6860,
  'promedio': 6861,
  'pórtate': 6862,
  'misiones': 6863,
  'meten': 6864,
  'lotería': 6865,
  'italianos': 6866,
  'guardianes': 6867,
  'ganaron': 6868,
  'fáciles': 6869,
  'excelentes': 6870,
  'escritura': 6871,
  'escapando': 6872,
  'encontre': 6873,
  'empacar': 6874,
  'em': 6875,
  'devuelve': 6876,
  'convirtiendo': 6877,
  'contraseña': 6878,
  'cojones': 6879,
  'caca': 6880,
  'bromeaba': 6881,
  'ayudaste': 6882,
  'artefacto': 6883,
  'abras': 6884,
  'subo': 6885,
  'sentimental': 6886,
  'semillas': 6887,
  'sáquenlo': 6888,
  'sabrías': 6889,
  'reconocido': 6890,
  'privacidad': 6891,
  'portaaviones': 6892,
  'parecerá': 6893,
  'ocupa': 6894,
  'lucir': 6895,
  'habré': 6896,
  'estudié': 6897,
  'enseñe': 6898,
  'encontraría': 6899,
  'dolerá': 6900,
  'dispuestos': 6901,
  'discreción': 6902,
  'deberás': 6903,
  'debate': 6904,
  'creencias': 6905,
  'crecen': 6906,
  'comas': 6907,
  'chiflado': 6908,
  'aterrador': 6909,
  'volverse': 6910,
  'visiones': 6911,
  'surf': 6912,
  'software': 6913,
  'rival': 6914,
  'registrado': 6915,
  'pensaré': 6916,
  'ocurren': 6917,
  'ocuparme': 6918,
  'obsesión': 6919,
  'nube': 6920,
  'nobles': 6921,
  'milla': 6922,
  'jamas': 6923,
  'insisto': 6924,
  'impide': 6925,
  'gol': 6926,
  'frágil': 6927,
  'eramos': 6928,
  'entrará': 6929,
  'dispararé': 6930,
  'dispárale': 6931,
  'derrotado': 6932,
  'chatarra': 6933,
  'cálculos': 6934,
  'caigo': 6935,
  'brillo': 6936,
  'basurero': 6937,
  'ayuden': 6938,
  'aquellas': 6939,
  'altamente': 6940,
  'alienígenas': 6941,
  'acercó': 6942,
  'abiertas': 6943,
  'toqué': 6944,
  'tomarte': 6945,
  'senos': 6946,
  'provocar': 6947,
  'plumas': 6948,
  'lograron': 6949,
  'inicial': 6950,
  'femenino': 6951,
  'extras': 6952,
  'entrego': 6953,
  'encontrarlos': 6954,
  'culto': 6955,
  'cuántica': 6956,
  'calendario': 6957,
  'atracción': 6958,
  'asustan': 6959,
  'asegurarse': 6960,
  'arregló': 6961,
  'aquél': 6962,
  'aprendiz': 6963,
  'aprendes': 6964,
  'agresión': 6965,
  'acompaño': 6966,
  'abuso': 6967,
  'viví': 6968,
  'triunfo': 6969,
  'traerte': 6970,
  'temblando': 6971,
  'respire': 6972,
  'ocúpate': 6973,
  'llévalo': 6974,
  'limpiando': 6975,
  'haria': 6976,
  'grandeza': 6977,
  'exhibición': 6978,
  'evacuación': 6979,
  'dobla': 6980,
  'deshazte': 6981,
  'desgraciadamente': 6982,
  'crei': 6983,
  'crack': 6984,
  'contenido': 6985,
  'arruinaste': 6986,
  'aprobación': 6987,
  'alumno': 6988,
  'agresivo': 6989,
  'zorro': 6990,
  'terminen': 6991,
  'sacrificios': 6992,
  'rompiendo': 6993,
  'riñón': 6994,
  'reúne': 6995,
  'recorrido': 6996,
  'positiva': 6997,
  'murciélago': 6998,
  'morimos': 6999,
  'medicamentos': 7000,
  'marines': 7001,
  'mantendré': 7002,
  'insignia': 7003,
  'halló': 7004,
  'filete': 7005,
  'esperad': 7006,
  'enviaste': 7007,
  'enseñando': 7008,
  'disfruto': 7009,
  'determinar': 7010,
  'desesperada': 7011,
  'delincuente': 7012,
  'cristianos': 7013,
  'calentamiento': 7014,
  'agradecerte': 7015,
  'afectado': 7016,
  'ábrelo': 7017,
  'sincera': 7018,
  'quedando': 7019,
  'pila': 7020,
  'peligrosas': 7021,
  'nasa': 7022,
  'mataran': 7023,
  'lamentablemente': 7024,
  'labor': 7025,
  'jinete': 7026,
  'hipoteca': 7027,
  'hacerles': 7028,
  'femenina': 7029,
  'elemento': 7030,
  'deshacerse': 7031,
  'defensas': 7032,
  'cuarentena': 7033,
  'comete': 7034,
  'casamos': 7035,
  'camarera': 7036,
  'callarte': 7037,
  'baloncesto': 7038,
  'acabaste': 7039,
  'vendí': 7040,
  'tuvieran': 7041,
  'sonidos': 7042,
  'sirves': 7043,
  'separación': 7044,
  'quítame': 7045,
  'opuesto': 7046,
  'norteamericano': 7047,
  'llevarle': 7048,
  'hospitalidad': 7049,
  'enteró': 7050,
  'declaro': 7051,
  'cifras': 7052,
  'catorce': 7053,
  'cambies': 7054,
  'ayudame': 7055,
  'aprendió': 7056,
  'apagado': 7057,
  'alambre': 7058,
  'terremoto': 7059,
  'separarnos': 7060,
  'secretas': 7061,
  'quemaduras': 7062,
  'preparo': 7063,
  'poeta': 7064,
  'pagarás': 7065,
  'olvidalo': 7066,
  'mudo': 7067,
  'magdalena': 7068,
  'letrero': 7069,
  'esconderse': 7070,
  'email': 7071,
  'conseguirás': 7072,
  'terminada': 7073,
  'táctica': 7074,
  'sonaba': 7075,
  'shh': 7076,
  'sepamos': 7077,
  'quedaras': 7078,
  'púdrete': 7079,
  'patán': 7080,
  'pareciera': 7081,
  'nucleares': 7082,
  'moría': 7083,
  'mírala': 7084,
  'miento': 7085,
  'lesiones': 7086,
  'leones': 7087,
  'invertir': 7088,
  'insulto': 7089,
  'grites': 7090,
  'flotando': 7091,
  'flechas': 7092,
  'establecer': 7093,
  'elevador': 7094,
  'eléctrico': 7095,
  'dueña': 7096,
  'determinado': 7097,
  'cuevas': 7098,
  'cuadra': 7099,
  'cortando': 7100,
  'cambiarlo': 7101,
  'aprovechar': 7102,
  'anual': 7103,
  'anímate': 7104,
  'ando': 7105,
  'suspendido': 7106,
  'sentirá': 7107,
  'perras': 7108,
  'melodía': 7109,
  'mandan': 7110,
  'linaje': 7111,
  'levántese': 7112,
  'interruptor': 7113,
  'decidieron': 7114,
  'conversaciones': 7115,
  'contrabando': 7116,
  'cachorro': 7117,
  'bañera': 7118,
  'asustar': 7119,
  'aceptó': 7120,
  'abren': 7121,
  'vinos': 7122,
  'vasos': 7123,
  'sueltes': 7124,
  'profecía': 7125,
  'molestarte': 7126,
  'metiendo': 7127,
  'llueve': 7128,
  'invitada': 7129,
  'inocencia': 7130,
  'helicópteros': 7131,
  'guarde': 7132,
  'grandiosa': 7133,
  'físicamente': 7134,
  'esperándote': 7135,
  'enrique': 7136,
  'elegiste': 7137,
  'diente': 7138,
  'desarrollar': 7139,
  'construyendo': 7140,
  'conocieron': 7141,
  'chicle': 7142,
  'atlántico': 7143,
  'acondicionado': 7144,
  'acercarme': 7145,
  'abrí': 7146,
  'tirador': 7147,
  'ruina': 7148,
  'respeta': 7149,
  'recordaba': 7150,
  'polis': 7151,
  'perseguir': 7152,
  'pégale': 7153,
  'novedad': 7154,
  'muévase': 7155,
  'muerde': 7156,
  'moscas': 7157,
  'memorias': 7158,
  'langosta': 7159,
  'japonesa': 7160,
  'insiste': 7161,
  'hablaron': 7162,
  'generales': 7163,
  'freno': 7164,
  'existía': 7165,
  'examinar': 7166,
  'euros': 7167,
  'etapa': 7168,
  'detención': 7169,
  'cuervos': 7170,
  'convertirá': 7171,
  'congresista': 7172,
  'asusté': 7173,
  'subtitulos': 7174,
  'sonó': 7175,
  'separamos': 7176,
  'reflejo': 7177,
  'navidades': 7178,
  'moverte': 7179,
  'levantó': 7180,
  'italiana': 7181,
  'integridad': 7182,
  'informa': 7183,
  'hanzo': 7184,
  'hábil': 7185,
  'fabulosa': 7186,
  'demas': 7187,
  'cofre': 7188,
  'causando': 7189,
  'cancha': 7190,
  'bruto': 7191,
  'brindemos': 7192,
  'ayudarás': 7193,
  'abandonada': 7194,
  'vito': 7195,
  'vestir': 7196,
  'usaron': 7197,
  'torpedos': 7198,
  'sonda': 7199,
  'relajarte': 7200,
  'reactor': 7201,
  'pobreza': 7202,
  'ocupar': 7203,
  'moverme': 7204,
  'localizado': 7205,
  'llévala': 7206,
  'insoportable': 7207,
  'insecto': 7208,
  'explota': 7209,
  'escondida': 7210,
  'engañó': 7211,
  'encontrara': 7212,
  'dinamita': 7213,
  'desaparición': 7214,
  'depto': 7215,
  'debíamos': 7216,
  'creed': 7217,
  'corra': 7218,
  'compramos': 7219,
  'cercana': 7220,
  'barro': 7221,
  'arreglaremos': 7222,
  'valentía': 7223,
  'tratos': 7224,
  'salmón': 7225,
  'quitas': 7226,
  'perdonen': 7227,
  'nerviosos': 7228,
  'necesariamente': 7229,
  'mercedes': 7230,
  'mejora': 7231,
  'maravillas': 7232,
  'levantarme': 7233,
  'intentaron': 7234,
  'intelectual': 7235,
  'ingenioso': 7236,
  'hirieron': 7237,
  'hablabas': 7238,
  'frito': 7239,
  'escapaste': 7240,
  'desactivar': 7241,
  'concierne': 7242,
  'cómodos': 7243,
  'calcetines': 7244,
  'agradecimiento': 7245,
  'abriendo': 7246,
  'tubos': 7247,
  'tristes': 7248,
  'texto': 7249,
  'ritual': 7250,
  'reservado': 7251,
  'proyectos': 7252,
  'perpetua': 7253,
  'observador': 7254,
  'nadando': 7255,
  'moviéndose': 7256,
  'mátala': 7257,
  'mantuvo': 7258,
  'latín': 7259,
  'irlandeses': 7260,
  'inaceptable': 7261,
  'golfo': 7262,
  'exitosa': 7263,
  'escucharte': 7264,
  'elle': 7265,
  'desnudos': 7266,
  'descansen': 7267,
  'descansando': 7268,
  'costar': 7269,
  'conociera': 7270,
  'comunicarse': 7271,
  'coja': 7272,
  'chao': 7273,
  'borrachos': 7274,
  'apocalipsis': 7275,
  'adrenalina': 7276,
  'acompañarme': 7277,
  'vídeo': 7278,
  'timbre': 7279,
  'roban': 7280,
  'provecho': 7281,
  'panqueques': 7282,
  'obligación': 7283,
  'helion': 7284,
  'guardado': 7285,
  'gemelas': 7286,
  'fundación': 7287,
  'ficción': 7288,
  'fantasías': 7289,
  'edición': 7290,
  'cavar': 7291,
  'carguen': 7292,
  'avion': 7293,
  'auténtica': 7294,
  'voladora': 7295,
  'trineo': 7296,
  'sirvienta': 7297,
  'siguieron': 7298,
  'quitaré': 7299,
  'promete': 7300,
  'primaria': 7301,
  'previsto': 7302,
  'pasajero': 7303,
  'paranoia': 7304,
  'paramos': 7305,
  'opus': 7306,
  'monjes': 7307,
  'molino': 7308,
  'llévenlo': 7309,
  'liberado': 7310,
  'juntar': 7311,
  'inventar': 7312,
  'interferencia': 7313,
  'institución': 7314,
  'infectado': 7315,
  'habra': 7316,
  'gym': 7317,
  'ganará': 7318,
  'encargaremos': 7319,
  'cortos': 7320,
  'cooperar': 7321,
  'comiste': 7322,
  'cogí': 7323,
  'calvo': 7324,
  'básico': 7325,
  'arteria': 7326,
  'ali': 7327,
  'alboroto': 7328,
  'adoran': 7329,
  'tango': 7330,
  'tablero': 7331,
  'sospecho': 7332,
  'sobreviviente': 7333,
  'renuncia': 7334,
  'reciba': 7335,
  'practicando': 7336,
  'permanezcan': 7337,
  'pensará': 7338,
  'ofrecerle': 7339,
  'mini': 7340,
  'mejorado': 7341,
  'marciales': 7342,
  'manden': 7343,
  'larguémonos': 7344,
  'fijo': 7345,
  'explicarle': 7346,
  'evaluación': 7347,
  'escogí': 7348,
  'elfos': 7349,
  'demente': 7350,
  'corté': 7351,
  'consiguen': 7352,
  'confirmación': 7353,
  'completar': 7354,
  'categoría': 7355,
  'ayudé': 7356,
  'atraer': 7357,
  'zapatillas': 7358,
  'violar': 7359,
  'sonreír': 7360,
  'pretende': 7361,
  'ordinario': 7362,
  'olvídelo': 7363,
  'nuca': 7364,
  'nietos': 7365,
  'metan': 7366,
  'levantes': 7367,
  'favores': 7368,
  'factor': 7369,
  'empezaremos': 7370,
  'detengas': 7371,
  'deseando': 7372,
  'cuernos': 7373,
  'criada': 7374,
  'consentimiento': 7375,
  'comunistas': 7376,
  'cartuchos': 7377,
  'cangrejo': 7378,
  'bonos': 7379,
  'apreciar': 7380,
  'agárralo': 7381,
  'vapor': 7382,
  'trataste': 7383,
  'tejado': 7384,
  'semestre': 7385,
  'riqueza': 7386,
  'recibes': 7387,
  'millonario': 7388,
  'mecánica': 7389,
  'leyendas': 7390,
  'jr': 7391,
  'halla': 7392,
  'estimado': 7393,
  'entiendas': 7394,
  'eché': 7395,
  'dirijo': 7396,
  'destruirá': 7397,
  'deberán': 7398,
  'cooperación': 7399,
  'conclusiones': 7400,
  'comunes': 7401,
  'brilla': 7402,
  'basada': 7403,
  'bandido': 7404,
  'astronauta': 7405,
  'aprisa': 7406,
  'aislado': 7407,
  'tribus': 7408,
  'tela': 7409,
  'reportero': 7410,
  'químico': 7411,
  'persiguen': 7412,
  'perfección': 7413,
  'palanca': 7414,
  'marrón': 7415,
  'madrugada': 7416,
  'hicieran': 7417,
  'guante': 7418,
  'firmó': 7419,
  'envían': 7420,
  'dañar': 7421,
  'creerás': 7422,
  'convertirme': 7423,
  'confiable': 7424,
  'comics': 7425,
  'carmen': 7426,
  'apretado': 7427,
  'agarre': 7428,
  'vulnerable': 7429,
  'vieras': 7430,
  'terminan': 7431,
  'tenso': 7432,
  'telegrama': 7433,
  'sepárense': 7434,
  'relojes': 7435,
  'referencia': 7436,
  'quemó': 7437,
  'periodo': 7438,
  'nevada': 7439,
  'matón': 7440,
  'matanza': 7441,
  'mantenido': 7442,
  'mantenerse': 7443,
  'inmortal': 7444,
  'hermanita': 7445,
  'frenos': 7446,
  'disparas': 7447,
  'despreciable': 7448,
  'dejeme': 7449,
  'cosita': 7450,
  'conejos': 7451,
  'carnicero': 7452,
  'bodega': 7453,
  'aires': 7454,
  'ringo': 7455,
  'reportar': 7456,
  'reflejos': 7457,
  'propina': 7458,
  'prácticas': 7459,
  'peleado': 7460,
  'peinado': 7461,
  'oscuros': 7462,
  'olvidamos': 7463,
  'negativa': 7464,
  'materiales': 7465,
  'martini': 7466,
  'mantenerme': 7467,
  'lesión': 7468,
  'invencible': 7469,
  'hubieses': 7470,
  'gratitud': 7471,
  'film': 7472,
  'explorador': 7473,
  'esconderte': 7474,
  'enviando': 7475,
  'discutirlo': 7476,
  'desaparecida': 7477,
  'cuna': 7478,
  'cosquillas': 7479,
  'comedia': 7480,
  'baterías': 7481,
  'avisado': 7482,
  'asistir': 7483,
  'apropiada': 7484,
  'afortunados': 7485,
  'acercarse': 7486,
  'acelera': 7487,
  'suene': 7488,
  'similares': 7489,
  'respetable': 7490,
  'planear': 7491,
  'pajarito': 7492,
  'inspección': 7493,
  'habiendo': 7494,
  'experimentar': 7495,
  'escolta': 7496,
  'domingos': 7497,
  'desesperación': 7498,
  'deseamos': 7499,
  'dejalo': 7500,
  'cuestan': 7501,
  'coro': 7502,
  'convincente': 7503,
  'conectados': 7504,
  'castigado': 7505,
  'cambiarme': 7506,
  'ayudan': 7507,
  'atardecer': 7508,
  'aparecido': 7509,
  'ambición': 7510,
  'agarro': 7511,
  'sobredosis': 7512,
  'rápidos': 7513,
  'probabilidad': 7514,
  'perdidas': 7515,
  'panza': 7516,
  'necesitábamos': 7517,
  'naval': 7518,
  'levantarte': 7519,
  'leña': 7520,
  'inesperado': 7521,
  'fichas': 7522,
  'favoritos': 7523,
  'explorar': 7524,
  'espaciales': 7525,
  'entregue': 7526,
  'dominio': 7527,
  'demostrado': 7528,
  'costumbres': 7529,
  'charlar': 7530,
  'certificado': 7531,
  'cayo': 7532,
  'anteriores': 7533,
  'alemana': 7534,
  'activa': 7535,
  'aceptan': 7536,
  'turnos': 7537,
  'traidores': 7538,
  'terrestre': 7539,
  'sirena': 7540,
  'sauditas': 7541,
  'revólver': 7542,
  'refresco': 7543,
  'precisión': 7544,
  'permanezca': 7545,
  'pérdidas': 7546,
  'perderlo': 7547,
  'monasterio': 7548,
  'mocoso': 7549,
  'marineros': 7550,
  'limón': 7551,
  'lanzó': 7552,
  'inmenso': 7553,
  'golpeando': 7554,
  'escogido': 7555,
  'deshacerme': 7556,
  'descenso': 7557,
  'debiera': 7558,
  'dándole': 7559,
  'cubo': 7560,
  'continúen': 7561,
  'comarca': 7562,
  'caviar': 7563,
  'bloqueado': 7564,
  'acompaña': 7565,
  'vístete': 7566,
  'teorías': 7567,
  'salvará': 7568,
  'sacan': 7569,
  'rio': 7570,
  'rifles': 7571,
  'relajate': 7572,
  'rasguño': 7573,
  'quebrado': 7574,
  'producir': 7575,
  'presiona': 7576,
  'peludo': 7577,
  'patricia': 7578,
  'operadora': 7579,
  'obligó': 7580,
  'mostaza': 7581,
  'mencionado': 7582,
  'martín': 7583,
  'exige': 7584,
  'entretenimiento': 7585,
  'entregó': 7586,
  'elfo': 7587,
  'desarrollado': 7588,
  'cenicienta': 7589,
  'babor': 7590,
  'ares': 7591,
  'anunciar': 7592,
  'admite': 7593,
  'triple': 7594,
  'técnicas': 7595,
  'sobrevivido': 7596,
  'rotos': 7597,
  'respóndeme': 7598,
  'resfriado': 7599,
  'rana': 7600,
  'preguntaron': 7601,
  'periodistas': 7602,
  'pedo': 7603,
  'pasto': 7604,
  'pamela': 7605,
  'madura': 7606,
  'lárguense': 7607,
  'kelvin': 7608,
  'judía': 7609,
  'jodidamente': 7610,
  'instalación': 7611,
  'hazle': 7612,
  'haras': 7613,
  'hacéis': 7614,
  'fumas': 7615,
  'farmacia': 7616,
  'eliminado': 7617,
  'dígales': 7618,
  'define': 7619,
  'cerebros': 7620,
  'celdas': 7621,
  'cargada': 7622,
  'agradecida': 7623,
  'acaben': 7624,
  'tripas': 7625,
  'tradicional': 7626,
  'sandwich': 7627,
  'romana': 7628,
  'regresan': 7629,
  'recorrer': 7630,
  'preocupaba': 7631,
  'movernos': 7632,
  'latas': 7633,
  'habitacion': 7634,
  'génesis': 7635,
  'estúpidas': 7636,
  'escogiste': 7637,
  'escocés': 7638,
  'encendida': 7639,
  'despido': 7640,
  'delgado': 7641,
  'cortaré': 7642,
  'convenció': 7643,
  'carnada': 7644,
  'cargas': 7645,
  'brutal': 7646,
  'atrasado': 7647,
  'atrapen': 7648,
  'acoso': 7649,
  'aah': 7650,
  'tropa': 7651,
  'sopla': 7652,
  'sangrar': 7653,
  'presentarme': 7654,
  'pavos': 7655,
  'oren': 7656,
  'niñez': 7657,
  'necesitaría': 7658,
  'mantengo': 7659,
  'leve': 7660,
  'lancha': 7661,
  'inversiones': 7662,
  'intenso': 7663,
  'existido': 7664,
  'enseñaste': 7665,
  'elegí': 7666,
  'duende': 7667,
  'dormía': 7668,
  'díganme': 7669,
  'desesperados': 7670,
  'cúbranse': 7671,
  'cicatrices': 7672,
  'cambiamos': 7673,
  'bailarín': 7674,
  'acordamos': 7675,
  'acera': 7676,
  'viviré': 7677,
  'víbora': 7678,
  'saques': 7679,
  'saldría': 7680,
  'pésimo': 7681,
  'patética': 7682,
  'pagarán': 7683,
  'ojala': 7684,
  'naturales': 7685,
  'mirarme': 7686,
  'mg': 7687,
  'máscaras': 7688,
  'logre': 7689,
  'laberinto': 7690,
  'halcón': 7691,
  'frutas': 7692,
  'fórmula': 7693,
  'esfera': 7694,
  'entusiasmo': 7695,
  'enseñan': 7696,
  'encontrarse': 7697,
  'encargarme': 7698,
  'desconocida': 7699,
  'descifrar': 7700,
  'deliciosa': 7701,
  'dejaran': 7702,
  'daban': 7703,
  'conocimientos': 7704,
  'comenzará': 7705,
  'asustados': 7706,
  'acabaré': 7707,
  'abrirla': 7708,
  'abrirá': 7709,
  'vulgar': 7710,
  'viena': 7711,
  'valiosa': 7712,
  'úitima': 7713,
  'tenés': 7714,
  'quité': 7715,
  'queriendo': 7716,
  'protegerme': 7717,
  'pornografía': 7718,
  'parecían': 7719,
  'originales': 7720,
  'obedece': 7721,
  'jode': 7722,
  'inscripción': 7723,
  'enteras': 7724,
  'encontrarle': 7725,
  'desear': 7726,
  'descuento': 7727,
  'dense': 7728,
  'déjenos': 7729,
  'darías': 7730,
  'creerán': 7731,
  'crecimiento': 7732,
  'continuación': 7733,
  'conocerlos': 7734,
  'coalición': 7735,
  'cigarros': 7736,
  'causas': 7737,
  'cargador': 7738,
  'calorías': 7739,
  'arqueros': 7740,
  'victimas': 7741,
  'tormentas': 7742,
  'tiraste': 7743,
  'soporte': 7744,
  'retroceder': 7745,
  'regan': 7746,
  'pudiese': 7747,
  'prepararé': 7748,
  'precios': 7749,
  'músico': 7750,
  'matarás': 7751,
  'luchado': 7752,
  'implica': 7753,
  'hombrecito': 7754,
  'hábitos': 7755,
  'gorra': 7756,
  'gorila': 7757,
  'galáctica': 7758,
  'corteza': 7759,
  'contéstame': 7760,
  'comprometido': 7761,
  'cálculo': 7762,
  'aseguraré': 7763,
  'ansiedad': 7764,
  'amenazó': 7765,
  'activar': 7766,
  'abriré': 7767,
  'ábrela': 7768,
  'une': 7769,
  'tomarse': 7770,
  'mudó': 7771,
  'levanto': 7772,
  'laboratorios': 7773,
  'financiero': 7774,
  'encubierto': 7775,
  'crió': 7776,
  'creador': 7777,
  'coloca': 7778,
  'brindo': 7779,
  'apoyar': 7780,
  'ahorros': 7781,
  'abres': 7782,
  'votar': 7783,
  'verónica': 7784,
  'velo': 7785,
  'unirte': 7786,
  'revisado': 7787,
  'queramos': 7788,
  'podriamos': 7789,
  'pagaría': 7790,
  'ignorar': 7791,
  'gimnasia': 7792,
  'fronteras': 7793,
  'etiqueta': 7794,
  'enojes': 7795,
  'enemigas': 7796,
  'definición': 7797,
  'declarado': 7798,
  'curado': 7799,
  'cerebrales': 7800,
  'ballet': 7801,
  'atrapé': 7802,
  'americanas': 7803,
  'actúas': 7804,
  'aconsejo': 7805,
  'terminaré': 7806,
  'subimos': 7807,
  'subes': 7808,
  'revise': 7809,
  'quiza': 7810,
  'químicos': 7811,
  'querias': 7812,
  'protegerla': 7813,
  'plana': 7814,
  'paranoico': 7815,
  'ofrecen': 7816,
  'nueces': 7817,
  'montones': 7818,
  'llevarlos': 7819,
  'llegaría': 7820,
  'inventado': 7821,
  'haberlos': 7822,
  'explotó': 7823,
  'esclava': 7824,
  'duró': 7825,
  'desperdiciar': 7826,
  'delicada': 7827,
  'cualidades': 7828,
  'costas': 7829,
  'contestó': 7830,
  'conecta': 7831,
  'colgando': 7832,
  'coartada': 7833,
  'celebrando': 7834,
  'atentos': 7835,
  'atascado': 7836,
  'anfitrión': 7837,
  'admirador': 7838,
  'acérquense': 7839,
  'abeja': 7840,
  'unir': 7841,
  'suprema': 7842,
  'sillas': 7843,
  'sacrificar': 7844,
  'retorno': 7845,
  'represento': 7846,
  'pelean': 7847,
  'pedirme': 7848,
  'mirarte': 7849,
  'lazo': 7850,
  'ideales': 7851,
  'habias': 7852,
  'guarida': 7853,
  'esposos': 7854,
  'esperaban': 7855,
  'escondidos': 7856,
  'dinosaurio': 7857,
  'conseguirte': 7858,
  'conocernos': 7859,
  'conocerle': 7860,
  'carteles': 7861,
  'atento': 7862,
  'arañas': 7863,
  'alucinaciones': 7864,
  'zonas': 7865,
  'viajado': 7866,
  'realista': 7867,
  'rama': 7868,
  'quiten': 7869,
  'paras': 7870,
  'pagaron': 7871,
  'mismísimo': 7872,
  'míreme': 7873,
  'marcho': 7874,
  'inventé': 7875,
  'interesados': 7876,
  'intentaremos': 7877,
  'ilegales': 7878,
  'grandote': 7879,
  'gotas': 7880,
  'fino': 7881,
  'estanque': 7882,
  'estaca': 7883,
  'esgrima': 7884,
  'escuchad': 7885,
  'envuelto': 7886,
  'doscientos': 7887,
  'disfraces': 7888,
  'delgada': 7889,
  'decia': 7890,
  'cuñado': 7891,
  'cumplí': 7892,
  'cuidadosamente': 7893,
  'consiguieron': 7894,
  'cepillo': 7895,
  'celebración': 7896,
  'botín': 7897,
  'arrancar': 7898,
  'alimenta': 7899,
  'ajá': 7900,
  'ahorrar': 7901,
  'africa': 7902,
  'vudú': 7903,
  'tuviese': 7904,
  'salte': 7905,
  'rehabilitación': 7906,
  'rechazado': 7907,
  'pasarme': 7908,
  'ordene': 7909,
  'mátenlo': 7910,
  'llegaba': 7911,
  'horizonte': 7912,
  'hacerlos': 7913,
  'formen': 7914,
  'excepcional': 7915,
  'entierro': 7916,
  'energia': 7917,
  'enanos': 7918,
  'eliges': 7919,
  'disculpo': 7920,
  'digitales': 7921,
  'dicha': 7922,
  'desaparecerá': 7923,
  'desafortunado': 7924,
  'delantera': 7925,
  'daga': 7926,
  'creeme': 7927,
  'contestes': 7928,
  'atravesó': 7929,
  'atrapamos': 7930,
  'asustas': 7931,
  'artificiales': 7932,
  'arreglamos': 7933,
  'arca': 7934,
  'apurate': 7935,
  'amabas': 7936,
  'acusados': 7937,
  'abuelos': 7938,
  'vagina': 7939,
  'solicito': 7940,
  'rezando': 7941,
  'repita': 7942,
  'reducir': 7943,
  'querés': 7944,
  'planeo': 7945,
  'perfectas': 7946,
  'neutral': 7947,
  'na': 7948,
  'mueves': 7949,
  'legales': 7950,
  'lavandería': 7951,
  'jeep': 7952,
  'helada': 7953,
  'futbol': 7954,
  'fijado': 7955,
  'feroz': 7956,
  'debían': 7957,
  'creían': 7958,
  'cordero': 7959,
  'consulado': 7960,
  'círculos': 7961,
  'caíste': 7962,
  'áreas': 7963,
  'virtud': 7964,
  'únicas': 7965,
  'técnicos': 7966,
  'silencioso': 7967,
  'sencillamente': 7968,
  'selección': 7969,
  'sabés': 7970,
  'reportera': 7971,
  'reliquia': 7972,
  'pertenezco': 7973,
  'penas': 7974,
  'paisaje': 7975,
  'ocupación': 7976,
  'notar': 7977,
  'migajas': 7978,
  'marcado': 7979,
  'mantenemos': 7980,
  'interrogatorio': 7981,
  'internado': 7982,
  'hubiesen': 7983,
  'honestos': 7984,
  'holanda': 7985,
  'helados': 7986,
  'gramos': 7987,
  'frio': 7988,
  'estate': 7989,
  'encargará': 7990,
  'electrónico': 7991,
  'disculpan': 7992,
  'defecto': 7993,
  'dedicas': 7994,
  'curva': 7995,
  'cría': 7996,
  'convierta': 7997,
  'comprometida': 7998,
  'ballenas': 7999,
  'amigable': 8000,
  'aceptable': 8001,
  'volverme': 8002,
  'urgencia': 8003,
  'sentarnos': 8004,
  'roman': 8005,
  'privados': 8006,
  'postura': 8007,
  'ponernos': 8008,
  'peluca': 8009,
  'parecería': 8010,
  'palomitas': 8011,
  'nocturna': 8012,
  'mercenarios': 8013,
  'maricas': 8014,
  'mantendrá': 8015,
  'intenten': 8016,
  'guardas': 8017,
  'grúa': 8018,
  'gastado': 8019,
  'estrelló': 8020,
  'digital': 8021,
  'dieran': 8022,
  'corría': 8023,
  'capturar': 8024,
  'campeones': 8025,
  'ardilla': 8026,
  'apaguen': 8027,
  'amen': 8028,
  'acostumbrada': 8029,
  'sugerir': 8030,
  'sucias': 8031,
  'succión': 8032,
  'registrar': 8033,
  'propietario': 8034,
  'portero': 8035,
  'perdonarme': 8036,
  'parados': 8037,
  'ofrecido': 8038,
  'muevete': 8039,
  'marcharme': 8040,
  'marchado': 8041,
  'israel': 8042,
  'informarle': 8043,
  'hablaban': 8044,
  'ganadores': 8045,
  'firmas': 8046,
  'expresar': 8047,
  'empezaba': 8048,
  'despierten': 8049,
  'crearon': 8050,
  'claridad': 8051,
  'ciegos': 8052,
  'bloque': 8053,
  'alterado': 8054,
  'vestirme': 8055,
  'velada': 8056,
  'serias': 8057,
  'salvarla': 8058,
  'procedimientos': 8059,
  'porche': 8060,
  'ponerla': 8061,
  'pasemos': 8062,
  'pala': 8063,
  'ogros': 8064,
  'movió': 8065,
  'molestarme': 8066,
  'menuda': 8067,
  'hostil': 8068,
  'epidemia': 8069,
  'enséñame': 8070,
  'deténganlo': 8071,
  'detenernos': 8072,
  'describir': 8073,
  'concedido': 8074,
  'comunicado': 8075,
  'comparar': 8076,
  'cogeré': 8077,
  'callas': 8078,
  'busques': 8079,
  'acercándose': 8080,
  'abandona': 8081,
  'volado': 8082,
  'ventajas': 8083,
  'tubería': 8084,
  'traicionaste': 8085,
  'traerla': 8086,
  'suministros': 8087,
  'regresando': 8088,
  'recientes': 8089,
  'puñado': 8090,
  'pretender': 8091,
  'pasadas': 8092,
  'guardo': 8093,
  'girando': 8094,
  'fugitivo': 8095,
  'escoba': 8096,
  'escaparse': 8097,
  'empleos': 8098,
  'echen': 8099,
  'duran': 8100,
  'duque': 8101,
  'dondequiera': 8102,
  'dibujar': 8103,
  'destinado': 8104,
  'dedico': 8105,
  'cuidadoso': 8106,
  'cristales': 8107,
  'creerte': 8108,
  'clavo': 8109,
  'cápsula': 8110,
  'bastón': 8111,
  'atencion': 8112,
  'aliado': 8113,
  'agradece': 8114,
  'acepté': 8115,
  'aceptaré': 8116,
  'aborten': 8117,
  'utilizado': 8118,
  'tranvía': 8119,
  'trágico': 8120,
  'tequila': 8121,
  'sostener': 8122,
  'serviría': 8123,
  'retira': 8124,
  'redonda': 8125,
  'realizado': 8126,
  'protocolos': 8127,
  'potente': 8128,
  'pensábamos': 8129,
  'mexicanos': 8130,
  'llévatelo': 8131,
  'inmortalidad': 8132,
  'infectada': 8133,
  'experta': 8134,
  'ética': 8135,
  'estancia': 8136,
  'específico': 8137,
  'empezaré': 8138,
  'ejecutar': 8139,
  'echarme': 8140,
  'déjalos': 8141,
  'comisionado': 8142,
  'chispas': 8143,
  'cerramos': 8144,
  'atraco': 8145,
  'asignado': 8146,
  'asesinaste': 8147,
  'armar': 8148,
  'acuérdate': 8149,
  'vosotras': 8150,
  'unidas': 8151,
  'tránsito': 8152,
  'suceso': 8153,
  'presiento': 8154,
  'patadas': 8155,
  'obtuve': 8156,
  'músculo': 8157,
  'legalmente': 8158,
  'imaginarme': 8159,
  'honores': 8160,
  'gordos': 8161,
  'furiosa': 8162,
  'funcionado': 8163,
  'fallé': 8164,
  'escóndete': 8165,
  'declaraciones': 8166,
  'cumplió': 8167,
  'cuerno': 8168,
  'comienzan': 8169,
  'clementina': 8170,
  'católica': 8171,
  'carcel': 8172,
  'cansados': 8173,
  'brasil': 8174,
  'asado': 8175,
  'álbum': 8176,
  'abdominal': 8177,
  'volvimos': 8178,
  'supermercado': 8179,
  'sujeta': 8180,
  'suavemente': 8181,
  'salvando': 8182,
  'reventar': 8183,
  'rastreo': 8184,
  'quienquiera': 8185,
  'pretendo': 8186,
  'pertenecía': 8187,
  'perdono': 8188,
  'pequeñín': 8189,
  'paganos': 8190,
  'onu': 8191,
  'obtuviste': 8192,
  'obliga': 8193,
  'nobel': 8194,
  'levántense': 8195,
  'lastime': 8196,
  'inconveniente': 8197,
  'flexible': 8198,
  'favoritas': 8199,
  'escaparon': 8200,
  'corres': 8201,
  'correctas': 8202,
  'copiado': 8203,
  'convertí': 8204,
  'burla': 8205,
  'atrevido': 8206,
  'alejarme': 8207,
  'abordar': 8208,
  'abejas': 8209,
  'vuelan': 8210,
  'surgió': 8211,
  'rechazar': 8212,
  'noto': 8213,
  'negociaciones': 8214,
  'llamara': 8215,
  'irónico': 8216,
  'inmediata': 8217,
  'incluido': 8218,
  'hábito': 8219,
  'etc': 8220,
  'escapé': 8221,
  'entraré': 8222,
  'encendedor': 8223,
  'eches': 8224,
  'dramático': 8225,
  'dominar': 8226,
  'demandas': 8227,
  'decido': 8228,
  'cumplo': 8229,
  'corrió': 8230,
  'conducto': 8231,
  'choca': 8232,
  'area': 8233,
  'arbusto': 8234,
  'ajo': 8235,
  'yankees': 8236,
  'violó': 8237,
  'valía': 8238,
  'tijeras': 8239,
  'sugieres': 8240,
  'rodea': 8241,
  'revisé': 8242,
  'programación': 8243,
  'privadas': 8244,
  'presidencial': 8245,
  'necesitaban': 8246,
  'navegación': 8247,
  'mencioné': 8248,
  'maravillosos': 8249,
  'llamarle': 8250,
  'late': 8251,
  'frecuencias': 8252,
  'estreno': 8253,
  'encargue': 8254,
  'empuje': 8255,
  'empeorar': 8256,
  'dormiré': 8257,
  'desaparecidos': 8258,
  'dejenme': 8259,
  'dare': 8260,
  'culturas': 8261,
  'concreto': 8262,
  'cesar': 8263,
  'caía': 8264,
  'bailas': 8265,
  'autorizada': 8266,
  'aleluya': 8267,
  'yanquis': 8268,
  'villano': 8269,
  'superhéroe': 8270,
  'separar': 8271,
  'quítese': 8272,
  'preparación': 8273,
  'preocupaciones': 8274,
  'porcentaje': 8275,
  'ofendido': 8276,
  'merecido': 8277,
  'matemática': 8278,
  'marea': 8279,
  'márchate': 8280,
  'limpios': 8281,
  'jinetes': 8282,
  'ingresos': 8283,
  'infinito': 8284,
  'globos': 8285,
  'futura': 8286,
  'filme': 8287,
  'estudiado': 8288,
  'escondes': 8289,
  'enfoque': 8290,
  'confirma': 8291,
  'concentrarme': 8292,
  'casamiento': 8293,
  'bombardeo': 8294,
  'bolígrafo': 8295,
  'atravesando': 8296,
  'atrapan': 8297,
  'aproxima': 8298,
  'antepasados': 8299,
  'anteojos': 8300,
  'amabilidad': 8301,
  'aislamiento': 8302,
  'verga': 8303,
  'veis': 8304,
  'universitario': 8305,
  'traigas': 8306,
  'tocino': 8307,
  'tito': 8308,
  'tires': 8309,
  'talentoso': 8310,
  'rodeados': 8311,
  'riñones': 8312,
  'quites': 8313,
  'puños': 8314,
  'preparé': 8315,
  'pipa': 8316,
  'percepción': 8317,
  'mostrarles': 8318,
  'mostrador': 8319,
  'habitantes': 8320,
  'golpeaste': 8321,
  'gelatina': 8322,
  'explicaciones': 8323,
  'espina': 8324,
  'enterrada': 8325,
  'doblar': 8326,
  'devuelvo': 8327,
  'descubrimos': 8328,
  'declaró': 8329,
  'cristiana': 8330,
  'crío': 8331,
  'conquista': 8332,
  'confiado': 8333,
  'cómics': 8334,
  'bolos': 8335,
  'barriga': 8336,
  'aparecerá': 8337,
  'agradecidos': 8338,
  'aficionado': 8339,
  'yace': 8340,
  'sucedería': 8341,
  'ruptura': 8342,
  'quítale': 8343,
  'protegida': 8344,
  'perdamos': 8345,
  'negó': 8346,
  'mantienes': 8347,
  'maniobra': 8348,
  'jurar': 8349,
  'impresionar': 8350,
  'ilusiones': 8351,
  'ignorante': 8352,
  'generadores': 8353,
  'figuras': 8354,
  'exhausto': 8355,
  'enfrentarse': 8356,
  'duelen': 8357,
  'divertirte': 8358,
  'dirigimos': 8359,
  'dental': 8360,
  'cuénteme': 8361,
  'correos': 8362,
  'carolina': 8363,
  'campesinos': 8364,
  'bombarderos': 8365,
  'bares': 8366,
  'vendes': 8367,
  'suero': 8368,
  'sermón': 8369,
  'puerco': 8370,
  'provoca': 8371,
  'prestas': 8372,
  'pidas': 8373,
  'pasarán': 8374,
  'paces': 8375,
  'oponente': 8376,
  'oigas': 8377,
  'maya': 8378,
  'lastimes': 8379,
  'jardinero': 8380,
  'interferir': 8381,
  'importará': 8382,
  'hablarles': 8383,
  'ganaremos': 8384,
  'forzado': 8385,
  'explicarme': 8386,
  'espérenme': 8387,
  'enamoré': 8388,
  'disturbios': 8389,
  'devuélveme': 8390,
  'detenerla': 8391,
  'cuanta': 8392,
  'clásica': 8393,
  'chaleco': 8394,
  'cafe': 8395,
  'ballena': 8396,
  'bajaré': 8397,
  'amos': 8398,
  'altitud': 8399,
  'acontecimientos': 8400,
  'acababa': 8401,
  'vigilante': 8402,
  'tomografía': 8403,
  'resurrección': 8404,
  'rencor': 8405,
  'probemos': 8406,
  'pólvora': 8407,
  'ofensiva': 8408,
  'nono': 8409,
  'narcóticos': 8410,
  'mudar': 8411,
  'intentaste': 8412,
  'ganarle': 8413,
  'forman': 8414,
  'ficha': 8415,
  'ferrocarril': 8416,
  'falleció': 8417,
  'divierte': 8418,
  'directores': 8419,
  'dificultad': 8420,
  'desesperadamente': 8421,
  'démosle': 8422,
  'culos': 8423,
  'creyendo': 8424,
  'comprador': 8425,
  'casaré': 8426,
  'balance': 8427,
  'asustes': 8428,
  'alienígena': 8429,
  'alcanzo': 8430,
  'ventilación': 8431,
  'tregua': 8432,
  'traficantes': 8433,
  'subterráneo': 8434,
  'simulación': 8435,
  'secuestradores': 8436,
  'sangra': 8437,
  'resolverlo': 8438,
  'realeza': 8439,
  'presidenta': 8440,
  'pensaban': 8441,
  'parecida': 8442,
  'pagarme': 8443,
  'oraciones': 8444,
  'marruecos': 8445,
  'levantate': 8446,
  'lastimé': 8447,
  'iniciales': 8448,
  'imitación': 8449,
  'financiera': 8450,
  'exquisito': 8451,
  'esponja': 8452,
  'entrevistas': 8453,
  'enterarme': 8454,
  'entenderá': 8455,
  'enseñas': 8456,
  'convertirte': 8457,
  'cansé': 8458,
  'cambiaría': 8459,
  'bang': 8460,
  'arresten': 8461,
  'argentina': 8462,
  'adjunto': 8463,
  'acordado': 8464,
  'trofeo': 8465,
  'trayendo': 8466,
  'seguras': 8467,
  'satélites': 8468,
  'sacarlos': 8469,
  'rezo': 8470,
  'regular': 8471,
  'pretendes': 8472,
  'póliza': 8473,
  'piérdete': 8474,
  'marqués': 8475,
  'malvados': 8476,
  'leia': 8477,
  'invitarte': 8478,
  'fanático': 8479,
  'factura': 8480,
  'extrañaré': 8481,
  'escoria': 8482,
  'décadas': 8483,
  'cortada': 8484,
  'considere': 8485,
  'comprensión': 8486,
  'codo': 8487,
  'claras': 8488,
  'calme': 8489,
  'cabras': 8490,
  'balazo': 8491,
  'bájala': 8492,
  'babilonia': 8493,
  'arruiné': 8494,
  'arbustos': 8495,
  'apodo': 8496,
  'alumna': 8497,
  'aleja': 8498,
  'albania': 8499,
  'ahogado': 8500,
  'acercamos': 8501,
  'abrimos': 8502,
  'veterinario': 8503,
  'vecina': 8504,
  'usada': 8505,
  'puros': 8506,
  'puntería': 8507,
  'públicas': 8508,
  'pronóstico': 8509,
  'planeas': 8510,
  'pasarla': 8511,
  'optimista': 8512,
  'odiaría': 8513,
  'obreros': 8514,
  'mentiría': 8515,
  'marino': 8516,
  'luchamos': 8517,
  'letal': 8518,
  'judicial': 8519,
  'exceso': 8520,
  'enigma': 8521,
  'canasta': 8522,
  'buscarlos': 8523,
  'blues': 8524,
  'apunto': 8525,
  'aparatos': 8526,
  'aguante': 8527,
  'agarramos': 8528,
  'adictos': 8529,
  'vinci': 8530,
  'tramando': 8531,
  'tácticas': 8532,
  'sostiene': 8533,
  'signifique': 8534,
  'significar': 8535,
  'sato': 8536,
  'sarcófago': 8537,
  'reportaje': 8538,
  'religiosa': 8539,
  'reconstruir': 8540,
  'propone': 8541,
  'precaución': 8542,
  'portador': 8543,
  'popa': 8544,
  'parlamento': 8545,
  'ofrecerte': 8546,
  'ocuparse': 8547,
  'nombrar': 8548,
  'molestan': 8549,
  'lecturas': 8550,
  'iríamos': 8551,
  'haríamos': 8552,
  'guardería': 8553,
  'francotiradores': 8554,
  'exista': 8555,
  'evidentemente': 8556,
  'enfrenta': 8557,
  'destruya': 8558,
  'defenderse': 8559,
  'continente': 8560,
  'consideran': 8561,
  'ausente': 8562,
  'anochecer': 8563,
  'alrededores': 8564,
  'aduana': 8565,
  'winchester': 8566,
  'utah': 8567,
  'ultimamente': 8568,
  'sospechosa': 8569,
  'séptimo': 8570,
  'seguirme': 8571,
  'sálvame': 8572,
  'romeo': 8573,
  'robos': 8574,
  'ratones': 8575,
  'quitarse': 8576,
  'publico': 8577,
  'profeta': 8578,
  'presentarle': 8579,
  'paramédicos': 8580,
  'opiniones': 8581,
  'mostrado': 8582,
  'miramos': 8583,
  'invertido': 8584,
  'ídolo': 8585,
  'hijita': 8586,
  'ganancia': 8587,
  'esperaría': 8588,
  'esperará': 8589,
  'dudar': 8590,
  'destruyeron': 8591,
  'construida': 8592,
  'confíes': 8593,
  'comeremos': 8594,
  'bus': 8595,
  'bienestar': 8596,
  'bailamos': 8597,
  'ayudaremos': 8598,
  'autodestrucción': 8599,
  'ahogó': 8600,
  'acercarte': 8601,
  'violentos': 8602,
  'valga': 8603,
  'vacíos': 8604,
  'triángulo': 8605,
  'traslado': 8606,
  'tigres': 8607,
  'supieran': 8608,
  'sorprendida': 8609,
  'solían': 8610,
  'soda': 8611,
  'secundarios': 8612,
  'secuestrador': 8613,
  'recupera': 8614,
  'rechazo': 8615,
  'ramas': 8616,
  'preciosas': 8617,
  'ofrecieron': 8618,
  'locuras': 8619,
  'lino': 8620,
  'libera': 8621,
  'legítimo': 8622,
  'lastimó': 8623,
  'huérfanos': 8624,
  'generosa': 8625,
  'exijo': 8626,
  'escáner': 8627,
  'entenderás': 8628,
  'desaparecen': 8629,
  'cruzó': 8630,
  'continuará': 8631,
  'confirmada': 8632,
  'cocinera': 8633,
  'claros': 8634,
  'ciclo': 8635,
  'caminas': 8636,
  'bando': 8637,
  'avergonzada': 8638,
  'atraviesa': 8639,
  'atentamente': 8640,
  'arruinando': 8641,
  'adicción': 8642,
  'violenta': 8643,
  'únicamente': 8644,
  'suéter': 8645,
  'sudor': 8646,
  'significaría': 8647,
  'reemplazo': 8648,
  'quiebra': 8649,
  'peligros': 8650,
  'matare': 8651,
  'llamare': 8652,
  'litro': 8653,
  'irías': 8654,
  'humillación': 8655,
  'glorioso': 8656,
  'gasto': 8657,
  'faro': 8658,
  'exclusiva': 8659,
  'entrenando': 8660,
  'empezará': 8661,
  'diabetes': 8662,
  'conteo': 8663,
  'centros': 8664,
  'captura': 8665,
  'balcón': 8666,
  'asegúrese': 8667,
  'arder': 8668,
  'apretar': 8669,
  'aprenderás': 8670,
  'abrieron': 8671,
  'visitarme': 8672,
  'verduras': 8673,
  'venecia': 8674,
  'vendrían': 8675,
  'vales': 8676,
  'tardaste': 8677,
  'suicidarse': 8678,
  'simio': 8679,
  'sensibilidad': 8680,
  'retire': 8681,
  'respondió': 8682,
  'rendirse': 8683,
  'pusiera': 8684,
  'principalmente': 8685,
  'preferida': 8686,
  'órgano': 8687,
  'operador': 8688,
  'náuseas': 8689,
  'mudarse': 8690,
  'mirame': 8691,
  'mátalos': 8692,
  'madam': 8693,
  'llevare': 8694,
  'invitaron': 8695,
  'intruso': 8696,
  'idiomas': 8697,
  'hoguera': 8698,
  'hablara': 8699,
  'genes': 8700,
  'entregas': 8701,
  'encontrada': 8702,
  'echarte': 8703,
  'echamos': 8704,
  'devuelva': 8705,
  'detenerte': 8706,
  'deshacer': 8707,
  'cosmo': 8708,
  'comprarle': 8709,
  'comenzaremos': 8710,
  'cerré': 8711,
  'bloqueando': 8712,
  'asustarte': 8713,
  'asegura': 8714,
  'vitaminas': 8715,
  'vengarse': 8716,
  'valla': 8717,
  'trozos': 8718,
  'tímida': 8719,
  'superado': 8720,
  'sueña': 8721,
  'seguidos': 8722,
  'salvavidas': 8723,
  'remolque': 8724,
  'prometer': 8725,
  'proceder': 8726,
  'prepararme': 8727,
  'pediatría': 8728,
  'pagos': 8729,
  'pagarte': 8730,
  'ninja': 8731,
  'nieta': 8732,
  'motocicleta': 8733,
  'mostrando': 8734,
  'montana': 8735,
  'miedos': 8736,
  'mantendremos': 8737,
  'mandíbula': 8738,
  'luchas': 8739,
  'logras': 8740,
  'levanté': 8741,
  'lanzado': 8742,
  'intrusos': 8743,
  'insistió': 8744,
  'habria': 8745,
  'gritó': 8746,
  'ganarme': 8747,
  'decepción': 8748,
  'cubriendo': 8749,
  'continuemos': 8750,
  'consideración': 8751,
  'conduzco': 8752,
  'colchón': 8753,
  'chupa': 8754,
  'camisas': 8755,
  'buscabas': 8756,
  'bloqueo': 8757,
  'armamento': 8758,
  'amenazando': 8759,
  'alias': 8760,
  'virginidad': 8761,
  'vengar': 8762,
  'tornado': 8763,
  'series': 8764,
  'sácala': 8765,
  'rompan': 8766,
  'responden': 8767,
  'reclamar': 8768,
  'presentaré': 8769,
  'preguntamos': 8770,
  'predicador': 8771,
  'pintor': 8772,
  'paguen': 8773,
  'ofreciendo': 8774,
  'matriz': 8775,
  'maricones': 8776,
  'libra': 8777,
  'jugó': 8778,
  'jodió': 8779,
  'invité': 8780,
  'inodoro': 8781,
  'ingresar': 8782,
  'ignorancia': 8783,
  'ibamos': 8784,
  'gustara': 8785,
  'extiende': 8786,
  'exagerando': 8787,
  'cuido': 8788,
  'cuidarte': 8789,
  'colapso': 8790,
  'buzón': 8791,
  'buenísimo': 8792,
  'bretaña': 8793,
  'bree': 8794,
  'bárbaro': 8795,
  'asombrosa': 8796,
  'alérgico': 8797,
  'acostaste': 8798,
  'abrirlo': 8799,
  'vip': 8800,
  'trueno': 8801,
  'tesoros': 8802,
  'superhéroes': 8803,
  'seras': 8804,
  'rotas': 8805,
  'recupere': 8806,
  'quejarte': 8807,
  'pulgar': 8808,
  'pillado': 8809,
  'pensemos': 8810,
  'pausa': 8811,
  'pandillas': 8812,
  'pagaremos': 8813,
  'olvidaba': 8814,
  'normalidad': 8815,
  'muérete': 8816,
  'movido': 8817,
  'miligramos': 8818,
  'meteré': 8819,
  'matrix': 8820,
  'honestidad': 8821,
  'golpearme': 8822,
  'frasco': 8823,
  'expulsado': 8824,
  'espectros': 8825,
  'cubiertas': 8826,
  'confié': 8827,
  'clemencia': 8828,
  'cago': 8829,
  'bromea': 8830,
  'atún': 8831,
  'ático': 8832,
  'arreglé': 8833,
  'amnesia': 8834,
  'acusa': 8835,
  'situacion': 8836,
  'servidor': 8837,
  'seran': 8838,
  'recoja': 8839,
  'ramón': 8840,
  'psiquiátrico': 8841,
  'protesto': 8842,
  'prisiones': 8843,
  'porquerías': 8844,
  'pondrán': 8845,
  'munición': 8846,
  'mudarnos': 8847,
  'molesten': 8848,
  'mentales': 8849,
  'llámalo': 8850,
  'jurisdicción': 8851,
  'informante': 8852,
  'guardan': 8853,
  'frases': 8854,
  'estuviéramos': 8855,
  'estaria': 8856,
  'enviará': 8857,
  'entorno': 8858,
  'engañarme': 8859,
  'enfrentamos': 8860,
  'diviértanse': 8861,
  'distracción': 8862,
  'déjale': 8863,
  'cuadrante': 8864,
  'cuaderno': 8865,
  'caminaba': 8866,
  'bledo': 8867,
  'altar': 8868,
  'zanahoria': 8869,
  'viviremos': 8870,
  'tomada': 8871,
  'tendras': 8872,
  'sugirió': 8873,
  'rabino': 8874,
  'quisiéramos': 8875,
  'promoción': 8876,
  'práctico': 8877,
  'peleo': 8878,
  'pálido': 8879,
  'pagaste': 8880,
  'orfanato': 8881,
  'operativo': 8882,
  'mutación': 8883,
  'monta': 8884,
  'mayoria': 8885,
  'mandaste': 8886,
  'luto': 8887,
  'llevaban': 8888,
  'interese': 8889,
  'indicios': 8890,
  'impostor': 8891,
  'histórico': 8892,
  'explicas': 8893,
  'estrictamente': 8894,
  'estaras': 8895,
  'establo': 8896,
  'diseñador': 8897,
  'deprimente': 8898,
  'defectos': 8899,
  'crié': 8900,
  'corregir': 8901,
  'caros': 8902,
  'banquero': 8903,
  'aparta': 8904,
  'analizar': 8905,
  'vainilla': 8906,
  'transbordador': 8907,
  'taxista': 8908,
  'superficial': 8909,
  'sirviendo': 8910,
  'separe': 8911,
  'seguiría': 8912,
  'robe': 8913,
  'rituales': 8914,
  'revela': 8915,
  'régimen': 8916,
  'protegen': 8917,
  'preocuparía': 8918,
  'pollos': 8919,
  'pis': 8920,
  'obsequio': 8921,
  'nace': 8922,
  'mirarlo': 8923,
  'manada': 8924,
  'hambrientos': 8925,
  'formulario': 8926,
  'fibra': 8927,
  'feas': 8928,
  'divertirme': 8929,
  'divertimos': 8930,
  'culpes': 8931,
  'creéis': 8932,
  'convento': 8933,
  'continúan': 8934,
  'comamos': 8935,
  'cierran': 8936,
  'champagne': 8937,
  'candidatos': 8938,
  'camine': 8939,
  'calabozo': 8940,
  'cal': 8941,
  'besa': 8942,
  'arreglando': 8943,
  'armonía': 8944,
  'ame': 8945,
  'agarraron': 8946,
  'aeropuertos': 8947,
  'tope': 8948,
  'tontas': 8949,
  'setenta': 8950,
  'rodaje': 8951,
  'reuniremos': 8952,
  'relájese': 8953,
  'recta': 8954,
  'psicología': 8955,
  'pierdan': 8956,
  'pastilla': 8957,
  'paseando': 8958,
  'pasados': 8959,
  'ocultando': 8960,
  'movida': 8961,
  'morirse': 8962,
  'mérito': 8963,
  'marchen': 8964,
  'ingreso': 8965,
  'informó': 8966,
  'industrias': 8967,
  'fundamental': 8968,
  'francotirador': 8969,
  'flaco': 8970,
  'fallaste': 8971,
  'estambul': 8972,
  'entraremos': 8973,
  'duendes': 8974,
  'dobles': 8975,
  'descubriste': 8976,
  'derribar': 8977,
  'demandar': 8978,
  'delincuentes': 8979,
  'cuidarme': 8980,
  'créditos': 8981,
  'corrí': 8982,
  'cera': 8983,
  'brazalete': 8984,
  'bolsón': 8985,
  'apertura': 8986,
  'amé': 8987,
  'abandono': 8988,
  'vivirás': 8989,
  'vendas': 8990,
  'utilizando': 8991,
  'temible': 8992,
  'tacto': 8993,
  'reporteros': 8994,
  'reemplazar': 8995,
  'prepararte': 8996,
  'precauciones': 8997,
  'planeamos': 8998,
  'ocuparemos': 8999,
  'monja': 9000,
  'moisés': 9001,
  'matones': 9002,
  'lucio': 9003,
  'gala': 9004,
  'estais': 9005,
  'entrañas': 9006,
  'elijo': 9007,
  'distribución': 9008,
  'dispararme': 9009,
  'dimensión': 9010,
  'despiertas': 9011,
  'despertarte': 9012,
  'creíamos': 9013,
  'creerías': 9014,
  'corrientes': 9015,
  'controlo': 9016,
  'conseguirá': 9017,
  'compraron': 9018,
  'cometiste': 9019,
  'celulares': 9020,
  'campanas': 9021,
  'burbujas': 9022,
  'besarme': 9023,
  'atada': 9024,
  'apuros': 9025,
  'agradas': 9026,
  'admirable': 9027,
  'web': 9028,
  'testificar': 9029,
  'teniamos': 9030,
  'robamos': 9031,
  'retiró': 9032,
  'resumen': 9033,
  'proximo': 9034,
  'presten': 9035,
  'presidencia': 9036,
  'pica': 9037,
  'pelee': 9038,
  'ofrecí': 9039,
  'obtenido': 9040,
  'mudamos': 9041,
  'manejas': 9042,
  'maligno': 9043,
  'lodo': 9044,
  'limonada': 9045,
  'lazos': 9046,
  'investiga': 9047,
  'inventando': 9048,
  'intervención': 9049,
  'informacion': 9050,
  'inclusive': 9051,
  'funcionamiento': 9052,
  'fugitivos': 9053,
  'esten': 9054,
  'entendía': 9055,
  'embarazoso': 9056,
  'electrónica': 9057,
  'échate': 9058,
  'drogada': 9059,
  'distinguir': 9060,
  'difunto': 9061,
  'destruidos': 9062,
  'declara': 9063,
  'cumpla': 9064,
  'contraté': 9065,
  'cobro': 9066,
  'circuitos': 9067,
  'capas': 9068,
  'aspirina': 9069,
  'amenazado': 9070,
  'alcaide': 9071,
  'alaska': 9072,
  'acta': 9073,
  'ace': 9074,
  'accidentalmente': 9075,
  'unió': 9076,
  'tuberías': 9077,
  'testículos': 9078,
  'tenerlos': 9079,
  'supervisión': 9080,
  'rompieron': 9081,
  'robada': 9082,
  'rastreador': 9083,
  'ramos': 9084,
  'quemada': 9085,
  'queens': 9086,
  'púrpura': 9087,
  'pascua': 9088,
  'pasarás': 9089,
  'pabellón': 9090,
  'movemos': 9091,
  'mantenernos': 9092,
  'magnifico': 9093,
  'llevemos': 9094,
  'limosina': 9095,
  'libremente': 9096,
  'lgual': 9097,
  'frías': 9098,
  'filmando': 9099,
  'estomago': 9100,
  'esqueleto': 9101,
  'dele': 9102,
  'cuidará': 9103,
  'consiste': 9104,
  'complicaciones': 9105,
  'camello': 9106,
  'caligrafía': 9107,
  'avaricia': 9108,
  'arzobispo': 9109,
  'antibióticos': 9110,
  'amaré': 9111,
  'templarios': 9112,
  'superarlo': 9113,
  'sonando': 9114,
  'sesenta': 9115,
  'sanguínea': 9116,
  'salas': 9117,
  'reparto': 9118,
  'relato': 9119,
  'regresaron': 9120,
  'recibirás': 9121,
  'prefieren': 9122,
  'podrido': 9123,
  'pieles': 9124,
  'perdonará': 9125,
  'peleaste': 9126,
  'partimos': 9127,
  'mozo': 9128,
  'manga': 9129,
  'llantas': 9130,
  'jala': 9131,
  'internacionales': 9132,
  'ganarse': 9133,
  'encerrada': 9134,
  'drogadicto': 9135,
  'dorada': 9136,
  'disponibles': 9137,
  'devuelves': 9138,
  'desees': 9139,
  'cruzada': 9140,
  'corras': 9141,
  'compran': 9142,
  'cogiendo': 9143,
  'cancion': 9144,
  'cancelado': 9145,
  'baratos': 9146,
  'aula': 9147,
  'argumento': 9148,
  'apurado': 9149,
  'variar': 9150,
  'vals': 9151,
  'tomarás': 9152,
  'tinieblas': 9153,
  'sexualmente': 9154,
  'sere': 9155,
  'sacará': 9156,
  'robótica': 9157,
  'revelado': 9158,
  'proponer': 9159,
  'prevenir': 9160,
  'pleno': 9161,
  'pellejo': 9162,
  'pégame': 9163,
  'pecadores': 9164,
  'paradero': 9165,
  'odiar': 9166,
  'noventa': 9167,
  'noroeste': 9168,
  'necesitemos': 9169,
  'monjas': 9170,
  'mexicana': 9171,
  'masajes': 9172,
  'manteniendo': 9173,
  'limusina': 9174,
  'lema': 9175,
  'huelen': 9176,
  'gen': 9177,
  'gabinete': 9178,
  'furgoneta': 9179,
  'faraón': 9180,
  'facturas': 9181,
  'estudia': 9182,
  'entendería': 9183,
  'engañaste': 9184,
  'empujar': 9185,
  'emocionada': 9186,
  'eminencia': 9187,
  'ejecutado': 9188,
  'creyeron': 9189,
  'corrupto': 9190,
  'convierten': 9191,
  'columbia': 9192,
  'católico': 9193,
  'basa': 9194,
  'alá': 9195,
  'acuario': 9196,
  'acabarás': 9197,
  'vara': 9198,
  'traido': 9199,
  'sudando': 9200,
  'sobrevivirá': 9201,
  'rompimos': 9202,
  'practica': 9203,
  'perderme': 9204,
  'partió': 9205,
  'partículas': 9206,
  'occidente': 9207,
  'nevera': 9208,
  'negado': 9209,
  'molestarla': 9210,
  'masculino': 9211,
  'marcharte': 9212,
  'lloro': 9213,
  'librería': 9214,
  'intacta': 9215,
  'impulsos': 9216,
  'hormigas': 9217,
  'guerrera': 9218,
  'golpee': 9219,
  'fresas': 9220,
  'facilidad': 9221,
  'extranjera': 9222,
  'entregaré': 9223,
  'encerrar': 9224,
  'dígaselo': 9225,
  'devuelto': 9226,
  'devoción': 9227,
  'descubra': 9228,
  'desaparezca': 9229,
  'denegado': 9230,
  'cumpliendo': 9231,
  'corredores': 9232,
  'constitución': 9233,
  'conserje': 9234,
  'comienzas': 9235,
  'cereal': 9236,
  'apoya': 9237,
  'aparecieron': 9238,
  'abordo': 9239,
  'zurdo': 9240,
  'vendiste': 9241,
  'turcos': 9242,
  'turbulencia': 9243,
  'tragar': 9244,
  'tráela': 9245,
  'típica': 9246,
  'rostros': 9247,
  'recogió': 9248,
  'presiones': 9249,
  'preparativos': 9250,
  'póquer': 9251,
  'perdi': 9252,
  'pediría': 9253,
  'organismo': 9254,
  'oportuno': 9255,
  'norteamérica': 9256,
  'necesitado': 9257,
  'morder': 9258,
  'mienten': 9259,
  'mariquita': 9260,
  'luchó': 9261,
  'logrará': 9262,
  'lamentable': 9263,
  'jet': 9264,
  'invitaste': 9265,
  'inversores': 9266,
  'infiel': 9267,
  'habian': 9268,
  'funcionara': 9269,
  'frijoles': 9270,
  'formal': 9271,
  'fenómenos': 9272,
  'expreso': 9273,
  'escondió': 9274,
  'empujen': 9275,
  'discutiremos': 9276,
  'diplomático': 9277,
  'desprecio': 9278,
  'deshacerte': 9279,
  'deriva': 9280,
  'cuéntale': 9281,
  'contamos': 9282,
  'conocerme': 9283,
  'coges': 9284,
  'civilizado': 9285,
  'buscó': 9286,
  'besarte': 9287,
  'asustarme': 9288,
  'arrodíllate': 9289,
  'arriesgarnos': 9290,
  'arrecife': 9291,
  'agradan': 9292,
  'acuéstate': 9293,
  'acostar': 9294,
  'acompañar': 9295,
  'yegua': 9296,
  'turista': 9297,
  'transilvania': 9298,
  'taquicardia': 9299,
  'suegra': 9300,
  'sucediera': 9301,
  'sensual': 9302,
  'recomendación': 9303,
  'recogiendo': 9304,
  'quisieron': 9305,
  'quemen': 9306,
  'pescador': 9307,
  'oxford': 9308,
  'monton': 9309,
  'menta': 9310,
  'juró': 9311,
  'jodete': 9312,
  'ganaba': 9313,
  'formidable': 9314,
  'equivocó': 9315,
  'directiva': 9316,
  'despedirte': 9317,
  'debéis': 9318,
  'cruces': 9319,
  'creativo': 9320,
  'contener': 9321,
  'comprueba': 9322,
  'compórtate': 9323,
  'chantaje': 9324,
  'bromear': 9325,
  'bici': 9326,
  'bajamos': 9327,
  'ayudantes': 9328,
  'apártese': 9329,
  'alejada': 9330,
  'aceptamos': 9331,
  'acabaría': 9332,
  'volverías': 9333,
  'víspera': 9334,
  'usará': 9335,
  'terapeuta': 9336,
  'subiré': 9337,
  'soborno': 9338,
  'sesiones': 9339,
  'rutas': 9340,
  'resultar': 9341,
  'recordando': 9342,
  'quisieran': 9343,
  'puestas': 9344,
  'pregunten': 9345,
  'pidan': 9346,
  'payasos': 9347,
  'ordené': 9348,
  'opino': 9349,
  'oiste': 9350,
  'nalgas': 9351,
  'mision': 9352,
  'medallón': 9353,
  'luzco': 9354,
  'lasaña': 9355,
  'kilómetro': 9356,
  'índice': 9357,
  'incluir': 9358,
  'improbable': 9359,
  'guiar': 9360,
  'garage': 9361,
  'fogata': 9362,
  'flan': 9363,
  'espiando': 9364,
  'detonador': 9365,
  'deportivo': 9366,
  'cuéntanos': 9367,
  'creímos': 9368,
  'concentrado': 9369,
  'ciegas': 9370,
  'catéter': 9371,
  'campeon': 9372,
  'borrado': 9373,
  'bajan': 9374,
  'bailes': 9375,
  'arriesgando': 9376,
  'apoyado': 9377,
  'androide': 9378,
  'agrado': 9379,
  'vómito': 9380,
  'violeta': 9381,
  'troyanos': 9382,
  'traerle': 9383,
  'taberna': 9384,
  'sujetos': 9385,
  'subí': 9386,
  'suben': 9387,
  'solteros': 9388,
  'sólido': 9389,
  'secuestrada': 9390,
  'respondes': 9391,
  'refugiados': 9392,
  'recompensas': 9393,
  'propósitos': 9394,
  'presionar': 9395,
  'poemas': 9396,
  'pesados': 9397,
  'pegarme': 9398,
  'nochebuena': 9399,
  'mudarme': 9400,
  'montado': 9401,
  'meteorito': 9402,
  'masas': 9403,
  'jabalí': 9404,
  'invitarme': 9405,
  'intensidad': 9406,
  'impresionó': 9407,
  'imagine': 9408,
  'húmedo': 9409,
  'humedad': 9410,
  'frontal': 9411,
  'fieles': 9412,
  'fé': 9413,
  'específicamente': 9414,
  'enterar': 9415,
  'encerrados': 9416,
  'determinación': 9417,
  'deciden': 9418,
  'culpas': 9419,
  'creamos': 9420,
  'conferencias': 9421,
  'buscaste': 9422,
  'burdel': 9423,
  'bistec': 9424,
  'arroja': 9425,
  'árabes': 9426,
  'añadir': 9427,
  'agradables': 9428,
  'aficionados': 9429,
  'soltó': 9430,
  'saltando': 9431,
  'sábados': 9432,
  'respetado': 9433,
  'racional': 9434,
  'pruébalo': 9435,
  'proteja': 9436,
  'preocuparnos': 9437,
  'ponerlos': 9438,
  'mírenlo': 9439,
  'mencionaste': 9440,
  'medir': 9441,
  'lléveme': 9442,
  'irresponsable': 9443,
  'inmunidad': 9444,
  'honra': 9445,
  'hablarlo': 9446,
  'gusten': 9447,
  'finge': 9448,
  'escombros': 9449,
  'egipcios': 9450,
  'dragones': 9451,
  'diciéndome': 9452,
  'diariamente': 9453,
  'detuvieron': 9454,
  'cuestiones': 9455,
  'crezca': 9456,
  'chester': 9457,
  'carruaje': 9458,
  'características': 9459,
  'bragas': 9460,
  'bailo': 9461,
  'avisaré': 9462,
  'arrepentirás': 9463,
  'álamo': 9464,
  'afectar': 9465,
  'adopción': 9466,
  'visitante': 9467,
  'trayectoria': 9468,
  'tenerme': 9469,
  'sujétense': 9470,
  'submarinos': 9471,
  'señuelo': 9472,
  'sentamos': 9473,
  'satisfacer': 9474,
  'roble': 9475,
  'revancha': 9476,
  'recogeré': 9477,
  'probaré': 9478,
  'prepararse': 9479,
  'pagarlo': 9480,
  'opio': 9481,
  'ocultos': 9482,
  'negociando': 9483,
  'lote': 9484,
  'jugué': 9485,
  'inauguración': 9486,
  'hale': 9487,
  'formularios': 9488,
  'fina': 9489,
  'extrañamos': 9490,
  'escritores': 9491,
  'discursos': 9492,
  'dato': 9493,
  'controlada': 9494,
  'consideras': 9495,
  'aventón': 9496,
  'arregle': 9497,
  'alejarse': 9498,
  'agujas': 9499,
  'acosté': 9500,
  'taxis': 9501,
  'sumo': 9502,
  'sentó': 9503,
  'satisfecha': 9504,
  'satanás': 9505,
  'retardado': 9506,
  'relajarme': 9507,
  'recordará': 9508,
  'rapidez': 9509,
  'psiquiatría': 9510,
  'proposición': 9511,
  'preciosos': 9512,
  'poste': 9513,
  'portátil': 9514,
  'pensarás': 9515,
  'opera': 9516,
  'necesarias': 9517,
  'maravillosas': 9518,
  'lastimen': 9519,
  'islandia': 9520,
  'implantes': 9521,
  'huido': 9522,
  'homosexuales': 9523,
  'heroe': 9524,
  'generosidad': 9525,
  'faltó': 9526,
  'explicado': 9527,
  'experimentado': 9528,
  'estrecho': 9529,
  'enviaremos': 9530,
  'envenenado': 9531,
  'entendieron': 9532,
  'discípulos': 9533,
  'díganle': 9534,
  'detectar': 9535,
  'desnudas': 9536,
  'comprarte': 9537,
  'cancela': 9538,
  'callada': 9539,
  'besó': 9540,
  'ayúdanos': 9541,
  'auxiliar': 9542,
  'automática': 9543,
  'aspectos': 9544,
  'ancho': 9545,
  'amazonas': 9546,
  'agárrense': 9547,
  'afectó': 9548,
  'acusar': 9549,
  'acompañante': 9550,
  'aborto': 9551,
  'yoyo': 9552,
  'volé': 9553,
  'vistas': 9554,
  'vigilado': 9555,
  'trabajadora': 9556,
  'sutil': 9557,
  'sustituto': 9558,
  'sugiriendo': 9559,
  'sombreros': 9560,
  'solicita': 9561,
  'simpática': 9562,
  'riquezas': 9563,
  'ríen': 9564,
  'rendir': 9565,
  'recibirán': 9566,
  'psicológico': 9567,
  'previamente': 9568,
  'prestar': 9569,
  'podrida': 9570,
  'peleó': 9571,
  'pegaste': 9572,
  'pedirles': 9573,
  'orquesta': 9574,
  'orientación': 9575,
  'ocupará': 9576,
  'octavo': 9577,
  'obligaciones': 9578,
  'niñito': 9579,
  'natal': 9580,
  'mostrarme': 9581,
  'llámala': 9582,
  'lastimando': 9583,
  'jerusalén': 9584,
  'intentándolo': 9585,
  'hurra': 9586,
  'golpearon': 9587,
  'fallas': 9588,
  'faché': 9589,
  'establecido': 9590,
  'esperó': 9591,
  'entenderías': 9592,
  'ensayos': 9593,
  'dure': 9594,
  'dejadme': 9595,
  'cucaracha': 9596,
  'cortés': 9597,
  'cora': 9598,
  'convicto': 9599,
  'coincide': 9600,
  'cirujanos': 9601,
  'circulación': 9602,
  'cifra': 9603,
  'cartero': 9604,
  'carpintero': 9605,
  'camarones': 9606,
  'besando': 9607,
  'bajé': 9608,
  'arrepiento': 9609,
  'apuntar': 9610,
  'alejate': 9611,
  'voluntaria': 9612,
  'visible': 9613,
  'válvula': 9614,
  'útiles': 9615,
  'union': 9616,
  'tolerar': 9617,
  'suceden': 9618,
  'soporta': 9619,
  'seguiste': 9620,
  'rompo': 9621,
  'robarme': 9622,
  'respondan': 9623,
  'prioridades': 9624,
  'predecir': 9625,
  'posicion': 9626,
  'pesos': 9627,
  'pesas': 9628,
  'perdición': 9629,
  'pedía': 9630,
  'pararlo': 9631,
  'pantalón': 9632,
  'padrastro': 9633,
  'obstante': 9634,
  'milicia': 9635,
  'lograrás': 9636,
  'jura': 9637,
  'ingenieros': 9638,
  'fíjense': 9639,
  'favela': 9640,
  'exteriores': 9641,
  'extensión': 9642,
  'excursión': 9643,
  'editorial': 9644,
  'diecisiete': 9645,
  'dadas': 9646,
  'cumplida': 9647,
  'cónsul': 9648,
  'construyeron': 9649,
  'coñac': 9650,
  'chocó': 9651,
  'cantina': 9652,
  'burlando': 9653,
  'banana': 9654,
  'balsa': 9655,
  'ayudarán': 9656,
  'avise': 9657,
  'atrape': 9658,
  'apúrese': 9659,
  'anota': 9660,
  'amuleto': 9661,
  'alabado': 9662,
  'vivirán': 9663,
  'vieran': 9664,
  'trabajaban': 9665,
  'tomarle': 9666,
  'terminarlo': 9667,
  'suministro': 9668,
  'rompa': 9669,
  'rodeada': 9670,
  'robarle': 9671,
  'quitan': 9672,
  'pego': 9673,
  'ofendas': 9674,
  'matarían': 9675,
  'mares': 9676,
  'maquinaria': 9677,
  'manguera': 9678,
  'lloras': 9679,
  'limitado': 9680,
  'liderazgo': 9681,
  'khan': 9682,
  'implante': 9683,
  'iluminación': 9684,
  'grite': 9685,
  'fantastico': 9686,
  'fanáticos': 9687,
  'explícame': 9688,
  'enviarán': 9689,
  'encienda': 9690,
  'dirigida': 9691,
  'dejad': 9692,
  'crueles': 9693,
  'crucial': 9694,
  'congelada': 9695,
  'cercanos': 9696,
  'casillero': 9697,
  'cascos': 9698,
  'averiguaré': 9699,
  'apuntes': 9700,
  'aceptaría': 9701,
  'aceptará': 9702,
  'victorias': 9703,
  'trompeta': 9704,
  'tocarte': 9705,
  'terminaría': 9706,
  'tensa': 9707,
  'tendra': 9708,
  'sushi': 9709,
  'sangriento': 9710,
  'revisaré': 9711,
  'retirarme': 9712,
  'retaguardia': 9713,
  'regaló': 9714,
  'recordado': 9715,
  'reaccionar': 9716,
  'pulgadas': 9717,
  'puja': 9718,
  'posada': 9719,
  'píldora': 9720,
  'permítanme': 9721,
  'patrullas': 9722,
  'novelas': 9723,
  'mu': 9724,
  'monumento': 9725,
  'mataremos': 9726,
  'lucen': 9727,
  'justificar': 9728,
  'insensible': 9729,
  'inmigración': 9730,
  'iniciativa': 9731,
  'importas': 9732,
  'gustes': 9733,
  'gaseosa': 9734,
  'forastero': 9735,
  'éxtasis': 9736,
  'echaste': 9737,
  'duela': 9738,
  'dividido': 9739,
  'dígamelo': 9740,
  'conocían': 9741,
  'confiamos': 9742,
  'cantidades': 9743,
  'cajero': 9744,
  'buque': 9745,
  'brindar': 9746,
  'ayuntamiento': 9747,
  'avisarle': 9748,
  'atacaremos': 9749,
  'arrestados': 9750,
  'apareces': 9751,
  'anular': 9752,
  'analítica': 9753,
  'ajustar': 9754,
  'volved': 9755,
  'violín': 9756,
  'veteranos': 9757,
  'tirano': 9758,
  'sueltos': 9759,
  'sonrían': 9760,
  'seríamos': 9761,
  'resaca': 9762,
  'recordarte': 9763,
  'protegerlo': 9764,
  'procura': 9765,
  'pregúntele': 9766,
  'pídele': 9767,
  'olvídenlo': 9768,
  'movía': 9769,
  'merced': 9770,
  'matarle': 9771,
  'maniobras': 9772,
  'luca': 9773,
  'juvenil': 9774,
  'íntimos': 9775,
  'interpretación': 9776,
  'ingles': 9777,
  'importó': 9778,
  'hirió': 9779,
  'ganadora': 9780,
  'físicos': 9781,
  'escalar': 9782,
  'empleada': 9783,
  'echan': 9784,
  'donación': 9785,
  'desgraciada': 9786,
  'crueldad': 9787,
  'creencia': 9788,
  'contarles': 9789,
  'conforme': 9790,
  'confiaba': 9791,
  'conectar': 9792,
  'cálido': 9793,
  'atraparemos': 9794,
  'asuste': 9795,
  'arrojó': 9796,
  'aroma': 9797,
  'antorcha': 9798,
  'ametralladora': 9799,
  'alecto': 9800,
  'acudir': 9801,
  'volcán': 9802,
  'venderlo': 9803,
  'valium': 9804,
  'tráiganlo': 9805,
  'tia': 9806,
  'terminas': 9807,
  'tac': 9808,
  'suciedad': 9809,
  'sáquenme': 9810,
  'reparaciones': 9811,
  'reduce': 9812,
  'pulgas': 9813,
  'provincia': 9814,
  'pretendía': 9815,
  'portado': 9816,
  'planeó': 9817,
  'pimienta': 9818,
  'pelearon': 9819,
  'ocupe': 9820,
  'marchando': 9821,
  'involucrados': 9822,
  'íntimo': 9823,
  'imperdonable': 9824,
  'hongos': 9825,
  'harina': 9826,
  'fragmentos': 9827,
  'farsante': 9828,
  'exploradores': 9829,
  'estirar': 9830,
  'espuma': 9831,
  'espiritu': 9832,
  'espectro': 9833,
  'envidio': 9834,
  'enloqueció': 9835,
  'échale': 9836,
  'diseñó': 9837,
  'dáselo': 9838,
  'crías': 9839,
  'conducía': 9840,
  'comprobado': 9841,
  'colombia': 9842,
  'cóctel': 9843,
  'cobre': 9844,
  'casero': 9845,
  'apágala': 9846,
  'agarrado': 9847,
  'adoraba': 9848,
  'voten': 9849,
  'viajan': 9850,
  'tóxico': 9851,
  'terminando': 9852,
  'suburbios': 9853,
  'sede': 9854,
  'retrete': 9855,
  'reinos': 9856,
  'recuperarlo': 9857,
  'prender': 9858,
  'pijama': 9859,
  'permitiría': 9860,
  'milenio': 9861,
  'metemos': 9862,
  'mesías': 9863,
  'mediante': 9864,
  'manager': 9865,
  'lanzas': 9866,
  'juré': 9867,
  'iras': 9868,
  'intentos': 9869,
  'insistes': 9870,
  'impresionada': 9871,
  'gorro': 9872,
  'gemela': 9873,
  'fritos': 9874,
  'firmo': 9875,
  'excavación': 9876,
  'diplomática': 9877,
  'despidió': 9878,
  'dese': 9879,
  'decirse': 9880,
  'cuiden': 9881,
  'corporal': 9882,
  'controlan': 9883,
  'colesterol': 9884,
  'codigo': 9885,
  'cierres': 9886,
  'chocolates': 9887,
  'castro': 9888,
  'carreta': 9889,
  'calefacción': 9890,
  'caderas': 9891,
  'brisa': 9892,
  'brecha': 9893,
  'bocadillo': 9894,
  'azafata': 9895,
  'armadas': 9896,
  'amorcito': 9897,
  'acabaremos': 9898,
  'volador': 9899,
  'verifica': 9900,
  'usaría': 9901,
  'transmitir': 9902,
  'torno': 9903,
  'tocarlo': 9904,
  'seguidas': 9905,
  'sádico': 9906,
  'rompen': 9907,
  'robados': 9908,
  'reunirse': 9909,
  'representar': 9910,
  'recibieron': 9911,
  'prototipo': 9912,
  'perdonado': 9913,
  'necesitara': 9914,
  'nacen': 9915,
  'morí': 9916,
  'mc': 9917,
  'mayordomo': 9918,
  'mátenlos': 9919,
  'mason': 9920,
  'lider': 9921,
  'libreta': 9922,
  'jaque': 9923,
  'insultar': 9924,
  'industrial': 9925,
  'incompetente': 9926,
  'impecable': 9927,
  'guay': 9928,
  'gritaba': 9929,
  'fuertemente': 9930,
  'extracción': 9931,
  'excita': 9932,
  'etcétera': 9933,
  'española': 9934,
  'equivocadas': 9935,
  'enviada': 9936,
  'duérmete': 9937,
  'disfrutado': 9938,
  'diputado': 9939,
  'diarrea': 9940,
  'déjela': 9941,
  'convertiré': 9942,
  'conmoción': 9943,
  'comieron': 9944,
  'cómete': 9945,
  'claves': 9946,
  'cebolla': 9947,
  'carreteras': 9948,
  'capitana': 9949,
  'camisetas': 9950,
  'bajes': 9951,
  'ayúdenos': 9952,
  'arruine': 9953,
  'arriesgarme': 9954,
  'aprobar': 9955,
  'aparición': 9956,
  'amistoso': 9957,
  'alguién': 9958,
  'aéreas': 9959,
  'abandone': 9960,
  'vena': 9961,
  'valió': 9962,
  'únete': 9963,
  'tratara': 9964,
  'transplante': 9965,
  'tailandia': 9966,
  'supongamos': 9967,
  'sucesos': 9968,
  'suaves': 9969,
  'soñaba': 9970,
  'sillón': 9971,
  'sacarnos': 9972,
  'romperé': 9973,
  'rindas': 9974,
  'rejas': 9975,
  'receptor': 9976,
  'procedan': 9977,
  'pop': 9978,
  'pasarte': 9979,
  'olvidarte': 9980,
  'olla': 9981,
  'obstrucción': 9982,
  'nudo': 9983,
  'menciona': 9984,
  'mejilla': 9985,
  'júpiter': 9986,
  'herramienta': 9987,
  'gordito': 9988,
  'fluido': 9989,
  'finanzas': 9990,
  'explicaría': 9991,
  'expectativas': 9992,
  'estatuas': 9993,
  'estafa': 9994,
  'escrituras': 9995,
  'enoja': 9996,
  'empujó': 9997,
  'dilema': 9998,
  'desconectado': 9999,
  'deprimida': 10000,
};

export function getWordFrequencyTier(wordKey: string): FrequencyTier {
  const rank = FREQUENCY_RANKS[wordKey.toLowerCase()];
  if (!rank) return 'advanced';
  if (rank <= 100) return 'super-common';
  if (rank <= 1000) return 'very-common';
  if (rank <= 3000) return 'common';
  if (rank <= 7000) return 'intermediate';
  return 'advanced';
}

export function getWordFrequencyRank(wordKey: string): number | undefined {
  return FREQUENCY_RANKS[wordKey.toLowerCase()];
}
